import { holdsNoChildren } from '../lib/sealedBlocks'
import type { BlogWidgetSettings, BusinessHoursRow, BusinessLocation, ContactFloatConfig, OfferTemplateKind, RatingEmpty, RatingIcon } from '@webbuilder/schemas'
import { bindCmsSlots, normalizeCmsDetailAction } from '../lib/cmsSlots'
import { CMS_DEFAULT_ENTRY_DESIGN, CMS_DEFAULT_LIST_DESIGN } from '../lib/cmsDefaultDesigns'
import type { MediaTexture } from '../lib/mediaTexture'
import type { BackgroundSlider } from '../lib/backgroundSlider'
import { stackOfferCardFooters, contactSlotHref } from '@webbuilder/schemas'
import type { ElementAnimation } from '../lib/animations'
import type { ImagePresentation } from '../lib/imagePresentation'
import type { ImageCrop } from '../lib/imageCrop'
import type { ImageVeil } from '../lib/imageVeil'
import type { TabsStyle } from '../lib/tabsStyle'
import { findBackgroundPhotoLayer, isBackgroundImage } from '../lib/backgroundLayer'
import type { StateTransition } from '@webbuilder/theme'
import { create } from 'zustand'
import { releaseStructuralOverrides } from '../lib/structuralSurfaces'
import { isMediaNode } from '../lib/mediaRadius'
import { devtools } from 'zustand/middleware'
import type { Breakpoint, ElementStyles, StyleProperties } from '@webbuilder/style-engine'
import type { ScheduleState, ScheduleWindow } from '@webbuilder/shared-types'
/* Reexportar con `export … from` no trae nada al ámbito de ESTE archivo: sólo lo
   deja pasar hacia fuera. El store también los usa, así que además se importan. */
import {
  CARD_CONTAINER_KEY_SET, DEFAULT_BRAND_LOGO, normalizeProjectTheme, normalizeTheme,
  type PageTheme, type ProjectTheme, type ThemeRole,
} from '@webbuilder/theme'
// NOTE: Circular import with history.store.ts — safe because pushSnapshot is
// only called inside action bodies, after both modules are fully initialized.
import { useHistoryStore } from './history.store'
import { generateSlug, ensureUniqueSlug } from '../lib/slug'
import { translate } from '../lib/i18n'
import { DEFAULT_PROJECT_CONTENT, DEFAULT_PAGE_THEME, DEFAULT_PAGE_STYLES } from './editor.defaults'
export { DEFAULT_PROJECT_CONTENT, DEFAULT_PAGE_THEME, DEFAULT_PAGE_STYLES }
import {
  updateNodeInTree, deleteNodeFromTree, addNodeToTree, clearProjectBindings,
  insertNodeAtTarget, findParentElement, regenIds, wrapNodesInDiv, copyableRoot, stripOrphanWidgetParts,
  reorderNodeInTree, moveNodeInTree, moveNodeToParentIndex, findNodeById,
} from './treeHelpers'
/* Sólo el valor: la vuelta desde lib/components es de tipos, que se borran al
   compilar, así que no hay ciclo en tiempo de ejecución. */
import { componentOwning, detachInstance } from '../lib/components'
import { alternateNewSection } from '../lib/sectionAlternation'

// ─── Domain types ─────────────────────────────────────────────────────────────

export type { ElementStyles }
export type { StyleProperties, Breakpoint } from '@webbuilder/style-engine'

export type ElementType =
  | 'section'
  | 'div'
  | 'text'
  | 'richHeading'
  | 'image'
  | 'icon'
  | 'rating'
  | 'avatarGroup'
  | 'stat'
  | 'button'
  | 'link'
  | 'divider'
  | 'embed'
  /** Aire entre dos piezas, en cuatro tamaños (ver `lib/spacer.ts`). */
  | 'spacer'

export type TextTag = 'p' | 'small' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

/**
 * The styleable parts of a form block.
 *
 * These are roles, not instances: "label" means every label in the form. The
 * canvas addresses them with a composite id (`<nodeId>::label`) so selection,
 * hover and the whole inspector work on them with no special casing — the part
 * behaves like an element that simply cannot be moved or deleted.
 */
export const FORM_PARTS = ['title', 'label', 'input', 'submit', 'message'] as const
export type FormPart = (typeof FORM_PARTS)[number]

/*
 * Cómo se llama cada pieza en el cromo del editor —el rótulo del lienzo, la
 * cabecera del inspector—. Son claves del catálogo del EDITOR: lo lee quien
 * edita, en su idioma, no el visitante. Estaban escritas en español y así
 * salían con la interfaz en inglés.
 */
const FORM_PART_LABEL_KEYS: Record<FormPart, string> = {
  title: 'part.form.title',
  label: 'part.form.label',
  input: 'part.form.input',
  submit: 'part.form.submit',
  message: 'part.form.message',
}

export function formPartLabel(part: FormPart): string {
  return translate(FORM_PART_LABEL_KEYS[part])
}

/** The element type a part behaves like, which is what decides its inspector groups. */
const FORM_PART_TYPES: Record<FormPart, ElementNode['type']> = {
  title: 'text',
  label: 'text',
  input: 'div',
  submit: 'button',
  message: 'text',
}

/**
 * The token each part follows until the customer overrides it.
 *
 * These are not decoration: they are the roles the form already rendered with,
 * so turning parts into editable nodes changes nothing on screen — and the
 * inspector shows the same values the canvas is actually using.
 */
const FORM_PART_ROLES: Record<FormPart, ElementNode['themeRole']> = {
  title: 'heading3',
  label: 'heading5',
  message: 'heading5',
  submit: 'buttonPrimary',
  // The field box is drawn by the form's own stylesheet, not by a brand token.
  input: undefined,
}

/**
 * Las piezas de una reseña que se pueden seleccionar y estilar.
 *
 * Mismo trato que las partes de un formulario, y por el mismo motivo: el
 * contenido lo lleva un modelo —la lista de reseñas—, no unos nodos hijos. Sin
 * esto, el bloque entero era una sola cosa: se elegía o no se elegía, y no había
 * forma de cambiarle el tamaño al nombre o el color al texto.
 *
 * TODAS las tarjetas comparten la pieza: pulsar el nombre de la tercera
 * selecciona «el nombre», no ese nombre. Es lo que mantiene el bloque parejo —y
 * lo contrario sería poder dejar una reseña con otra letra que las demás.
 */
export const REVIEW_PARTS = ['card', 'name', 'meta', 'text', 'avatar', 'source', 'stars', 'more'] as const

export type ReviewPart = (typeof REVIEW_PARTS)[number]

/** Ver `FORM_PART_LABEL_KEYS`. */
const REVIEW_PART_LABEL_KEYS: Record<ReviewPart, string> = {
  card: 'part.review.card',
  name: 'part.review.name',
  meta: 'part.review.meta',
  text: 'part.review.text',
  avatar: 'part.review.avatar',
  source: 'part.review.source',
  stars: 'part.review.stars',
  more: 'part.review.more',
}

export function reviewPartLabel(part: ReviewPart): string {
  return translate(REVIEW_PART_LABEL_KEYS[part])
}

const REVIEW_PART_TYPES: Record<ReviewPart, ElementNode['type']> = {
  card: 'div',
  name: 'text',
  meta: 'text',
  text: 'text',
  avatar: 'image',
  source: 'image',
  stars: 'text',
  more: 'button',
}

/** El rol del tema con el que nace cada pieza; el mismo que dibuja el bloque. */
const REVIEW_PART_ROLES: Record<ReviewPart, ElementNode['themeRole']> = {
  card: 'card',
  name: 'heading4',
  meta: 'heading5',
  text: 'paragraph',
  avatar: undefined,
  source: undefined,
  stars: 'rating',
  more: 'link',
}

const FORM_PART_SEPARATOR = '::' 

export function formPartId(nodeId: string, part: FormPart): string {
  return `${nodeId}${FORM_PART_SEPARATOR}${part}`
}

/** Splits a composite id, or returns null for an ordinary element id. */
export function parseFormPartId(id: string | null | undefined): { nodeId: string; part: FormPart } | null {
  if (!id) return null
  const index = id.indexOf(FORM_PART_SEPARATOR)
  if (index === -1) return null
  const part = id.slice(index + FORM_PART_SEPARATOR.length)
  if (!(FORM_PARTS as readonly string[]).includes(part)) return null
  return { nodeId: id.slice(0, index), part: part as FormPart }
}

export function reviewPartId(nodeId: string, part: ReviewPart): string {
  return `${nodeId}${FORM_PART_SEPARATOR}${part}`
}

/** Igual que `parseFormPartId`, para las piezas de una reseña. */
export function parseReviewPartId(id: string | null | undefined): { nodeId: string; part: ReviewPart } | null {
  if (!id) return null
  const index = id.indexOf(FORM_PART_SEPARATOR)
  if (index === -1) return null
  const part = id.slice(index + FORM_PART_SEPARATOR.length)
  if (!(REVIEW_PARTS as readonly string[]).includes(part)) return null
  return { nodeId: id.slice(0, index), part: part as ReviewPart }
}

/** Ver `FORM_PART_LABEL_KEYS`. */
const FAQ_PART_LABEL_KEYS: Record<FaqPart, string> = {
  item: 'part.faq.item',
  question: 'part.faq.question',
  answer: 'part.faq.answer',
  icon: 'part.faq.icon',
}

export function faqPartLabel(part: FaqPart): string {
  return translate(FAQ_PART_LABEL_KEYS[part])
}

const FAQ_PART_TYPES: Record<FaqPart, ElementNode['type']> = {
  item: 'div',
  question: 'text',
  answer: 'text',
  icon: 'text',
}

/* La pregunta es un titular del sitio y la respuesta su párrafo: sin esto el
   bloque traería su propia tipografía y se leería como algo pegado encima de la
   página. Es la misma decisión que en reseñas. */
const FAQ_PART_ROLES: Record<FaqPart, ElementNode['themeRole']> = {
  item: 'card',
  /* H4 (2026-10-07, él lo pidió): la pregunta es el titular de cada fila,
     pero como H3 pesaba lo mismo que el título de la sección de al lado y la
     lista se leía como una pila de titulares. Antes era H5, demasiado poco. */
  question: 'heading4',
  answer: 'paragraph',
  icon: undefined,
}

/**
 * Qué borrar de verdad al borrar algo de un carrusel (`slider`).
 *
 * Una diapositiva es un hijo de la pista (`wb-slider-track`). Si lo que se
 * borra es lo único dentro de una diapositiva, se borra la diapositiva; y si
 * ésa es la única que queda, el carrusel entero. Fuera de un carrusel, lo
 * mismo que se pidió.
 */
export function carouselDeletionTarget(elements: ElementNode[], elementId: string): string {
  let target = elementId
  const parent = findParentElement(elements, target)
  const grandParent = parent ? findParentElement(elements, parent.id) : null
  if (parent && grandParent?.schemaId === 'wb-slider-track' && parent.children.length === 1) target = parent.id
  const track = findParentElement(elements, target)
  if (track?.schemaId === 'wb-slider-track' && track.children.length === 1) {
    let host: ElementNode | null = track
    while (host && host.behavior?.type !== 'slider') host = findParentElement(elements, host.id)
    if (host) target = host.id
  }
  return target
}

/**
 * Qué se borra de verdad al borrar `elementId`: él mismo, o el componente
 * entero si era lo último que le quedaba dentro.
 *
 * Una galería infinita, una rueda o un carril de tarjetas sin piezas no son
 * nada —una caja vacía que no hace nada y que había que ir a buscar para
 * borrarla aparte—. Así que la última tarjeta se lleva su componente, como ya
 * pasaba con la última diapositiva de un carrusel (`carouselDeletionTarget`).
 */
export function componentDeletionTarget(elements: ElementNode[], elementId: string): string {
  const carousel = carouselDeletionTarget(elements, elementId)
  if (carousel !== elementId) return carousel
  const parent = findParentElement(elements, elementId)
  if (!parent || parent.children.length !== 1) return elementId
  const holdsItems = parent.behavior?.type === 'marquee' || parent.behavior?.type === 'carousel' || parent.schemaId === 'wb-carousel-track'
  if (!holdsItems) return elementId
  let host: ElementNode | null = parent
  while (host && !host.behavior) host = findParentElement(elements, host.id)
  return host && !host.pinned ? host.id : elementId
}

export function faqPartId(nodeId: string, part: FaqPart): string {
  return `${nodeId}${FORM_PART_SEPARATOR}${part}`
}

/** Igual que `parseReviewPartId`, para las piezas de una pregunta frecuente. */
export function parseFaqPartId(id: string | null | undefined): { nodeId: string; part: FaqPart } | null {
  if (!id) return null
  const index = id.indexOf(FORM_PART_SEPARATOR)
  if (index === -1) return null
  const part = id.slice(index + FORM_PART_SEPARATOR.length)
  if (!(FAQ_PARTS as readonly string[]).includes(part)) return null
  return { nodeId: id.slice(0, index), part: part as FaqPart }
}

/** Sólo estas dos piezas llevan texto que pueda salir de una ficha. */
export function faqPartTakesCms(part: FaqPart): part is 'question' | 'answer' {
  return part === 'question' || part === 'answer'
}

/** El campo donde vive el enlace de cada pieza. */
const FAQ_BINDING_KEY = { question: 'questionBinding', answer: 'answerBinding' } as const

/** La pregunta sobre la que se está trabajando: la señalada, o la primera. */
export function faqEntryFor(host: ElementNode, itemId?: string | null): FaqEntry | undefined {
  if (host.behavior?.type !== 'faq') return undefined
  const items = host.behavior.items ?? []
  return items.find((entry) => entry.id === itemId) ?? items[0]
}

const faqPartNodeCache = new WeakMap<ElementNode, Record<string, ElementNode>>()

/**
 * El nodo de mentira de una pieza del FAQ. Ver `buildReviewPartNode`.
 *
 * `itemId` es la fila sobre la que se pulsó. El estilo no lo mira —las piezas
 * lo comparten, que es lo que mantiene el bloque parejo— pero el enlace al CMS
 * sí: es de una pregunta concreta, así que sin esto la barra enseñaría el de la
 * primera fila con la tercera seleccionada.
 */
export function buildFaqPartNode(host: ElementNode, part: FaqPart, itemId?: string | null): ElementNode | undefined {
  if (host.behavior?.type !== 'faq') return undefined
  const entry = faqEntryFor(host, itemId)
  const key = `${part}::${entry?.id ?? ''}`
  const cached = faqPartNodeCache.get(host)
  const hit = cached?.[key]
  if (hit) return hit
  const role = FAQ_PART_ROLES[part]
  /* El enlace al CMS viaja dentro del nodo, con la misma forma que en un texto
     normal. Es lo que hace que la barra de selección y la de texto lo enseñen y
     lo suelten sin saber nada de preguntas frecuentes: preguntan por
     `contentBinding`, y aquí hay uno. */
  const binding = faqPartTakesCms(part) ? entry?.[FAQ_BINDING_KEY[part]] : undefined
  const node: ElementNode = {
    id: faqPartId(host.id, part),
    type: FAQ_PART_TYPES[part],
    label: faqPartLabel(part),
    children: [],
    styles: host.behavior.partStyles?.[part] ?? { desktop: {} },
    ...(role ? { themeRole: role } : {}),
    ...(host.behavior.partRefs?.[part] ? { styleRefs: host.behavior.partRefs[part] } : {}),
    ...(binding ? { contentBinding: binding } : {}),
  }
  faqPartNodeCache.set(host, { ...cached, [key]: node })
  return node
}

const reviewPartNodeCache = new WeakMap<ElementNode, Partial<Record<ReviewPart, ElementNode>>>()

/**
 * El nodo de mentira de una pieza de reseña, para que el inspector la trate como
 * a cualquier otra cosa. Nunca se guarda en el árbol: lo que persiste es
 * `behavior.partStyles`, y esto se reconstruye a partir de ahí.
 */
export function buildReviewPartNode(host: ElementNode, part: ReviewPart): ElementNode | undefined {
  if (host.behavior?.type !== 'reviews') return undefined
  const cached = reviewPartNodeCache.get(host)
  const hit = cached?.[part]
  if (hit) return hit
  const role = REVIEW_PART_ROLES[part]
  const node: ElementNode = {
    id: reviewPartId(host.id, part),
    type: REVIEW_PART_TYPES[part],
    label: reviewPartLabel(part),
    children: [],
    styles: host.behavior.partStyles?.[part] ?? { desktop: {} },
    ...(role ? { themeRole: role } : {}),
    ...(host.behavior.partRefs?.[part] ? { styleRefs: host.behavior.partRefs[part] } : {}),
  }
  reviewPartNodeCache.set(host, { ...cached, [part]: node })
  return node
}

/** Dobla el cambio del inspector dentro del comportamiento del bloque. */
/**
 * Dobla el cambio del inspector dentro del bloque de preguntas.
 *
 * Idéntico en forma al de reseñas para estilos y tokens: lo tocado a mano queda
 * anotado como anulación para que el rol del tema no lo descarte al dibujar.
 *
 * El TEXTO en sí no pasa por aquí —sale de la lista de preguntas, y dejar que un
 * control de contenido escribiera aquí lo pisaría—, pero sí pasa de dónde sale:
 * atar una pieza a un campo del CMS se dobla dentro de SU pregunta.
 */
export function applyFaqPartUpdate(
  host: ElementNode,
  part: FaqPart,
  updates: Partial<ElementNode>,
  itemId?: string | null,
): Partial<ElementNode> | null {
  if (host.behavior?.type !== 'faq') return null

  /*
   * De dónde sale el texto, que es lo único de contenido que llega aquí.
   *
   * Se ata UNA PREGUNTA y una pieza de esa pregunta: la respuesta de la tercera
   * fila, y nada más. Atándolo al bloque —como estaba— conectar una respuesta
   * reescribía la lista entera: las demás filas cambiaban y el título escrito a
   * mano desaparecía, porque pasaba a leerse de un campo que nadie había
   * elegido.
   *
   * Soltarlo devuelve esa pieza a su texto escrito, que nunca se tocó.
   */
  if ('contentBinding' in updates && faqPartTakesCms(part)) {
    const entry = faqEntryFor(host, itemId)
    if (!entry) return null
    const key = FAQ_BINDING_KEY[part]
    const binding = updates.contentBinding
    const next = binding?.collection && binding.field ? binding : undefined
    return {
      behavior: {
        ...host.behavior,
        items: (host.behavior.items ?? []).map((row) => {
          if (row.id !== entry.id) return row
          /* Se quita la clave, no se pone en «undefined»: con
             `exactOptionalPropertyTypes` no son lo mismo, y una clave presente
             con nada dentro es lo que el resto del editor lee como «atado». */
          const { [key]: _dropped, ...rest } = row
          return next ? { ...rest, [key]: next } : rest
        }),
      },
    }
  }

  const touchesStyles = Boolean(updates.styles)
  const touchesRefs = 'styleRefs' in updates
  if (!touchesStyles && !touchesRefs) return null

  const previous = host.behavior.partRefs?.[part]
  const touchedKeys = touchesStyles
    ? [...new Set(Object.values(updates.styles ?? {}).flatMap((byBreakpoint) => Object.keys(byBreakpoint ?? {})))]
    : []
  const overrides = touchedKeys.length > 0
    ? [...new Set([...(previous?.overrides ?? []), ...touchedKeys])]
    : previous?.overrides

  const nextRefs = touchesRefs
    ? updates.styleRefs
    : overrides && overrides.length > 0
      ? { ...previous, overrides }
      : previous

  return {
    behavior: {
      ...host.behavior,
      ...(touchesStyles ? { partStyles: { ...host.behavior.partStyles, [part]: updates.styles } } : {}),
      ...(nextRefs ? { partRefs: { ...host.behavior.partRefs, [part]: nextRefs } } : {}),
    },
  }
}

export function applyReviewPartUpdate(
  host: ElementNode,
  part: ReviewPart,
  updates: Partial<ElementNode>,
): Partial<ElementNode> | null {
  if (host.behavior?.type !== 'reviews') return null
  /* Los estilos y los tokens; el resto no. El texto de una reseña sale de la
     lista, no del lienzo, y dejar que un control de contenido escribiera aquí lo
     pisaría. */
  const touchesStyles = Boolean(updates.styles)
  const touchesRefs = 'styleRefs' in updates
  if (!touchesStyles && !touchesRefs) return null

  /*
   * Lo que se toca a mano queda anotado como anulación.
   *
   * Cada pieza nace con un rol del tema —la tarjeta con `card`, el texto con
   * `paragraph`—, y al dibujar, el rol SE LLEVA sus propiedades: es lo que hace
   * que cambiar la tipografía del sitio alcance también a las reseñas. El
   * efecto secundario es que un color puesto a mano sobre el texto se
   * descartaba, porque el color es una de las que el rol posee.
   *
   * `overrides` existe justo para esto: son las propiedades donde el valor del
   * elemento gana al del token. Se anota la que se acaba de tocar y nada más,
   * así que el resto sigue siguiendo al tema — que es la mitad del trato.
   */
  const previous = host.behavior.partRefs?.[part]
  const touchedKeys = touchesStyles
    ? [...new Set(Object.values(updates.styles ?? {}).flatMap((byBreakpoint) => Object.keys(byBreakpoint ?? {})))]
    : []
  const overrides = touchedKeys.length > 0
    ? [...new Set([...(previous?.overrides ?? []), ...touchedKeys])]
    : previous?.overrides

  const nextRefs = touchesRefs
    ? updates.styleRefs
    : overrides && overrides.length > 0
      ? { ...previous, overrides }
      : previous

  return {
    behavior: {
      ...host.behavior,
      ...(touchesStyles ? { partStyles: { ...host.behavior.partStyles, [part]: updates.styles } } : {}),
      ...(nextRefs ? { partRefs: { ...host.behavior.partRefs, [part]: nextRefs } } : {}),
    },
  }
}

/**
 * Cache keyed on the host node, so the same host yields the same part object.
 *
 * `selectActiveElement` is a Zustand selector: returning a freshly built node on
 * every call makes the store's snapshot look different each render, and React
 * re-renders forever. Host nodes are replaced immutably on every edit, so this
 * invalidates itself exactly when the model changes.
 */
const formPartNodeCache = new WeakMap<ElementNode, Partial<Record<FormPart, ElementNode>>>()

/**
 * A stand-in node for a form part, so the inspector can edit it like anything
 * else. It is never stored in the tree: what persists is `form.partStyles` and
 * `form.partRefs`, and this is rebuilt from them.
 */
export function buildFormPartNode(host: ElementNode, part: FormPart): ElementNode | undefined {
  if (!host.form) return undefined
  const cached = formPartNodeCache.get(host)
  const hit = cached?.[part]
  if (hit) return hit
  /* El botón de enviar sigue el estilo global de botón principal, como
     cualquier botón insertado (`styleRefs.button`): con sólo el rol salía con
     otra letra —blanca sobre un verde lima— y sin la sombra ni el borde del
     resto de botones del sitio. Si la persona le eligió otro, manda el suyo. */
  const styleRefs = host.form.partRefs?.[part] ?? (part === 'submit' ? { button: 'button-primary' } : undefined)
  const role = FORM_PART_ROLES[part]
  const node: ElementNode = {
    id: formPartId(host.id, part),
    type: FORM_PART_TYPES[part],
    label: formPartLabel(part),
    children: [],
    styles: host.form.partStyles?.[part] ?? { desktop: {} },
    ...(role ? { themeRole: role } : {}),
    ...(styleRefs ? { styleRefs } : {}),
  }
  formPartNodeCache.set(host, { ...cached, [part]: node })
  return node
}

/** Folds an inspector edit on a part back into the host's form model. */
export function applyFormPartUpdate(
  host: ElementNode,
  part: FormPart,
  updates: Partial<ElementNode>,
): Partial<ElementNode> | null {
  if (!host.form) return null
  const form = { ...host.form }
  if (updates.styles) form.partStyles = { ...form.partStyles, [part]: updates.styles }
  if ('styleRefs' in updates) form.partRefs = { ...form.partRefs, [part]: updates.styleRefs }
  // Anything else an inspector control might send (content, href, label…) has no
  // meaning for a part: the text comes from the field list, not from the canvas.
  if (!updates.styles && !('styleRefs' in updates)) return null
  return { form }
}


export type SectionBackgroundRole =
  | 'auto'
  | 'background'
  | 'surface'
  | 'muted'
  | 'primary'
  | 'accent'
  | 'custom'

export const CMS_SYSTEM_FIELDS: CmsField[] = [
  /* El cuerpo de la ficha, en todas las colecciones y sin tener que pedirlo.
     Una ficha es casi siempre algo que se escribe —un post, la descripción de un
     producto, una nota—, y cuando el campo había que crearlo a mano la mitad de
     las colecciones se quedaban con un puñado de casillas y ningún sitio donde
     contar nada. Al ser un campo de verdad y no un hueco aparte, se puede
     enlazar desde el lienzo como cualquier otro. */
  { id: 'content', name: 'Contenido', type: 'richText' },
  { id: 'status', name: 'Estado', type: 'text' },
  { id: 'slug', name: 'Slug', type: 'text' },
  { id: 'created', name: 'Fecha de creación', type: 'text' },
  { id: 'seoTitle', name: 'SEO · Título', type: 'text' },
  { id: 'seoDescription', name: 'SEO · Descripción', type: 'text' },
  { id: 'seoImage', name: 'SEO · Imagen', type: 'image' },
]

export const CMS_SYSTEM_FIELD_IDS = new Set(CMS_SYSTEM_FIELDS.map((field) => field.id))
/**
 * Los de sistema que ya NO se ponen solos: «Contenido» (2026-10-20, él lo
 * pidió). Salía en toda colección, pegado al final de la ficha, y confundía —
 * unas preguntas frecuentes tenían «Respuesta» y además un «Contenido» que no
 * era nada—. Ahora es un texto largo más: el blog lo trae en su definición, y
 * la colección que ya lo tenía lo conserva como un campo normal (se renombra,
 * se mueve y se borra). Quien lo quiera, lo agrega como cualquier propiedad.
 */
export const CMS_OPTIONAL_SYSTEM_FIELD_IDS = new Set(['content'])
export const CMS_LOCKED_FIELD_IDS = new Set(['title', ...[...CMS_SYSTEM_FIELD_IDS].filter((id) => !CMS_OPTIONAL_SYSTEM_FIELD_IDS.has(id))])
export const CMS_SEO_FIELD_IDS = ['seoTitle', 'seoDescription', 'seoImage'] as const

export type ContentBinding = {
  source: 'projectContent'
  collection: string
  itemId?: string
  field: string
}

/**
 * Qué ofertas enseña un bloque de catálogo, y con qué forma.
 *
 * ── Por qué `kind` y `offerIds` conviven ─────────────────────────────────────
 * Son las dos maneras legítimas de querer un catálogo, y no se pueden reducir a
 * una. «Todos mis servicios» es una regla: quien da de alta el séptimo espera
 * verlo aparecer sin volver al editor. «Estos tres» es una lista: quien la
 * escribió eligió el orden y no quiere que un alta se le cuele en medio.
 *
 * La lista manda cuando la hay. Un bloque con ofertas elegidas a mano Y una
 * familia puesta sería una contradicción esperando a que alguien la descubra.
 */
export type CatalogBinding = {
  /** `all` o una de las tres familias del catálogo. Se ignora si hay `offerIds`. */
  kind: 'all' | 'bookings' | 'digital' | 'products' | 'services' | 'memberships'
  /**
   * Ofertas elegidas a mano, en el orden en que se enseñan.
   *
   * Los ids y no las fichas: el título y el precio de una oferta se editan en el
   * panel, y guardarlos aquí dejaría el bloque contando lo que costaba el día
   * que se insertó.
   */
  offerIds?: string[] | undefined
  /** Cómo se reparten las tarjetas. `featured` es una sola, a lo grande. */
  layout: 'grid' | 'list' | 'carousel' | 'featured'
  /** Cuántas caben por fila en pantalla ancha. Sin valor, tres. */
  columns?: 1 | 2 | 3 | 4 | undefined
  /** Tope de tarjetas. Sin valor, todas las que haya. */
  limit?: number | undefined
  /**
   * Saltarse las primeras N.
   *
   * Es lo que hace convivir «una destacada» con «y debajo el resto»: la
   * segunda se salta la que ya enseña la primera. Existe por el mismo motivo
   * que `CmsRepeat.offset`, y se resuelve igual.
   */
  offset?: number | undefined
  /** Qué lleva cada tarjeta. Ausente = sí, que es como se insertan. */
  showImage?: boolean | undefined
  showDescription?: boolean | undefined
  showPrice?: boolean | undefined

  /**
   * En qué orden. Ausente = el del catálogo, que es el del panel.
   *
   * Por VALOR y no por campo como en el CMS: aquí no hay columnas que elegir
   * —una oferta no tiene campos a medida—, y ofrecer «campo + dirección» sobre
   * cuatro datos fijos sería un mando de dos piezas para cuatro respuestas.
   */
  sort?: 'catalog' | 'priceAsc' | 'priceDesc' | 'title' | 'duration' | 'newest' | 'bestselling' | undefined
  /** Sólo las gratis, sólo las de pago, o todas. */
  price?: 'all' | 'free' | 'paid' | undefined
  /** Dejar fuera las que no tengan foto. Una tarjeta sin foto rompe la rejilla. */
  requireImage?: boolean | undefined

  /**
   * Los tokens con los que se viste, igual que `styleRefs` en un nodo.
   *
   * Ids de tokens de Marca, no valores. Guardar el radio o el tamaño dejaría
   * este bloque fuera del sistema: es lo que ya pasó una vez, cuando la hoja
   * traía los suyos escritos a mano y el catálogo era lo único del sitio que no
   * cambiaba al cambiar la marca.
   *
   * Vacío significa «el rol de siempre» —`card`, `heading4`, `paragraph`,
   * `heading5`—, así que un bloque recién insertado ya va a juego sin elegir
   * nada.
   */
  cardStyle?: string | undefined
  titleStyle?: string | undefined
  bodyStyle?: string | undefined
  priceStyle?: string | undefined
  /** El botón de cada tarjeta: id de un estilo de botón del editor. */
  buttonStyle?: string | undefined

  /**
   * Los textos que pone el bloque, cuando no sirven los de fábrica.
   *
   * Vacío = el que corresponde a la familia de cada oferta, que es lo bueno por
   * defecto: en un catálogo mixto, una reserva dice «Ver horarios» y una
   * descarga «Conseguirlo». Escribir algo aquí lo iguala para todas, y es la
   * decisión de quien quiere su propia voz —«Pídelo», «Reserva ya»—.
   */
  ctaText?: string | undefined
  /** Qué se lee cuando no hay ninguna oferta que enseñar. */
  emptyText?: string | undefined
}

export type CmsRepeat = {
  source: 'projectContent'
  collection: string
  limit?: number
  /** Skip the first N items (used by "featured + grid" so the grid doesn't repeat the featured item). */
  offset?: number
  /**
   * Por página, con «‹ 1 2 3 ›» debajo: la lista de `/blog`. El lienzo enseña
   * la primera; al publicar, la plantilla lleva la marca y el worker sirve la
   * página pedida (`?p=`). Ver `BLOG_PAGER_MARKER`. Las ofertas usan el suyo
   * (`offers.perPage`).
   */
  perPage?: number
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  filterField?: string
  filterValue?: string
  /**
   * Fichas elegidas a mano («Cuáles › Elegir a mano»), en este orden. Con
   * alguna, la lista enseña sólo ésas: no se ordena ni se corta por `limit`.
   * Las que se borren de la colección simplemente no salen.
   */
  itemIds?: string[] | undefined
  /**
   * Recorrer las IMÁGENES de un campo en vez de las fichas de la colección.
   *
   * Es lo que convierte un repetidor en una galería: con `overField` puesto al
   * id de un campo de varias imágenes, cada copia dibuja una de sus imágenes en
   * lugar de una ficha distinta.
   *
   * La ficha sale del contexto de alrededor —el repetidor de arriba, o la página
   * de ficha— y no se elige aquí: una galería es «las fotos de ESTA venta», y
   * dejar elegir la ficha además del campo daría dos formas de decir lo mismo
   * que pueden contradecirse.
   */
  overField?: string | undefined
  /**
   * El aspecto de las tarjetas de una lista del CMS, como las del catálogo
   * (`offerCardLook.ts`): estilo, columnas, proporción de la foto y alineación.
   * Marcas en la lista; la tarjeta modelo no cambia. Las ofertas usan `offers`.
   */
  card?: {
    /** En fila (foto a un lado, texto al otro) en vez de tarjetas, o en carrusel (una fila que se desliza). */
    layout?: 'list' | 'carousel' | undefined
    /** En carrusel: llega hasta el borde derecho de la pantalla y las siguientes se asoman. */
    railBleed?: boolean | undefined
    /** El aspecto de cada fila en «Lista»: con líneas entre filas (por defecto), sin nada o en su tarjeta. */
    listStyle?: 'plain' | 'card' | undefined
    /** La foto de la fila a la derecha (por defecto, a la izquierda). */
    mediaSide?: 'right' | undefined
    /** En «Lista»: el hueco entre la foto y el texto. Vacío = el mediano. */
    listGap?: 'sm' | 'lg' | undefined
    /** En «Lista»: el espacio entre los textos de la fila. Vacío = el mediano. */
    listSpacing?: 'sm' | 'lg' | undefined
    /** El tamaño de la foto: en «Lista», su ancho; en tarjetas, su alto máximo. Vacío = el del diseño. */
    imageSize?: 'sm' | 'md' | 'lg' | undefined
    look?: 'minimal' | 'grid' | 'luxury' | 'elevated' | undefined
    columns?: 1 | 2 | 3 | 4 | undefined
    imageRatio?: 'square' | 'portrait' | 'tall' | 'landscape' | 'wide' | undefined
    textAlign?: 'left' | 'center' | undefined
  } | undefined
  /**
   * Cómo se acota el catálogo, cuando la colección es la de las ofertas.
   *
   * Aparte de `filterField`/`filterValue` porque aquí hacen falta varios filtros
   * a la vez —familia Y precio Y con foto— y esos dos sólo guardan uno. El orden
   * y el tope siguen siendo `sortField` y `limit`, los de cualquier colección.
   */
  offers?: {
    kind?: 'all' | 'bookings' | 'digital' | 'products' | 'services' | 'memberships' | undefined
    price?: 'all' | 'free' | 'paid' | undefined
    requireImage?: boolean | undefined
    /** Columnas visibles por fila en las listas de ofertas. */
    columns?: 1 | 2 | 3 | 4 | undefined
    /**
     * Rejilla (por defecto) o carrusel: una fila que se desliza de lado, con
     * flechas, en la que `columns` es cuántas se ven a la vez. Ver `offerRailAttrs`.
     */
    layout?: 'grid' | 'carousel' | undefined
    /**
     * Cuántas por página en una rejilla; con más, debajo sale «‹ 1 2 3 ›». Sin
     * valor, todas. Al publicar se hornea la página 1 y el guion de las listas
     * pasa de página. Ver `offerPagerHtml`.
     */
    perPage?: number | undefined
    /**
     * Filtros por colección arriba de la lista («Todos · Verano · Ofertas»):
     * las colecciones con algo de lo que enseña. En el sitio publicado.
     */
    collectionChips?: boolean | undefined
    /**
     * Los filtros de tienda arriba de la lista: ordenar, marca, talla, color,
     * precio, disponibilidad, duración, con quién… (salen los que tienen sentido
     * con lo que enseña). Sin valor: encendidos en la lista de la página de
     * cada familia (`lockedKind`), apagados en las demás. Ver `offerFilters.ts`.
     */
    filters?: boolean | undefined
    /**
     * El aspecto de las tarjetas, sin rehacerlas: tarjeta, minimal, rejilla con
     * bordes, luxury o elevada; la proporción de la foto, el texto al centro y
     * el botón sólo al pasar el ratón. Ver `offerCardLook.ts`.
     */
    cardLook?: 'card' | 'minimal' | 'grid' | 'luxury' | 'elevated' | undefined
    imageRatio?: 'auto' | 'square' | 'portrait' | 'tall' | 'landscape' | 'wide' | undefined
    textAlign?: 'left' | 'center' | undefined
    ctaOnHover?: boolean | undefined
    /** El estilo de botón del tema para los filtros y «Ordenar por». Vacío = el contorno de la barra. */
    filtersButton?: string | undefined
    /** Enseñar sólo una colección del panel (su `slug`). Vacío = todas las ofertas. */
    collection?: string | undefined
    /** Ofertas elegidas a mano, en su orden. Mandan sobre la familia. */
    offerIds?: string[] | undefined
    /**
     * La familia la fija la página: la lista de `/shop` enseña productos y no
     * se cambia a otra cosa (ver `catalogIndexElements`). Lo demás —orden,
     * columnas, tarjeta— sí.
     */
    lockedKind?: boolean | undefined
  } | undefined
  /**
   * Filtrar por vigencia: «las próximas», «las que ya pasaron».
   *
   * Aparte de `filterField`/`filterValue` porque no es un campo de la
   * colección: es un estado que se calcula, y que además se recalcula en el
   * navegador. Guardado como lista —`['upcoming','current']` es una sola
   * pestaña de «activas»—.
   */
  scheduleStates?: ScheduleState[] | undefined
}

/** The interaction state currently targeted by the inspector. `normal` edits the base
 * styles; the others edit `ElementNode.states[...]` and preview live in the canvas. */
export type InteractionState = 'normal' | 'hover' | 'pressed' | 'focus' | 'current' | 'open'

export type ElementInteraction = {
  href?: string
  target?: '_self' | '_blank'
  action?: 'link' | 'submit' | 'anchor'
  /** `false`: la imagen de una tarjeta del blog o del CMS NO abre su ficha (por defecto sí, ver `defaultItemLink`). */
  opensItem?: false
  cmsLink?: {
    source: 'projectContent'
    collection: string
    itemId?: string
    slugField?: string
    pageSlug?: string
  }
}

export type NavLink = {
  id: string
  label: string
  href: string
  kind: 'link' | 'dropdown' | 'mega'
  items?: Array<{ id: string; label: string; href: string }>
}

/**
 * Una reseña, tal y como se guarda en el bloque.
 *
 * Contenido gestionado y no nodos hijos, igual que los enlaces del nav: añadir
 * y quitar reseñas es una operación de lista, y cada una tiene los mismos
 * campos. Con nodos sueltos, la tercera reseña podría quedarse sin foto y con
 * el nombre en otro tamaño, y no habría forma de recortarlas todas por igual.
 */
/**
 * Una pregunta del bloque de preguntas frecuentes.
 *
 * Contenido gestionado y no nodos hijos, igual que las reseñas: añadir y quitar
 * preguntas es una operación de lista, y las diez tienen que verse iguales.
 */
export type FaqEntry = {
  id: string
  /** La pregunta, que es lo que se ve siempre. */
  question: string
  /** La respuesta, que aparece al abrir. */
  answer: string
  /**
   * Si arranca abierta.
   *
   * Ausente por defecto: todas las preguntas arrancan cerradas. Sólo se activa
   * cuando la persona elige explícitamente «Empieza abierta» para esa fila.
   */
  open?: boolean
  /**
   * De dónde sale la pregunta, cuando no se escribe aquí.
   *
   * Por PREGUNTA y no por bloque: atar la respuesta de una fila no puede tocar
   * las demás ni borrar el título que alguien escribió a mano. Es lo mismo que
   * hace cualquier texto de la página, aplicado a una fila de la lista.
   */
  questionBinding?: ElementNode['contentBinding']
  /** Y de dónde sale la respuesta. Lo que no se ata sigue escrito a mano. */
  answerBinding?: ElementNode['contentBinding']
}

export const FAQ_PARTS = ['item', 'question', 'answer', 'icon'] as const
export type FaqPart = (typeof FAQ_PARTS)[number]

export type ReviewEntry = {
  id: string
  /** Quién la escribe. */
  name: string
  /** El cargo, la ciudad o la fecha: lo que sitúe a quien la escribe. */
  meta?: string
  /** Su foto. */
  avatarUrl?: string
  /** El texto, entero. Lo que se recorta se decide al dibujar. */
  text: string
  /**
   * De dónde viene, elegida del catálogo: `google`, `yelp`, `tripadvisor`…
   *
   * Con esto la marca se dibuja sola —ver [[reviewSources]]— y no hay que ir a
   * buscar un logo fuera. Los dos campos de abajo siguen existiendo para «Otra»
   * y para las reseñas escritas antes de que hubiera catálogo.
   */
  source?: string
  /** El nombre, cuando la fuente no está en el catálogo. */
  sourceName?: string
  /** Y su logo, cuando se quiere el archivo oficial exacto. */
  sourceLogoUrl?: string
  /** De cero a cinco. Cero es «sin puntuación» y no dibuja estrellas. */
  rating?: number
}

/** Un logo del componente «Logos»: imagen o texto. */
export type LogoEntry = {
  id: string
  kind: 'image' | 'text'
  /** El nombre: lo que se lee si es texto, y el texto alternativo si es imagen. */
  text?: string | undefined
  src?: string | undefined
  href?: string | undefined
}

export type ElementBehavior =
  /**
   * Las dos piezas vivas de una plantilla de oferta (ver `OFFER_BUY_SLOT` en
   * @webbuilder/schemas): la zona de compra —precio, opciones, cantidad,
   * envío, cupón y botón— y la galería de fotos. En el lienzo se ven como una
   * muestra con la oferta elegida; publicadas dejan su hueco y el worker pone
   * la de verdad. Lo que se diseña aquí es la CAJA: dónde va y cómo se viste.
   */
  | { type: 'offerBuy' }
  | { type: 'offerGallery'; layout?: 'thumbnails' | 'stack' }
  /**
   * «Información del producto»: los campos de «Información adicional» de la
   * oferta, solos, en pestañas, acordeón o uno tras otro. Ver `offerDetailsHtml`.
   */
  | {
      type: 'offerDetails'
      layout?: 'tabs' | 'accordion' | 'stack'
      /** Ids de campo que no salen (la descripción es `__description`). */
      hidden?: string[]
      /** Ids en el orden elegido. */
      order?: string[]
      /** Si la descripción entra como primera sección. Por defecto sí; `false` la quita. */
      description?: boolean
      initiallyOpen?: boolean
    }
  | {
      type: 'beforeAfter'
      beforeImage?: string
      afterImage?: string
      beforeLabel?: string
      afterLabel?: string
      /** Initial divider position, from 0 to 100. */
      position?: number
    }
  | {
      /**
       * Galería de imágenes.
       *
       * Un solo widget con DOS orígenes en vez de dos widgets: lo único que
       * cambia entre «mis imágenes» y «las de una ficha» es de dónde sale la
       * lista. Todo lo demás —columnas, proporción, carrusel, la vista grande—
       * es igual, y separarlos obligaría a hacer cada mejora dos veces.
       */
      type: 'gallery'
      /** `manual`: fotos elegidas. `cms`: campo de imágenes. `offer`: oferta actual. */
      source?: 'manual' | 'cms' | 'offer'
      /** Las imágenes, cuando el origen es manual. */
      images?: string[]
      /** La colección y el campo, cuando el origen es el CMS. */
      collection?: string
      field?: string
      /** La oferta, cuando el origen es `offer`. Vacío: la de la página (plantilla de oferta). */
      offer?: string
      /** Rejilla, carrusel deslizable, o una tras otra a lo alto. */
      layout?: 'grid' | 'carousel' | 'stack'
      /** Cuántas caben por fila, y cuántas se ven en el carril. */
      columns?: 1 | 2 | 3 | 4 | 5 | 6
      /* El hueco NO vive aquí: es el `gap` del propio bloque, que ya se ajusta
         en Diseño. Un segundo control para lo mismo se contradice con el
         primero en cuanto alguien toca uno de los dos. */
      /**
       * La proporción de cada hueco.
       *
       * `auto` deja cada imagen con la suya: es lo honesto cuando vienen de
       * sitios distintos, pero descuadra la rejilla. El resto recorta al mismo
       * marco, que es lo que hace que se lea como una galería y no como un
       * montón de fotos.
       */
      ratio?: 'auto' | 'square' | 'wide' | 'portrait'
      /** Cuántas enseñar como mucho. Cero o ausente: todas. */
      limit?: number
      /** Skip this many images, for example the cover already shown above. */
      offset?: number
      /** Si al pulsar una se abre grande. Por defecto sí. */
      lightbox?: boolean
      /** El texto alternativo compartido, para cuando no hay uno por imagen. */
      alt?: string
    }
  | {
      /**
       * Preguntas frecuentes: una lista de preguntas que se abren al pulsarlas.
       *
       * El contenido lo lleva el comportamiento y no unos hijos —ver
       * [[FaqEntry]]—, por lo mismo que las reseñas: son una lista, y lo que
       * cambia entre una pregunta y otra es el texto, no el diseño.
       */
      type: 'faq'
      items?: FaqEntry[]
      /**
       * Si abrir una cierra las demás.
       *
       * Por defecto sí: es lo que hace que una lista larga siga siendo una lista
       * y no una pared de texto. Se puede apagar cuando las respuestas son
       * cortas y compararlas tiene sentido.
       */
      single?: boolean
      /** El signo del mando: «+» que gira a «×», o una flecha que gira. */
      marker?: 'plus' | 'chevron'
      /**
       * De qué lado va el signo. Por defecto al final, que es donde se busca.
       *
       * Al principio es la otra convención de siempre —la del índice de un
       * libro—, y con preguntas cortas se lee mejor: el signo queda pegado al
       * texto en vez de al otro extremo de una fila casi vacía.
       */
      markSide?: 'start' | 'end'
      /** Lo que mide el signo. Ausente = 16px, lo que trae la hoja. */
      markSize?: string
      /**
       * Una línea entre preguntas.
       *
       * La otra forma de separar una lista, y la que no cambia el fondo: con
       * tarjetas se ve el corte por el hueco, y sin ellas —el caso más común—
       * una lista sin líneas es un bloque de texto corrido.
       */
      divider?: boolean
      /** Si la respuesta entra deslizando. Por defecto sí. */
      animate?: boolean
      /**
       * Separación entre preguntas. Ausente = 0, que es como nació.
       *
       * Va en el comportamiento y no en los estilos de una pieza porque el
       * contenedor de la lista no es seleccionable: se dibuja alrededor de las
       * filas y no tiene ficha propia en el panel. Sin esto, las preguntas se
       * tocan y una lista larga se lee como un bloque macizo, sin forma de
       * separarlas desde ninguna parte.
       */
      gap?: string
      /**
       * Separación DENTRO de la fila: entre el texto de la pregunta y el signo.
       *
       * Otra cosa que la de arriba, y por eso otro ajuste: aquélla separa una
       * pregunta de la siguiente y ésta airea la fila por dentro. Con una sola
       * habría que elegir cuál de las dos se puede tocar.
       */
      rowGap?: string
      /** Estilos por pieza, igual que en reseñas. */
      partRefs?: Partial<Record<FaqPart, ElementStyleRefs>>
      partStyles?: Partial<Record<FaqPart, ElementStyles>>
    }
  | {
      // Generic disclosure: a trigger reveals a freely-designed panel. Children carry
      // schemaIds wb-popover-trigger / wb-popover-panel; runtime + CSS handle it.
      type: 'popover'
      /** Opens on click/tap or on hover (hover falls back to click on touch). */
      openOn?: 'click' | 'hover'
      /**
       * How the panel appears.
       *
       * Las cuatro primeras FLOTAN sobre la página: el contenido de alrededor no
       * se entera y sigue donde estaba. `inline` es la quinta y es distinta: el
       * panel es un bloque más del flujo, así que al abrirse empuja hacia abajo
       * lo que venga después. Es lo que se espera de un «leer más», de una
       * pregunta frecuente o de una ficha que se despliega.
       */
      presentation?: 'dropdown' | 'drawer' | 'modal' | 'fullscreen' | 'inline'
    }
  | {
      type: 'marquee'
      /** El sentido en escritorio, que dice también el eje: izquierda/derecha o arriba/abajo. */
      direction?: 'left' | 'right' | 'up' | 'down'
      /**
       * El eje en tableta y en celular, si cambia: un carrusel vertical junto
       * al texto en escritorio suele querer correr de lado en una pantalla
       * estrecha. Sin poner, el del tamaño de arriba. El sentido se conserva:
       * «arriba» corre a la izquierda y «abajo», a la derecha. Ver `marqueeAxisAt`.
       */
      axisTablet?: 'horizontal' | 'vertical'
      axisMobile?: 'horizontal' | 'vertical'
      speed?: number
      pauseOnHover?: boolean
      fadeEdges?: boolean
      /**
       * La forma del recorrido. `line` (o sin poner), la cinta de siempre.
       * `wheel`, una rueda grande cuyo centro queda bajo el bloque: las
       * tarjetas suben por un lado, cruzan arriba inclinadas con la curva y
       * bajan por el otro. Ver `marqueeInner` y `.wb-wheel` en behaviorAssets.
       * `orbit`, un círculo entero a la vista: las piezas dan la vuelta
       * derechas, como logos o caras alrededor de un centro (`.wb-orbit`).
       */
      shape?: 'line' | 'wheel' | 'orbit'
      /** Rueda: cuánto se curva, 0 (casi recta) … 100 (muy cerrada). */
      wheelCurve?: number
      /** Rueda: si se recorta en el borde de la sección (sí, por defecto), no en el del bloque. */
      wheelClip?: boolean
      /** Rueda: el espacio entre tarjetas, en px. Sin poner, la separación de la caja. */
      wheelGap?: number
      /** Rueda: el ancho de cada tarjeta, en px, y su proporción («3 / 4»). Sin poner, el de cada pieza. */
      wheelCardWidth?: number
      wheelRatio?: string
    }
  | {
      type: 'countdown'
      targetDate?: string
      label?: string
      expiredLabel?: string
      showDays?: boolean
      showHours?: boolean
      showMinutes?: boolean
      showSeconds?: boolean
    }
  | {
      type: 'slider'
      autoplay?: boolean
      interval?: number
      showArrows?: boolean
      showDots?: boolean
      loop?: boolean
      transition?: 'slide' | 'fade'
      /**
       * Dónde van las flechas y los puntos: `inside`, encima de la diapositiva
       * (lo de siempre, y lo que tienen los ya creados), u `outside`, las
       * flechas a los lados y los puntos debajo, fuera de la tarjeta.
       */
      controls?: 'inside' | 'outside'
      /**
       * La diapositiva que se está editando.
       *
       * Es estado del editor y no del sitio: sólo se emite al lienzo, y la
       * página publicada siempre arranca por la primera. Vive aquí —y no en un
       * estado suelto— para que al volver al proyecto se siga en la misma, que
       * es lo que se espera de algo que se dejó a medias.
       */
      editorSlide?: number
    }
  | {
      // Apple-style card rail: several cards visible at once (overflow/peek),
      // advanced by arrows. Card widths come from the children's own styles.
      type: 'carousel'
      showArrows?: boolean
      /** Where the arrows sit in the row below the cards. */
      arrowsAlign?: 'left' | 'center' | 'right'
      snap?: boolean
      step?: 'card' | 'page'
      /** Clip overflow into a scroller only when explicitly enabled. */
      clip?: boolean
      /** Avanza solo en la página publicada (en el lienzo nunca, como el slider). */
      autoplay?: boolean
      /** Segundos entre avance y avance. */
      interval?: number
    }
  | {
      /**
       * Un porcentaje con su barra: el texto, el valor y la barra juntos, para
       * que cambiar el número mueva la barra. Ver `lib/progress.ts`.
       */
      type: 'progress'
      /** De 0 a 100. */
      value?: number
      /** Lo que mide («Clientes satisfechos»). Se traduce como cualquier texto. */
      barLabel?: string
      /** El texto arriba y el valor a la derecha, todo en una línea o sólo la barra. */
      layout?: 'stacked' | 'inline' | 'bar'
      showValue?: boolean
      /** Grosor de la barra, en px. */
      height?: number
      /** Color del relleno: el principal, el de acento o uno suave. */
      tone?: 'primary' | 'accent' | 'muted'
      /** Esquinas rectas en vez de redondas. */
      square?: boolean
    }
  | {
      /**
       * Logos de clientes, socios o «salimos en»: cada uno es una IMAGEN (el
       * archivo del logo) o un TEXTO (el nombre escrito como marca). La IA sólo
       * escribe nombres; quien tiene los archivos los cambia por imágenes. Ver
       * [[LogoEntry]] y `features/logos`.
       */
      type: 'logos'
      /** En una fila, en rejilla de mosaicos o deslizándose solos. */
      layout?: 'row' | 'grid' | 'marquee'
      /** Cuántos por fila en la rejilla. */
      columns?: 3 | 4 | 5 | 6
      /** Alto de cada logo, en px (el texto se escala a partir de él). */
      size?: number
      /** En gris, y en su color al pasar el puntero. */
      grayscale?: boolean
      /** Opacidad de 0.2 a 1. */
      opacity?: number
      /** Cada logo en su recuadro, como una tarjeta suave. */
      tiles?: boolean
      /** Segundos que tarda en pasar un logo, al deslizarse. */
      speed?: number
      items?: LogoEntry[]
    }
  | {
      /**
       * Reseñas de clientes, con su fuente y su foto.
       *
       * El contenido lo lleva el propio comportamiento y no unos hijos: ver
       * [[ReviewEntry]]. Lo que cambia entre disposiciones es sólo cómo se
       * colocan las tarjetas, así que pasar de carrusel a rejilla no toca ni
       * una reseña.
       */
      type: 'reviews'
      /** Cómo se colocan: carril con flechas, rejilla o una debajo de otra. */
      layout?: 'carousel' | 'grid' | 'list'
      /** Cuántas caben por fila en rejilla y cuántas se ven en el carril. */
      columns?: 1 | 2 | 3 | 4
      /**
       * A cuántos caracteres se corta el texto. Cero deja la reseña entera.
       *
       * Recortar es lo que permite que cuatro reseñas de largos distintos se
       * vean como una fila y no como una escalera.
       */
      truncateAt?: number
      showSource?: boolean
      showAvatar?: boolean
      showRating?: boolean
      /** El texto del botón que abre la reseña entera. */
      moreLabel?: string
      /** Los estilos de cada pieza, compartidos por todas las tarjetas. */
      partStyles?: Partial<Record<ReviewPart, ElementStyles>>
      /**
       * Y sus tokens: tipografía, color, botón.
       *
       * Sin esto, elegir un token sobre una pieza no hacía nada — el parche sólo
       * aceptaba estilos sueltos y descartaba en silencio la referencia. Las
       * partes de un formulario ya lo guardaban así; esto es lo mismo.
       */
      partRefs?: Partial<Record<ReviewPart, ElementStyleRefs>>
      reviews?: ReviewEntry[]
    }
  | {
      // Responsive navbar: inline links on desktop, hamburger + panel below the chosen
      // breakpoint. Children carry slot schemaIds (nav-logo / nav-links / nav-toggle /
      // nav-panel); CSS handles layout/collapse and the runtime wires the toggle (a11y).
      type: 'nav'
      /** Sticks to the top of the viewport while scrolling. */
      sticky?: boolean
      /** Breakpoint at which links collapse into the hamburger. */
      collapseAt?: 'tablet' | 'mobile'
      /** Mobile menu presentation. */
      mobileStyle?: 'drawer' | 'fullscreen' | 'dropdown'
      /** Scroll effect. */
      scrollEffect?: 'none' | 'shrink' | 'hide' | 'blur'
      /**
       * Cómo se coloca: de borde a borde, flotante —separada de los bordes, con
       * esquinas— o píldora —ajustada a su contenido, centrada—.
       */
      layout?: 'full' | 'floating' | 'pill'
      /**
       * Separación de los bordes y de arriba, en px, con `floating` o `pill`.
       * Los proyectos de antes guardan `sm` / `md` / `lg` (ver `navInsetPx`).
       */
      inset?: number | 'sm' | 'md' | 'lg'
      /**
       * Con `floating` o `pill`: la primera sección sube por detrás de la barra,
       * en vez de dejar una franja del fondo de la página alrededor. Encendido
       * si no se dice nada.
       */
      overlay?: boolean
      /**
       * Con `pill`: el espacio entre el logo y lo de la derecha, en px. La
       * píldora se ajusta a su contenido, así que es lo que decide su ancho.
       * Sin dato, 28 —lo de siempre—.
       */
      pillGap?: number
      /** How the link to the page being viewed is distinguished. */
      currentPageIndicator?: 'none' | 'underline' | 'background' | 'text'
      /**
       * El buscador del sitio: una lupa en la barra que busca en las páginas y
       * en el blog. Encendido si no se dice nada; se ve también en celular, fuera
       * del menú.
       */
      search?: boolean
      /**
       * El icono del carrito con su numerito. Sólo sale si el sitio usa carrito
       * (Ajustes › «Usar carrito»); esto lo apaga en ESTA barra.
       */
      cart?: boolean
      /**
       * El botón de la cuenta del cliente («Iniciar sesión» o su inicial). Sólo
       * sale si el sitio tiene cuentas de clientes (Ajustes › General); esto lo
       * apaga en ESTA barra. Lo pone el worker al servir la página.
       */
      account?: boolean
      /** Managed content model — the nav HTML is generated from this (edited in the right panel). */
      brand?: { name?: string; logoUrl?: string }
      links?: NavLink[]
      cta?: { label: string; href: string } | null
    }
  | {
      /* Pestañas: la tira elige un panel y se ve ese, uno solo, en cualquier
         tamaño. El panel que no está elegido sale del flujo, así que tampoco
         cuenta para el alto: ninguno estira a los demás. */
      type: 'tabs'
      /** Visual treatment of the tab selector. */
      tabStyle?: TabsStyle
      /** Mostrar la tira de botones. Sin ella sólo se pasa de panel solo. */
      showTabs?: boolean
      /** Advance panels automatically. */
      autoplay?: boolean
      /** Seconds between automatic advances. */
      interval?: number
      /** Wrap around at the ends. */
      loop?: boolean
      /**
       * Cómo entra el panel elegido. Vale igual en móvil, iPad y escritorio: lo
       * que se elige aquí es lo que se ve en los tres.
       *
       * `slide` lo trae del lado hacia el que se avanza, que es lo que cuenta
       * que hay más pestañas a ese lado. `fade` lo funde en el sitio, sin
       * dirección. `none` cambia y ya — con muchos paneles, o en algo que se
       * consulta a menudo, la animación sólo hace esperar.
       */
      transition?: 'slide' | 'fade' | 'none'
    }

export type ProjectContentItem = {
  id: string
  title?: string
  label?: string
  description?: string
  image?: string
  icon?: string
  url?: string
  email?: string
  phone?: string
  value?: string
}

// ─── Dynamic CMS (Framer-style collections) ───────────────────────────────────
/**
 * `multiImage` ya no se ofrece: es el mismo campo que `image`.
 *
 * Había dos tipos para lo mismo —uno de una foto y otro de varias— y eso
 * obligaba a decidir de antemano cuántas ibas a poner, que es una pregunta que
 * nadie sabe responder al crear la colección. Ahora «Imágenes» acepta una o
 * muchas y la PRIMERA es la principal.
 *
 * El tipo se queda en la unión porque las colecciones que ya existen lo tienen
 * guardado, y se comporta igual que `image` en todas partes. No hace falta
 * migrar nada: el valor se lee con `parseImageList`, que entiende tanto una url
 * suelta —lo que guardaba `image`— como la lista en JSON de `multiImage`.
 */
export type CmsFieldType = 'text' | 'title' | 'richText' | 'code' | 'image' | 'multiImage' | 'video' | 'link' | 'email' | 'phone' | 'number' | 'date' | 'dateRange' | 'option'
export type CmsField = {
  id: string
  name: string
  type: CmsFieldType
  options?: string[]
  /**
   * Color de una opción o categoría, por nombre.
   *
   * Se apunta al crearla y al cambiarlo a mano. Lo que no está aquí —lo que
   * venía de antes o de una importación— saca su color del propio nombre, así
   * que una colección sin nada de esto ya sale con colores estables en vez de
   * en gris. Ver `CMS_OPTION_COLORS` y `cmsOptionColor`.
   */
  optionColors?: Record<string, string>
  /** Varios valores separados por comas, en chips como las categorías (los autores de una entrada del blog). */
  multiple?: boolean | undefined
}

/**
 * La paleta de las opciones y categorías, cerrada a propósito.
 *
 * Doce colores y ni uno más: con un selector libre, cada categoría acaba con un
 * tono ligeramente distinto del resto y la tabla se vuelve ruido. Al ser una
 * lista corta, además, dos categorías del mismo color se leen como parejas, que
 * es justo para lo que sirve el color aquí.
 *
 * El orden es el de la rueda —neutros, cálidos, fríos— y no el alfabético: así
 * la cuadrícula de 4×3 del selector enseña los parecidos juntos, que es como se
 * elige un color.
 */
export const CMS_OPTION_COLORS = [
  { id: 'gray', label: 'Gris' },
  { id: 'brown', label: 'Café' },
  { id: 'red', label: 'Rojo' },
  { id: 'orange', label: 'Naranja' },
  { id: 'yellow', label: 'Amarillo' },
  { id: 'lime', label: 'Lima' },
  { id: 'green', label: 'Verde' },
  { id: 'teal', label: 'Turquesa' },
  { id: 'blue', label: 'Azul' },
  { id: 'indigo', label: 'Índigo' },
  { id: 'purple', label: 'Morado' },
  { id: 'pink', label: 'Rosa' },
] as const

/**
 * El color de una categoría u opción: el que tenga apuntado o, si no tiene,
 * uno sacado de su propio nombre.
 *
 * El de reserva sale del nombre y no de su posición en la lista, que es lo que
 * lo hace servir: «Rebajas» sale del mismo color en todas las fichas y sigue
 * saliendo igual después de añadir otra categoría antes o de reordenar.
 * Repartirlos por orden de aparición habría recoloreado media tabla con cada
 * alta.
 */
export function cmsOptionColor(field: CmsField, option: string): string {
  const name = option.trim()
  const picked = field.optionColors?.[name]
  if (picked) return picked
  const key = name.toLocaleLowerCase('es-MX')
  let hash = 0
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0
  return CMS_OPTION_COLORS[hash % CMS_OPTION_COLORS.length]!.id
}

// User-selectable field types (Spanish labels). 'title' is excluded — it's the fixed "Nombre" field.
/**
 * Cómo se llama cada tipo de campo, en CLAVES.
 *
 * Tienen que decir lo MISMO que la rejilla de tipos de `NewCollectionModal`:
 * quien elige «Texto largo» allí no reconocería «Texto extenso» aquí. Esa
 * coincidencia la fija `cmsFieldTypes.test.tsx`, y por eso las dos tablas
 * guardan claves y no frases — comparar texto ya escrito obligaría a que las dos
 * hablaran el mismo idioma en el mismo instante.
 */
export const CMS_FIELD_TYPE_LABELS: { value: CmsFieldType; labelKey: string }[] = [
  { value: 'text', labelKey: 'cms.type.text' },
  { value: 'richText', labelKey: 'cms.type.longText' },
  { value: 'code', labelKey: 'blk.code' },
  { value: 'image', labelKey: 'cms.type.images' },
  { value: 'video', labelKey: 'blk.videoLink' },
  { value: 'link', labelKey: 'toolbar.link' },
  { value: 'email', labelKey: 'cms.type.email' },
  { value: 'phone', labelKey: 'cms.type.phone' },
  { value: 'number', labelKey: 'cms.type.number' },
  { value: 'date', labelKey: 'cms.type.dateTime' },
  { value: 'dateRange', labelKey: 'schedule.window' },
  { value: 'option', labelKey: 'cms.option' },
]
/** An item is a row; `values` holds the working draft. `publishedValues` holds the live version. */
export type CmsItem = { id: string; values: Record<string, string>; publishedValues?: Record<string, string> | null }

export type CmsCollection = {
  id: string
  name: string
  /** Collection-level description (singleton CMS content for the collection/listing page). */
  description?: string
  /** Collection-level cover image (singleton CMS content). */
  image?: string
  icon?: string
  color?: string
  /** Field ids hidden from the table view (data is kept). */
  hiddenFields?: string[]
  /**
   * El orden de las columnas de la tabla, de izquierda a derecha.
   *
   * Mezcla ids de campos con los de `CMS_TABLE_COLUMNS`: la miniatura, el
   * estado y las dos fechas no son campos de la colección —salen de otros
   * sitios— pero se arrastran igual que los demás, así que su sitio tiene que
   * caber en la misma lista.
   *
   * Es un orden parcial y a propósito: lo que no figure aquí —una columna
   * añadida después de la última vez que se reordenó— se coloca al final en
   * lugar de desaparecer.
   */
  columnOrder?: string[]
  /**
   * Para qué sirve, cuando es una colección con papel propio. `menu`: los
   * platillos del restaurante, la que usan los bloques de la categoría Menú
   * (ver `lib/menuCollection`). Su id es el normal de cualquier colección; los
   * bloques la encuentran por este papel.
   */
  kind?: 'menu'
  /**
   * Los campos de fecha que dan vigencia a las fichas de esta colección.
   *
   * Declararla es lo que convierte una colección normal en una con estados —por
   * venir, en curso, terminada— que las listas pueden filtrar y los elementos
   * pueden usar para aparecer o no. Sin ella, todo se comporta igual que antes.
   *
   * No sabe de ventas: sirve igual para eventos, cursos, convocatorias o menús
   * del día. Ver `@webbuilder/shared-types` (`cmsSchedule`).
   */
  schedule?: ScheduleWindow | undefined
  fields: CmsField[]
  items: CmsItem[]
}

/** Las columnas de la tabla que no son campos de la colección. */
export const CMS_TABLE_COLUMNS = {
  image: '__image',
  status: '__status',
  created: '__created',
  modified: '__modified',
} as const

/** Sentinel used as `cmsRepeat.collection` to iterate over the collections themselves (a collections index). */
export const CMS_COLLECTIONS_SOURCE = '__collections__'

/**
 * La colección de las OFERTAS: el catálogo que se vende, como una colección
 * más del CMS.
 *
 * Virtual, igual que la de arriba: no vive en `projectContent` sino en la base
 * de comercio —precios, Stripe, reservas—, y el editor la lee del contexto del
 * catálogo. Tratarla como colección es lo que hace que un catálogo se edite
 * como un bloque de CMS: la tarjeta es un nodo normal con sus hijos, cada hijo
 * se enlaza a un campo, se viste con sus tokens y su CSS, y al tocar una
 * tarjeta cambian todas.
 *
 * Nunca se guarda en `projectContent`. Metida allí se publicaría como contenido
 * propio del sitio y quedaría contando los precios del día que se guardó.
 */
export const CMS_OFFERS_SOURCE = '__offers__'

/**
 * Las COLECCIONES DE TIENDA del panel (Verano, Nike…), como fuente de la
 * plantilla de su página (`/colecciones/<slug>`). No es `CMS_COLLECTIONS_SOURCE`,
 * que son las colecciones del CMS. El «elemento» es una colección, por su slug.
 */
export const SHOP_COLLECTION_SOURCE = '__shop_collection__'
/** En la plantilla de colección, la lista enseña «la colección que se ve»: `offers.collection`. */
export const CURRENT_SHOP_COLLECTION = '__current__'

/** Si una página es la plantilla de la página de cada colección. */
export function isCollectionTemplatePage(page: Pick<Page, 'cmsDynamic'> | null | undefined): boolean {
  return page?.cmsDynamic?.collection === SHOP_COLLECTION_SOURCE && page.cmsDynamic.kind === 'item'
}

/** Copia de un árbol con ids nuevos (los del lienzo y los de cada formulario): para duplicar una página. */
function withFreshNodeIds(elements: ElementNode[]): ElementNode[] {
  return elements.map((node) => ({
    ...node,
    id: crypto.randomUUID(),
    schemaId: node.schemaId ?? node.id,
    ...(node.form ? { form: { ...node.form, formId: crypto.randomUUID() } } : {}),
    children: withFreshNodeIds(node.children ?? []),
  }))
}

/** El diseño general de las colecciones de la tienda: el que usa toda colección sin uno propio. */
export function isSharedCollectionTemplate(page: Pick<Page, 'cmsDynamic'> | null | undefined): boolean {
  return isCollectionTemplatePage(page) && !page?.cmsDynamic?.forCollection
}

/** La página con que se ve una colección: la suya si la tiene, si no la general. */
export function collectionTemplateOf<P extends Pick<Page, 'cmsDynamic'>>(pages: readonly P[], slug: string | undefined): P | undefined {
  return (slug ? pages.find((page) => isCollectionTemplatePage(page) && page.cmsDynamic?.forCollection === slug) : undefined)
    ?? pages.find((page) => isSharedCollectionTemplate(page))
}

/**
 * Los campos de una oferta que se pueden colocar en una tarjeta.
 *
 * Fijos, como los de la colección de colecciones: una oferta no tiene campos a
 * medida. Los ids son los de la ficha que devuelve el worker —`CatalogItem`—,
 * así que lo que se lee en el lienzo y lo que repinta la página publicada es la
 * misma propiedad del mismo objeto.
 */
export const CMS_OFFER_FIELDS: CmsField[] = [
  { id: 'title', name: 'Nombre', type: 'title' },
  { id: 'description', name: 'Descripción', type: 'text' },
  { id: 'image', name: 'Foto', type: 'image' },
  { id: 'price', name: 'Precio', type: 'text' },
  /* La cifra sola y su periodicidad, por separado: una tabla de planes pinta
     «$29» en grande y «al mes» chico al lado. */
  { id: 'priceAmount', name: 'Importe', type: 'text' },
  { id: 'period', name: 'Periodo', type: 'text' },
  /* Las viñetas de la descripción, como lista con palomitas. */
  { id: 'features', name: 'Incluye', type: 'richText' },
  { id: 'duration', name: 'Duración', type: 'text' },
  /* Qué es: servicio, producto digital o reserva. Lo que lleva el antetítulo
     de una tarjeta que enseña ofertas que no son reservas (no tienen duración). */
  { id: 'type', name: 'Tipo', type: 'text' },
  { id: 'cta', name: 'Llamada a la acción', type: 'text' },
  /* «Más información»: el segundo botón de la tarjeta. Sólo tiene texto cuando
     el principal hace algo ahí mismo (agregar al carrito); si el principal ya
     lleva a la página de la oferta, se esconde para no tener dos iguales. */
  { id: 'more', name: 'Más información', type: 'text' },
  /* Las tallas, colores… de un producto con variantes, en una línea. */
  { id: 'options', name: 'Opciones', type: 'text' },
  /* «Agotado», «Últimas 3» o «Nuevo»: lo pone el worker según la oferta. */
  { id: 'badge', name: 'Distintivo', type: 'text' },
  /* Lo que una tienda completa enseña además en la tarjeta. Cada uno sale sólo
     si alguna oferta de la lista lo tiene (ver `availableOfferFields`). */
  { id: 'brand', name: 'Marca', type: 'text' },
  /* El «antes» tachado y el descuento, de la variante más barata. */
  { id: 'compareAt', name: 'Precio antes', type: 'text' },
  { id: 'discount', name: 'Descuento', type: 'text' },
  /* «Quedan 3», «Agotado». */
  { id: 'stock', name: 'Existencias', type: 'text' },
  /* En reservas: el cupo y quiénes la dan. */
  { id: 'spots', name: 'Cupo', type: 'text' },
  { id: 'with', name: 'Con quién', type: 'text' },
]
/**
 * La colección del BLOG: las entradas, como una colección más del CMS.
 *
 * Virtual, como la de ofertas: las entradas viven en la base del blog y se
 * escriben en el panel. Se carga al abrir el proyecto (`setBlogPosts`) para que
 * la plantilla de la entrada se diseñe con la maquinaria de siempre —campos
 * enlazados, «ver con esta ficha»— y NUNCA se guarda: el autoguardado la quita
 * (`withoutVirtualCollections`). Al publicar, su plantilla sale una sola vez,
 * con marcadores, y la rellena el worker (ver `BLOG_TEMPLATE_PATH`).
 */
export const CMS_BLOG_SOURCE = '__blog__'

/**
 * Los platillos, en los bloques de la biblioteca (categoría Menú). Es un alias:
 * al insertar el bloque en un proyecto se cambia por el id de SU colección de
 * menú (la crea si no hay; ver `adoptMenuBlocks`). Sólo lo ven el editor de
 * templates y las miniaturas, con platillos de muestra.
 */
export const CMS_MENU_SOURCE = '__menu__'

/** Los campos de una entrada que se enlazan en la plantilla. Los ids son los marcadores de `BLOG_TEMPLATE_FIELDS`. */
export const CMS_BLOG_FIELDS: CmsField[] = [
  { id: 'title', name: 'Título', type: 'title' },
  { id: 'excerpt', name: 'Extracto', type: 'text' },
  { id: 'image', name: 'Portada', type: 'image' },
  { id: 'content', name: 'Contenido', type: 'richText' },
  { id: 'author', name: 'Autor', type: 'text' },
  { id: 'date', name: 'Fecha', type: 'text' },
  { id: 'categories', name: 'Categorías', type: 'text' },
  { id: 'readingTime', name: 'Tiempo de lectura', type: 'text' },
  { id: 'authorAvatar', name: 'Foto del autor', type: 'image' },
  { id: 'slug', name: 'Slug', type: 'text' },
]

/** Las colecciones que no son del proyecto: se ven en el editor pero no se guardan ni se editan en el CMS. */
export function isVirtualCollection(id: string): boolean {
  return id === CMS_BLOG_SOURCE
}

/** El contenido del proyecto tal como se guarda: sin las colecciones virtuales. */
export function withoutVirtualCollections(content: ProjectContent): ProjectContent {
  return content.collections.some((collection) => isVirtualCollection(collection.id))
    ? { ...content, collections: content.collections.filter((collection) => !isVirtualCollection(collection.id)) }
    : content
}

/** Pseudo-fields exposed by a collection item (used by the collections index and the listing page). */
export const CMS_COLLECTION_META_FIELDS: CmsField[] = [
  { id: '__name', name: 'Nombre', type: 'title' },
  { id: '__description', name: 'Descripción', type: 'text' },
  { id: '__image', name: 'Imagen', type: 'image' },
]

/** Field ids that are internal/SEO and should NOT be offered as placeable CMS content on the canvas. */
/**
 * Lo que no se coloca en una página.
 *
 * El slug es la dirección de la ficha —se usa para enlazar, no para leerse— y
 * los de SEO son metadatos que van en la cabecera del documento. El estado sí
 * se coloca: enseñar «Publicado» o «Borrador» junto a una ficha es una etiqueta
 * como cualquier otra, y era lo único de esta lista que alguien querría poner a
 * la vista.
 */
export const CMS_NON_PLACEABLE_FIELD_IDS = new Set(['slug', 'seoTitle', 'seoDescription', 'seoImage'])
/** A field a user can drop onto the canvas as bound content (excludes status/slug/SEO). */
export function isPlaceableCmsField(field: CmsField): boolean {
  return !CMS_NON_PLACEABLE_FIELD_IDS.has(field.id)
}

/**
 * The one field that represents an item's cover/thumbnail everywhere in the UI.
 *
 * This must be stable: choosing the first image that happens to contain a value
 * makes the table, the canvas and the dynamic page point at different fields as
 * soon as a collection has a gallery or an OG image. Existing collections use
 * `image`; custom collections fall back to their first visible image field.
 */
/** El texto de una tarjeta de contacto sin marcado, para sacar de él su enlace. */
function plainContactText(content: unknown): string {
  return typeof content === 'string' ? content.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim() : ''
}

export function isCmsImageField(field: Pick<CmsField, 'type'>): boolean {
  return field.type === 'image' || field.type === 'multiImage'
}

export function cmsPrimaryImageField(collection: Pick<CmsCollection, 'fields'>): CmsField | undefined {
  const isImageField = (field: CmsField) => isCmsImageField(field)
  return collection.fields.find((field) => field.id === 'image' && isImageField(field))
    /**
     * Una imagen SUELTA gana a una galería.
     *
     * El campo `image` de las colecciones de plantilla manda siempre, pero los
     * campos que se crean a mano reciben un id `field-xxxxxx` —nunca `image`—,
     * así que ahí no hay nada canónico y hay que decidir por el tipo.
     *
     * Y por el tipo está claro: una imagen sencilla es UNA foto que representa
     * la ficha; una galería es un conjunto donde la primera no es más
     * importante que las otras, sólo va delante. Quien añade una imagen suelta a
     * una colección que ya tenía galería está diciendo justamente eso.
     *
     * Antes decidía el orden de declaración, así que la portada dependía de en
     * qué momento se creó cada campo: añadir una imagen a una colección con
     * galería no cambiaba nada y no había forma de entender por qué.
     */
    ?? collection.fields.find((field) => field.type === 'image' && isPlaceableCmsField(field))
    ?? collection.fields.find((field) => isImageField(field) && isPlaceableCmsField(field))
}

/** Map a blueprint's field id to a real field of the target collection, by id then by role/type. */
function resolveCmsFieldId(collection: CmsCollection, originalFieldId: string, elementType: string): string {
  const fields = collection.fields
  if (fields.some((f) => f.id === originalFieldId)) return originalFieldId
  const firstOfType = (type: CmsFieldType) => fields.find((f) => f.type === type)?.id
  if (elementType === 'image' || originalFieldId === 'image') return cmsPrimaryImageField(collection)?.id ?? originalFieldId
  if (originalFieldId === 'content') return firstOfType('richText') ?? originalFieldId
  if (originalFieldId === 'title') return firstOfType('title') ?? originalFieldId
  if (originalFieldId === 'created' || originalFieldId === 'date') {
    return fields.find((f) => f.id === 'created' || /date|fecha/i.test(f.name))?.id ?? originalFieldId
  }
  if (originalFieldId === 'description') {
    return fields.find((f) => f.id === 'description')?.id ?? fields.find((f) => f.type === 'text')?.id ?? originalFieldId
  }
  return originalFieldId
}

/**
 * Prepares a CMS blueprint for a collection on drop: assigns the collection to every
 * repeater that doesn't have one, and remaps current-item field bindings to the
 * collection's real fields (so blocks work with any collection). Meta bindings are left alone.
 */
export function bindCmsBlueprintToCollection(node: ElementNode, collection: CmsCollection): ElementNode {
  /* Una lista de OFERTAS no se ata a una colección del CMS. Sus hijos están
     enlazados a los campos fijos de una oferta —`title`, `price`, `image`—, y
     reasignarlos al campo más parecido de una colección cualquiera dejaría las
     tarjetas leyendo otra cosa sin ningún aviso. Hoy no llega aquí —sólo se
     llama con bloques de la categoría `cms`—, y esto es para que siga así
     aunque mañana alguien cambie un bloque de categoría. */
  if (node.cmsRepeat?.collection === CMS_OFFERS_SOURCE) return node
  const next: ElementNode = { ...node }
  if (next.cmsRepeat?.source === 'projectContent' && !next.cmsRepeat.collection) {
    next.cmsRepeat = { ...next.cmsRepeat, collection: collection.id }
  }
  const binding = next.contentBinding
  if (binding?.source === 'projectContent' && binding.collection === '' && binding.field && !binding.field.startsWith('__')) {
    next.contentBinding = { ...binding, field: resolveCmsFieldId(collection, binding.field, next.type) }
  }
  if (next.children.length > 0) {
    next.children = next.children.map((child) => bindCmsBlueprintToCollection(child, collection))
  }
  return next
}

/**
 * Cambiar la colección de una lista: sus piezas pasan a los campos
 * equivalentes de la nueva.
 *
 * Sin esto, al pasar una lista del Blog a «Portfolio» la tarjeta seguía
 * enlazada a `excerpt` o `image` del blog —campos que Portfolio no tiene— y
 * salía «Sin contenido» y la foto vacía, sin explicación. Por cada pieza: el
 * mismo campo si existe; si no, uno con el mismo nombre; si no, uno del mismo
 * tipo (la foto, a la foto principal). Las listas anidadas de otra colección no
 * se tocan.
 */
export function rebindCardToCollection(node: ElementNode, from: CmsCollection | undefined, to: CmsCollection): ElementNode {
  const remap = (fieldId: string, elementType: string): string => {
    if (to.fields.some((field) => field.id === fieldId)) return fieldId
    const old = from?.fields.find((field) => field.id === fieldId)
    if (old) {
      const byName = to.fields.find((field) => field.name.trim().toLowerCase() === old.name.trim().toLowerCase())
      if (byName) return byName.id
      if (isCmsImageField(old)) return cmsPrimaryImageField(to)?.id ?? fieldId
      const byType = to.fields.find((field) => field.type === old.type && isPlaceableCmsField(field))
      if (byType) return byType.id
    }
    return resolveCmsFieldId(to, fieldId, elementType)
  }
  const visit = (current: ElementNode, root: boolean): ElementNode => {
    if (!root && current.cmsRepeat) return current
    const next: ElementNode = { ...current }
    const binding = next.contentBinding
    if (binding?.source === 'projectContent' && binding.collection === '' && binding.field && !binding.field.startsWith('__')) {
      next.contentBinding = { ...binding, field: remap(binding.field, next.type) }
    }
    if (next.children.length > 0) next.children = next.children.map((child) => visit(child, false))
    return next
  }
  return visit(node, true)
}

/**
 * Una sección reutilizable: el original, del que cuelgan todas sus copias.
 *
 * ── Por qué vive en el PROYECTO y no en la biblioteca de la cuenta ──────────
 * «Mis secciones» es de la cuenta y sirve para llevarse un bloque de un proyecto
 * a otro: ahí lo que se copia es el contenido, y las copias no se hablan entre
 * sí. Un componente es lo contrario: una sola verdad compartida por las páginas
 * de ESTE sitio.
 *
 * Y hay una razón que decide sola: la página publicada tiene que ser
 * autocontenida. Si una instancia sólo apuntara a una fila de la biblioteca,
 * publicar tendría que ir a buscarla —y el HTML dejaría de poder generarse sin
 * red—. Viviendo en `projectContent`, el renderizador ya lo recibe en los tres
 * caminos, incluido el de publicar.
 */
export type ProjectComponent = {
  id: string
  name: string
  /** El contenido, tal cual se dibuja en cada instancia. */
  node: ElementNode
  updatedAt?: string
  /**
   * La barra o el pie DEL SITIO.
   *
   * No tiene instancias en las páginas: se dibuja arriba o abajo de todas
   * (`withSiteLayout`). Por eso vive aquí, con las secciones reutilizables:
   * editar un elemento suyo desde cualquier página ya se escribe en el original
   * sin más cableado (`applyToOwningTree`), que es justo lo que se quiere —una
   * sola barra para todo el sitio—. No sale en la biblioteca de secciones.
   */
  slot?: SiteSlot
}

/**
 * `banner`: la franja de encima de la barra —un aviso, una promo, las redes—.
 * Se elige entre las secciones de la categoría `banner` (Capas › «Agregar
 * banner») y, como la barra, sale en todas las páginas.
 */
export type SiteSlot = 'banner' | 'nav' | 'footer'

/** Qué hace una página con la barra y el pie del sitio. Sin valor, se enseñan. */
export type PageLayout = {
  banner?: 'show' | 'hide'
  /** `minimal`: sólo la marca, sin menú. Para el pago o una landing. */
  nav?: 'show' | 'minimal' | 'hide'
  footer?: 'show' | 'hide'
}

export type ProjectContent = {
  business: {
    name: string
    tagline: string
    description: string
    logo: string
    /** El horario: lo pone el asistente y se cambia en Información del negocio. Va al pie. */
    hours?: BusinessHoursRow[]
    /** Las sucursales, con dos o más (`locations.ts` en schemas). Con una, `contact.address`. */
    locations?: BusinessLocation[]
  }
  navigation: {
    items: ProjectContentItem[]
  }
  /** User-defined dynamic collections (replaces the old fixed services/people/etc.). */
  collections: CmsCollection[]
  contact: {
    email: string
    phone: string
    address: string
  }
  /**
   * El botón de contacto flotante (WhatsApp, llamar, correo, mensaje), en
   * todas las páginas. Ver `contactFloat.ts` en schemas.
   */
  contactFloat?: ContactFloatConfig
  /**
   * Las secciones reutilizables del sitio.
   *
   * Opcional: un proyecto de antes de que existieran no las tiene, y no tenerlas
   * no es un estado inválido — es no haber creado ninguna.
   */
  components?: ProjectComponent[]
  /**
   * Las redes que el dueño dio al crear el sitio con IA —Instagram, LinkedIn,
   * WhatsApp…—. De aquí salen los enlaces del pie la primera vez que se pone
   * (`withDefaultSiteChrome`); después el pie es suyo y se edita en el lienzo.
   */
  socials?: ProjectSocial[]
}

export type ProjectSocial = { label: string; url: string; id?: string; network?: string }







/**
 * The brand mark, as one token every inserted logo follows.
 *
 * The four arrangements the UI offers — logo+nombre, sólo nombre, icono+nombre,
 * icono — are not four settings but two: what the mark is, and whether the name
 * shows next to it. Storing the combination instead would make "hide the name"
 * a different edit depending on which arrangement is active.
 */



export type ElementStyleRefs = {
  typography?: string
  button?: string
  /** An empty value records an explicit detach, so legacy card inference does
   * not silently bind the element to `card-default` again. */
  card?: string
  /** Same as `card`: an empty value detaches from `section-default` on purpose. */
  section?: string
  icon?: string
  colors?: Partial<Record<'color' | 'background' | 'backgroundColor' | 'borderColor' | 'boxShadowColor', string>>
  /**
   * Token-owned properties where this element's own value wins. Without it the
   * only way to override one property was to detach from the token entirely,
   * which froze every other property at its value on that day — a button that
   * had its weight changed stopped following the brand's border forever.
   */
  overrides?: string[]
}

export { RATING_ICONS, RATING_EMPTY_MODES } from '@webbuilder/schemas'
export type { RatingIcon, RatingEmpty } from '@webbuilder/schemas'

export type SocialLinksSettings = {
  /** Sólo iconos, icono y nombre, botones, o una lista hacia abajo. */
  style: 'icons' | 'labels' | 'buttons' | 'list'
  /** El fondo de cada icono: círculo, cuadrado o ninguno. */
  shape?: 'circle' | 'square' | 'plain' | undefined
  /** Los colores de cada red en vez de los del sitio. */
  brandColors?: boolean | undefined
  size?: 'sm' | 'md' | 'lg' | undefined
  /** Las redes (`instagram`, `tiktok`…) que no salen en ÉSTE. */
  hidden?: string[] | undefined
}

export type ElementNode = {
  id: string
  type: ElementType
  children: ElementNode[]
  /** Breakpoint-aware styles. Edit panel always targets `desktop`; canvas resolves per breakpoint. */
  styles: ElementStyles
  /** Automatic foreground contrast is the default; off/light are explicit overrides. */
  textContrastMode?: 'auto' | 'off' | 'light' | undefined
  /** Stylable interaction states. Each holds style overrides applied via CSS pseudo/attr selectors
   * (hover→:hover, focus→:focus-visible, current→[aria-current="page"], open→ancestor [data-*-open]). */
  states?: {
    hover?: StyleProperties
    pressed?: StyleProperties
    focus?: StyleProperties
    current?: StyleProperties
    open?: StyleProperties
  } | undefined
  content?: string | undefined  // text, button, link
  /** Semantic HTML tag selected through a typography token. */
  textTag?: TextTag | undefined
  accentContent?: string
  afterContent?: string
  /** AI content slot for template authoring — the AI builder fills nodes by this id (slot-title, slot-list…). */
  aiSlot?: string | undefined
  /** Anchor id — rendered as the element's HTML id so links (#anchor) scroll to it. */
  anchorId?: string | undefined
  /**
   * El nombre de la sección en el menú, cuando el cliente pidió un enlace a
   * ella («Clases», «Membresía»). Sólo esas van al menú automático de un sitio
   * de una página: todas llenaban la barra.
   */
  navLabel?: string | undefined
  src?: string      // image
  /** Zoom inside the image frame; the source file is kept intact. */
  imageCrop?: ImageCrop | undefined
  /** Device-specific crop and overlay; desktop uses the base image fields. */
  imagePresentation?: Partial<Record<'tablet' | 'mobile', ImagePresentation>> | undefined
  /** El velo pintado encima de la foto (sombra, tinte, degradado). Ver `imageVeil.ts`. */
  imageVeil?: ImageVeil | undefined
  /** Una trama encima de la foto o el vídeo (cuadrícula, grano, desenfoque…). Ver `lib/mediaTexture.ts`. */
  mediaTexture?: MediaTexture | undefined
  /**
   * Vídeo propio en lugar de la foto: `src` es la portada y esto el archivo.
   * Sirve igual en una imagen suelta que en la de fondo anclado (vídeo de
   * fondo). Por omisión arranca solo, sin sonido y en bucle; con `controls`
   * se reproduce a mano. Ver `canvas/videoMedia.ts`.
   */
  video?: {
    src: string
    autoplay?: boolean | undefined
    muted?: boolean | undefined
    loop?: boolean | undefined
    controls?: boolean | undefined
    /** La portada sacada del propio vídeo: para volver a ella si se puso otra foto de portada. */
    autoPoster?: string | undefined
  } | undefined
  /**
   * Slider de fondo: la foto anclada (`backgroundLayer`) que va cambiando sola.
   * `src` es la primera foto (recorte, velo, IA y carga temprana siguen igual);
   * `slides` son las siguientes. Ver `lib/backgroundSlider.ts`.
   */
  backgroundSlider?: BackgroundSlider | undefined
  /**
   * De qué bloque de la biblioteca salió esta sección (su id y su categoría).
   * Lo usa «Cambiar diseño» para ofrecer otros de la misma categoría. Ver
   * `lib/sectionRedesign.ts`. Las secciones de antes no lo traen: se deduce.
   */
  sourceTemplate?: { id: string; category: string } | undefined
  /** Bloque incrustado de YouTube o Vimeo: el reproductor sale de este enlace. */
  videoEmbed?: { url: string; title?: string | undefined } | undefined
  /** Foto de fondo de su caja: el dibujo la pega detrás del contenido. Ver `backgroundLayer.ts`. */
  backgroundLayer?: boolean | undefined
  /** Ajustes de fondo inactivos, para volver a imagen o degradado sin rehacerlos. */
  backgroundFillPresets?: Partial<Record<'image' | 'gradient', { styles: ElementStyles; layer?: ElementNode | undefined; autoCover?: ElementNode | undefined }>> | undefined
  /**
   * Widths available beside `src`, as `<base>-<width>.<ext>`.
   *
   * Recorded when the image is chosen rather than derived from the URL: a
   * `srcset` candidate that 404s breaks the image, so the published page may
   * only offer widths it knows were uploaded.
   */
  srcWidths?: number[] | undefined
  /**
   * La foto de una tarjeta de oferta con varias fotos: si cambia al pasar el
   * ratón (`hover`, por defecto), las recorre con él (`scrub`) o se queda en
   * la primera (`single`). Ver `offerPhotos`.
   */
  offerPhotos?: 'single' | 'hover' | 'scrub' | undefined
  /**
   * Turns this container into a real form on the published site.
   *
   * `formId` is generated here, in the editor, and the Worker upserts a row
   * with that same id at publish time. Letting the client choose it means
   * inserting a form needs no network round trip and cannot half-fail — the
   * block works offline and only starts receiving once the site is live.
   */
  /**
   * A qué oferta apunta este botón, cuando vende algo.
   *
   * Va en el nodo y no en un tipo de elemento nuevo porque un botón de compra es
   * un botón: se estiliza igual, se coloca igual y se copia igual. Lo único
   * suyo es a dónde lleva al pulsarlo.
   *
   * `priceId` vacío significa "el precio de por defecto de esa oferta", que es
   * lo que hace que cambiar de precio en el panel no obligue a repasar cada
   * botón del sitio.
   */
  offer?: {
    offerId: string
    priceId?: string | undefined
    /** El nombre de la oferta cuando se enlazó, para poder leerlo en el editor. */
    label?: string | undefined
  } | undefined
  /**
   * Convierte este contenedor en un catálogo: las ofertas del negocio, pintadas
   * como tarjetas.
   *
   * En el nodo y no en un tipo de elemento propio, igual que `offer` y `form`:
   * por fuera es un contenedor de contenido neutro que se coloca dentro de la
   * sección elegida por el usuario; lo único suyo es qué se dibuja dentro.
   *
   * Lo de dentro NO son nodos hijos. Las tarjetas se pintan con los tokens de
   * Marca —el estilo de tarjeta, el de botón, los de texto— por la misma razón
   * que las reseñas y las preguntas son contenido gestionado y no hijos sueltos:
   * el catálogo cambia solo cuando se da de alta una oferta en el panel, y una
   * tarjeta que nadie ha editado no puede quedarse sin foto o con el nombre en
   * otro tamaño. Además es lo que ya hacen las páginas índice del catálogo
   * —`/services` y sus hermanas—, así que un bloque y un índice del mismo sitio
   * se ven igual.
   */
  catalog?: CatalogBinding | undefined
  form?: {
    formId: string
    /**
     * Names the inbox in the dashboard — "Contacto", "Newsletter".
     *
     * Siempre tiene que valer algo: es como se distingue una bandeja de otra, y
     * el servidor rechaza publicar un formulario sin nombre. Para no verlo en la
     * pagina esta `showName`, no vaciarlo — eran dos decisiones atadas al mismo
     * campo, y ocultar el titulo dejaba la bandeja sin nombre.
     */
    name: string
    /** Si el titulo se dibuja en la pagina. Ausente = si. */
    showName?: boolean | undefined
    /** Optional supporting copy shown below the form title. */
    description?: string | undefined
    /** Si la descripcion se dibuja en la pagina. Ausente = si. */
    showDescription?: boolean | undefined
    fields: Array<{
      name: string
      label: string
      type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'checkbox'
      required: boolean
      options?: string[]
    }>
    submitLabel: string
    successMessage: string
    /** Empty means dashboard only; otherwise a comma-separated list of up to five recipients. */
    notificationEmail: string
    /**
     * Mandar además el contacto a la cuenta de Omnisend del proyecto.
     *
     * Ausente = no. Va por formulario y no por proyecto porque un presupuesto y
     * una suscripción a novedades no son la misma intención: meter a quien pide
     * precio en una campaña es lo que acaba con los correos del negocio marcados
     * como spam.
     *
     * La bandeja del panel recibe SIEMPRE, encendido esto o no. Es un destino
     * añadido, igual que los avisos por correo, y nunca un destino alternativo:
     * si fuera excluyente, una clave revocada costaría los contactos.
     */
    omnisend?: {
      enabled: boolean
      /**
       * El `name` de la casilla de opt-in del propio formulario. Vacío = no hay.
       *
       * Lo único que da de alta como suscrito. Sin ella el contacto llega igual
       * pero sin tocar su estado de suscripción — escribirle a una empresa no es
       * pedir su newsletter, y quien ya estuviera suscrito no puede caerse de la
       * lista por usar el formulario de contacto.
       */
      consentField: string
    } | undefined
    /**
     * Las etiquetas del formulario.
     *
     * Del formulario y no de Omnisend, aunque nacieran ahí: ordenan la bandeja
     * del panel —qué es cada mensaje sin abrirlo— y, cuando el envío a Omnisend
     * está activado, viajan además con el contacto para segmentar campañas.
     *
     * Una sola lista y no dos: son dos usos del mismo dato, y dos campos casi
     * iguales en la misma pantalla sólo obligan a explicar en cuál escribir.
     */
    tags?: string[] | undefined
    /**
     * Per-part styling, keyed by role rather than by field.
     *
     * A form is styled by kind — every label alike, every input alike — so the
     * canvas selects a part, not one field's label. Restyling from any label
     * reaches all of them, which is what keeps a form from drifting into a
     * patchwork one edit at a time.
     */
    partStyles?: Partial<Record<FormPart, ElementStyles>> | undefined
    /** Token references per part (colour, typography), same shape as a node's. */
    partRefs?: Partial<Record<FormPart, ElementNode['styleRefs']>> | undefined
  } | undefined
  /* Admite `undefined` explícito: quitar el icono es escribirlo, no omitirlo. */
  iconName?: string | undefined
  /**
   * A qué lado del texto va el icono de un botón.
   *
   * No hay interruptor de encender/apagar: el icono está puesto o no lo está,
   * y eso lo dice `iconName`. Dos controles para una decisión pueden acabar
   * contradiciéndose —icono elegido y apagado a la vez— y entonces hay que
   * decidir cuál gana, que es una pregunta que no debería existir.
   */
  iconPosition?: 'left' | 'right' | undefined
  iconSize?: number | undefined
  /**
   * La calificación: de 0 a `ratingMax`, con medias (4.5). Los iconos son SVG,
   * no texto: la IA no tiene nada que reescribir en ellos.
   */
  ratingValue?: number | undefined
  ratingSize?: number | undefined
  /** Cuántos iconos en total, llenos y vacíos. Por defecto 5. */
  ratingMax?: number | undefined
  /** La forma de cada icono. Por defecto, estrella. */
  ratingIcon?: RatingIcon | undefined
  /** Cómo se ven los vacíos: suaves (el color, tenue), en contorno o sin enseñar. */
  ratingEmpty?: RatingEmpty | undefined
  /** Show the numeric score to the right of the rating icons. */
  ratingShowValue?: boolean | undefined
  avatarCount?: number | undefined
  avatarSize?: number | undefined
  /** Cuánto se encima cada foto sobre la anterior, en % de su tamaño (60 encimadas … −40 separadas). */
  avatarOverlap?: number | undefined
  avatarUrls?: string[]
  href?: string | undefined     // link
  contentMode?: 'html' | undefined
  contentBinding?: ContentBinding | undefined
  cmsRepeat?: CmsRepeat | undefined
  /**
   * Instancia de una sección reutilizable.
   *
   * El nodo que la lleva no guarda contenido: sus hijos se dibujan desde el
   * original que vive en `projectContent.components`. Por eso cambiar el
   * original cambia todas las páginas a la vez — no hay nada que propagar,
   * porque no hay más que una copia de la verdad.
   *
   * Desvincular la convierte en un nodo normal con el contenido dentro: es la
   * salida, y tiene que existir desde el primer día. Una función que ata sin
   * poder desatar se convierte en algo que la gente evita usar.
   */
  componentId?: string | undefined
  ariaLabel?: string | undefined
  alt?: string | undefined
  isDecorative?: boolean | undefined
  visibility?: Partial<Record<Breakpoint, boolean>> | undefined
  /**
   * Aparecer sólo cuando la ficha esté en cierto estado de vigencia.
   *
   * Es lo que deja quitar la dirección de una venta terminada, enseñar un
   * distintivo de «terminada» sólo entonces, o dejar el botón de inscripción
   * hasta que un curso empieza. Vacío o ausente = siempre visible.
   *
   * Se decide en el navegador —ver `@webbuilder/shared-types` (`cmsSchedule`)—, así que NO sirve para
   * guardar un secreto: lo oculto sigue estando en el HTML publicado.
   */
  scheduleVisibility?: ScheduleState[] | undefined
  interaction?: ElementInteraction | undefined
  behavior?: ElementBehavior | undefined
  /** Entrada y efecto al pasar el ratón. Ver `lib/animations`. */
  animation?: ElementAnimation | undefined
  /** La transición entre estados de ESTE elemento; sin ella, la de su estilo global. */
  transition?: StateTransition | undefined
  /** Marks a nav dropdown panel as a full-width mega menu (emits data-nav-mega). */
  navMega?: boolean | undefined
  /**
   * El aspecto del panel de un menú desplegable, sin tocar su contenido: en el
   * mega, cuántas columnas (1–4); y en los dos, si se ven los iconos o fotos y
   * las descripciones de sus tarjetas. Cambiar de mega a lista es `navMega`.
   */
  navMenuColumns?: 1 | 2 | 3 | 4 | undefined
  navMenuIcons?: boolean | undefined
  navMenuDescriptions?: boolean | undefined
  /** El alto de un `spacer` en computadora, en px. La altura de verdad vive en
   *  sus estilos (un `clamp()`, ver `lib/spacer.ts`); esto es lo que enseña y
   *  mueve el deslizador. */
  spacerPx?: number | undefined
  /**
   * La barra o el pie del sitio en automático: su menú y sus columnas se
   * rehacen solos con las páginas, el catálogo, el contacto y las políticas.
   * Ver `lib/autoSiteChrome`.
   */
  autoContent?: boolean | undefined
  /** En la barra automática: el título del menú de colecciones. Vacío = «Colecciones». */
  navCollectionsLabel?: string | undefined
  /** Un menú que arma la barra automática (hoy, el de las colecciones de tienda). */
  autoMenu?: 'catalog' | 'collections' | undefined
  /**
   * El banner del sitio (`schemaId: 'site-banner'`): desde y hasta cuándo se
   * enseña, como fecha y hora locales de `<input type="datetime-local">`.
   * Lo decide el navegador de quien visita (ver `applyBanners` en el runtime),
   * así que una promo que termina el domingo se quita sola sin republicar.
   */
  siteBanner?: {
    showFrom?: string | undefined
    showUntil?: string | undefined
    /** Sólo en estas páginas (sus ids). Vacío o ausente: en todas. */
    onlyPages?: string[] | undefined
  } | undefined
  /**
   * Las redes sociales del negocio (`schemaId: 'social-links'`, 2026-10-29).
   * Las piezas no se guardan: se arman al dibujar desde
   * `projectContent.socials` (ver `lib/socialLinks.ts`), así que una red nueva
   * sale en todas partes. Aquí sólo el aspecto y las que se esconden aquí.
   */
  socialLinks?: SocialLinksSettings | undefined
  /**
   * Una pieza del blog que no es una entrada: los filtros o un bloque de la
   * barra lateral (temas, autores, recientes). Su contenido se arma al dibujar
   * con las entradas; en la plantilla publicada del blog deja una marca que el
   * worker rellena al servir. Ver `blogWidgets.ts` en schemas.
   */
  blogWidget?: BlogWidgetSettings | undefined
  /** Con qué datos se rehízo por última vez, para no repetirlo si no cambian. */
  autoSignature?: string | undefined
  /**
   * La barra o el pie (a mano, no en automático) ya recibió el enlace al blog.
   * Se pone una sola vez: si el dueño lo quita, no vuelve. Ver `withBlogLink`.
   */
  blogLinked?: boolean | undefined
  /** Custom display name shown in the layers panel. */
  label?: string
  /**
   * El nombre por defecto con el que nació, como CLAVE del catálogo
   * («bp.spacer»), y el texto con el que se guardó entonces («Espacio»).
   * Mientras `label` siga siendo ese texto, se enseña la clave traducida al
   * idioma de quien edita; si la persona lo renombra, manda su nombre. Así un
   * bloque insertado en español no se queda en español con el editor en inglés.
   */
  labelKey?: string | undefined
  labelDefault?: string | undefined
  /** Locked from the Layers panel — cannot be selected on the canvas. */
  locked?: boolean
  /**
   * Fijo en su sitio: se elige y se viste como cualquier otro, pero no se
   * mueve, no se borra ni se duplica, y nada se pone por encima de él. Lo
   * llevan los bloques de la plantilla de oferta —la compra y la información
   * del producto—: sin ellos la página no vende. Ver `pinnedBlocks`.
   */
  pinned?: boolean | undefined
  /**
   * El enlace de una tarjeta de contacto que se quedó sin dato al crear el
   * sitio («Agrega tu teléfono»). Su tarjeta se marca «Por completar» en el
   * editor y no sale publicada hasta que se escriba el dato (al editarlo se
   * quita). Ver `contactCards` en schemas.
   */
  contactPending?: boolean | undefined
  /** Qué dato lleva una tarjeta de contacto (`phone`, `email`, `address`…): de ahí sale su enlace. */
  contactSlot?: string | undefined
  /** Saved `display` value before the element was hidden. Used to restore the correct display on un-hide. */
  _savedDisplay?: string
  /** Stable id used by schema tokens after runtime element IDs are regenerated. */
  schemaId?: string
  /** Human-friendly section schema used by the simplified editing panel and AI layer. */
  sectionType?: string
  /** Versioned schema id, e.g. hero@1.0. */
  schemaVersion?: string
  /** Shared visual role. Changing the page theme updates every element using this role. */
  themeRole?: ThemeRole | undefined
  /** Shared style component references used by project-level styles. */
  /**
   * Marks a container as the brand logo: its content is drawn from
   * `theme.brandLogo` instead of from its children, so editing the brand updates
   * every logo already placed. `variant` overrides the automatic light/dark
   * choice for one instance.
   */
  brandLogo?: {
    variant: 'auto' | 'light' | 'dark'
    /** Composition of this placement; omitted follows the site's brand logo. */
    display?: 'both' | 'mark' | 'name'
    /** Automatic contrast applies only to the wordmark, never to the artwork. */
    textContrast?: 'auto' | 'brand'
    /** Removes the icon plate for placements that sit directly on a designed surface. */
    transparentMark?: boolean
    /** Tamaño del dibujo en ESTE lugar, en % del de Marca (100 = igual). */
    markScale?: number
    /** Tamaño del nombre en ESTE lugar, en % del de Marca (100 = igual). */
    nameScale?: number
  } | undefined
  styleRefs?: ElementStyleRefs | undefined
  /** Visual background intent for root sections. `auto` uses the default page background. */
  backgroundRole?: SectionBackgroundRole | undefined
  /**
   * Fondo elegido a mano: con «Alternar fondos» encendido, esta sección se queda
   * con el suyo y las demás se acomodan alrededor. Lo pone elegir un estilo de
   * sección; «Automático» lo quita. Ver `withAutoSectionTones`.
   */
  sectionToneManual?: true | undefined
  /**
   * Only applies to type === 'section'.
   * '100%' (default) → all styles apply directly to the section element (full control).
   * '80rem'          → children are wrapped in a centered 80rem max-width container (legacy).
   * 'NNNpx'          → lo mismo con otra medida: el «Ancho del contenido» de la
   *                    barra, cuando la sección no tiene estilo de sección que lo
   *                    decida (ver `sectionContentWidth`).
   */
  containerWidth?: string
}


export type Page = {
  id: string
  name: string
  slug: string
  /** Si enseña la barra y el pie del sitio. Ver `withSiteLayout`. */
  layout?: PageLayout | undefined
  cmsDynamic?: {
    kind?: 'collection' | 'item'
    collection: string
    slugField: string
    previewItemId?: string
    /** En la plantilla de la página de una oferta (`CMS_OFFERS_SOURCE`): de qué tipo. */
    offerKind?: OfferTemplateKind
    /**
     * En la página de una colección de la tienda: si es el diseño PROPIO de
     * una (su slug). Sin él, el general, con el que se ven las demás.
     */
    forCollection?: string
  } | undefined
  isHome: boolean
  isSlugCustom: boolean
  elements: ElementNode[]
  theme: PageTheme
  pageStyles?: ElementStyles

  // SEO
  seoTitle: string
  seoDescription: string
  canonicalUrl: string

  // Open Graph
  ogTitle: string
  ogDescription: string
  ogImage: string | null

  /**
   * Apagado en Ajustes de página › «Publicar esta página»: la página sigue en
   * el editor pero el sitio contesta un 404 en su dirección (ver
   * `unpublishedStubHtml`). Sin el campo = publicada. La portada no se apaga.
   */
  unpublished?: boolean | undefined

  // Advanced
  accessMode: 'public' | 'password'
  password: string
  indexable: boolean
  includeInSitemap: boolean
  redirect301: string | null
  customHeadCode: string

  // Metadata
  thumbnailUrl: string | null
  createdAt: string
  updatedAt: string
}


export { BUILT_IN_COLOR_COMPONENTS } from '@webbuilder/theme'
/* Viven en `@webbuilder/theme` con el resto de la derivación. */
export { ICON_TOKEN_STYLE_KEYS } from '@webbuilder/theme'
/* Los tipos del tema también viven ahí. Se reexportan para que las decenas de
   archivos que los importan desde el store sigan funcionando: mover el dato no
   tiene por qué mover cada `import` del editor. */
export type {
  BrandLogo, GlobalButtonComponent, GlobalCardComponent, GlobalColorComponent,
  GlobalIconComponent, GlobalSectionComponent, GlobalTypographyComponent, PageTheme, ProjectTheme,
  ThemeRole, ThemeRoleElementProps,
} from '@webbuilder/theme'
export { DEFAULT_BRAND_LOGO }

/* La derivación del tema vive en `@webbuilder/theme`: la comparten el editor y
   el panel. Aquí sólo se reexporta para que nada de lo que ya la importaba
   desde el store tenga que cambiar de sitio. */
export { normalizeProjectTheme, normalizeTheme }


// Legacy migration: older themes stored role backgrounds/borders as hardcoded hex, so
// role-painted elements (navs, cards, sections…) never tracked the project's global colors.
// On load, swap any role color that still equals its old default hex for the matching theme
// var. Only exact-hex matches are touched — a user-customized value is left as-is.


/**
 * Los tipos que sobreviven a una recarga.
 *
 * Lo que no figure aquí vuelve como `text`, y entonces su valor se dibuja como
 * texto crudo: una vigencia guardada salía en la tabla como
 * `{"start":"2026-08-26",…}` en cuanto se recargaba el proyecto. Añadir un tipo
 * nuevo es SIEMPRE dos sitios: el tipo y esta lista. Hay una prueba que lo
 * comprueba, porque olvidarlo no falla al escribirlo — falla al volver.
 */
const CMS_FIELD_TYPES: CmsFieldType[] = ['text', 'title', 'richText', 'code', 'image', 'multiImage', 'video', 'link', 'email', 'phone', 'number', 'date', 'dateRange', 'option']
const LEGACY_FIELD_TYPE: Record<string, CmsFieldType> = { title: 'title', description: 'richText', image: 'image' }
const LEGACY_FIELD_NAME: Record<string, string> = {
  title: 'Título', description: 'Descripción', image: 'Imagen', icon: 'Icono',
  url: 'URL', email: 'Email', phone: 'Teléfono', value: 'Valor', label: 'Etiqueta',
}


/** Nombres que tuvieron los campos de sistema y que ya no se usan. */
const LEGACY_SYSTEM_FIELD_NAMES: Record<string, string[]> = {
  status: ['Status'],
}

/**
 * Los campos de sistema, presentes y con su nombre actual.
 *
 * Lo segundo hace falta porque el nombre viaja GUARDADO en cada colección: al
 * renombrar «Status» a «Estado», los proyectos que ya existían seguían
 * enseñando el viejo en el CMS y en los desplegables del lienzo. Sólo se
 * renombra si nadie lo ha tocado —si el nombre guardado es exactamente uno de
 * los que pusimos nosotros—, para no pisar el que haya elegido el usuario.
 */
function ensureCmsSystemFields(fields: CmsField[]): CmsField[] {
  const next = fields.map((field) => {
    const system = CMS_SYSTEM_FIELDS.find((candidate) => candidate.id === field.id)
    const legacy = LEGACY_SYSTEM_FIELD_NAMES[field.id]
    if (!system || !legacy || !legacy.includes(field.name)) return field
    return { ...field, name: system.name }
  })
  for (const field of CMS_SYSTEM_FIELDS) {
    if (CMS_OPTIONAL_SYSTEM_FIELD_IDS.has(field.id)) continue
    if (!next.some((f) => f.id === field.id)) next.push(field)
  }
  return next
}

function normalizeCmsCollection(raw: unknown, fallbackId: string): CmsCollection {
  const c = (raw && typeof raw === 'object' ? raw : {}) as Partial<CmsCollection>
  /**
   * Los campos que eran «Interruptor», un tipo que ya no existe.
   *
   * El tipo desaparecido cae solo en «Texto» —es lo que hace la comprobación de
   * abajo con cualquier tipo que no reconoce—, pero sus valores están guardados
   * como `'true'`/`'false'` y de golpe se leerían así en la tabla y en la
   * página publicada. Se apuntan aquí para traducirlos al pasar por los datos.
   */
  const wasSwitch = new Set<string>()
  const rawFields: CmsField[] = Array.isArray(c.fields)
    ? c.fields.filter((f): f is CmsField => !!f && typeof f === 'object').map((f, i) => {
      const id = String(f.id ?? `field-${i + 1}`)
      if (String(f.type) === 'switch') wasSwitch.add(id)
      return {
        id,
        name: String(f.name ?? 'Campo'),
        type: CMS_FIELD_TYPES.includes(f.type) ? f.type : 'text',
        ...(Array.isArray(f.options) ? { options: f.options.filter((o): o is string => typeof o === 'string') } : {}),
      }
    })
    : []
  /** `'true'`/`'false'` pasan a las palabras que ya se enseñaban en su sitio. */
  const readValue = (id: string, raw: unknown): string => {
    /* `String()` a secas convertía un objeto en «[object Object]» y lo
       enseñaba tal cual en la ficha. Se enumeran los tipos que SÍ tienen una
       lectura —un escalar, o una lista de escalares—; lo demás no se enseña.
       En blanco y no «[object Object]»: un hueco se ve y se corrige, y aquello
       parecía un valor. */
    const scalar = (item: unknown): string =>
      typeof item === 'string' ? item
      : typeof item === 'number' || typeof item === 'boolean' ? String(item)
      : ''
    const value = Array.isArray(raw)
      ? (raw as unknown[]).map(scalar).filter((item) => item !== '').join(', ')
      : scalar(raw)
    if (!wasSwitch.has(id)) return value
    if (value === 'true') return 'Sí'
    if (value === 'false') return 'No'
    return value
  }
  const fields = ensureCmsSystemFields(rawFields.length > 0 ? rawFields : [{ id: 'title', name: 'Título', type: 'title' }])
  const items: CmsItem[] = Array.isArray(c.items)
    ? c.items.filter((it): it is CmsItem => !!it && typeof it === 'object').map((it, i) => {
      const rawValues = (it.values && typeof it.values === 'object') ? it.values as Record<string, unknown> : {}
      const values: Record<string, string> = {}
      for (const field of fields) values[field.id] = readValue(field.id, rawValues[field.id])
      if (!values.created) values.created = new Date().toISOString().slice(0, 10)
      // Carry bookkeeping timestamps (not fields) so the Created/Modified columns survive a reload.
      if (typeof rawValues.createdAt === 'string') values.createdAt = rawValues.createdAt
      if (typeof rawValues.updatedAt === 'string') values.updatedAt = rawValues.updatedAt
      // Preserve the published snapshot so the publish / pending-changes / draft state survives a
      // reload from the backend (it round-trips inside projects.data jsonb). Normalize it over the
      // same fields as `values` so a clean published item compares equal (and shows as "Publicado").
      const rawPublished = it.publishedValues && typeof it.publishedValues === 'object'
        ? it.publishedValues as Record<string, unknown>
        : null
      let publishedValues: Record<string, string> | null = null
      if (rawPublished) {
        publishedValues = {}
        for (const field of fields) publishedValues[field.id] = readValue(field.id, rawPublished[field.id])
      } else if (values.status === 'Publicado' || values.status === 'Público') {
        // Legacy items saved before the 3-state model were "published" with no snapshot: seed one
        // from their current values so they stay live on the published site after this migration.
        publishedValues = { ...values }
      }
      if (!values.status) values.status = 'Borrador'
      return { id: String(it.id ?? `item-${i + 1}`), values, publishedValues }
    })
    : []
  let hiddenFields = Array.isArray(c.hiddenFields) ? c.hiddenFields.filter((id): id is string => typeof id === 'string') : undefined
  /* El cuerpo, fuera de la tabla salvo que ya estuviera. Es una columna de
     párrafos: en la rejilla no cabe y no dice nada que su ficha no diga mejor.
     Quien ya tenía el campo se queda como estaba —su columna es suya—, y el que
     lo quiera ver puede sacarlo desde el menú de columnas. */
  if (!rawFields.some((field) => field?.id === 'content') && !(hiddenFields ?? []).includes('content')) {
    hiddenFields = [...(hiddenFields ?? []), 'content']
  }
  /* El orden de las columnas sobrevive a la recarga.
     Se quedaba fuera de este objeto, que es el que reconstruye cada colección
     al leer el proyecto: arrastrar una columna la movía en el momento —el
     estado sí lo guardaba— y al volver a normalizar el contenido reaparecía
     donde estaba. Las columnas que son campo disimulaban el fallo, porque al
     reordenar se reordenan también sus campos; la miniatura, el estado y las
     dos fechas no son campos de nadie y volvían siempre a su sitio de fábrica.
     Se filtra a cadenas por lo mismo que `hiddenFields`: viene de un JSON. */
  const columnOrder = Array.isArray(c.columnOrder)
    ? c.columnOrder.filter((id): id is string => typeof id === 'string')
    : undefined
  /* La ventana de vigencia se reconstruye desde cero como todo lo demás: sin
     nombrarla aquí, declararla funcionaría hasta la primera recarga y luego las
     listas volverían a enseñarlo todo sin que nadie supiera por qué. Sólo vale
     si apunta a un campo que existe. */
  const rawSchedule = c.schedule as { startField?: unknown; endField?: unknown } | undefined
  const fieldIds = new Set(fields.map((f) => f.id))
  const startField = typeof rawSchedule?.startField === 'string' && fieldIds.has(rawSchedule.startField)
    ? rawSchedule.startField
    : null
  const endField = typeof rawSchedule?.endField === 'string' && fieldIds.has(rawSchedule.endField)
    ? rawSchedule.endField
    : null
  const schedule = startField
    ? { startField, ...(endField ? { endField } : {}) }
    : null

  return { id: String(c.id ?? fallbackId), name: String(c.name ?? 'Colección'), ...(c.description ? { description: String(c.description) } : {}), ...(c.image ? { image: String(c.image) } : {}), ...(c.icon ? { icon: String(c.icon) } : {}), ...(c.color ? { color: String(c.color) } : {}), ...(hiddenFields && hiddenFields.length ? { hiddenFields } : {}), ...(columnOrder && columnOrder.length ? { columnOrder } : {}), ...(schedule ? { schedule } : {}), ...(c.kind === 'menu' ? { kind: 'menu' as const } : {}), fields, items }
}

/** Migrate a legacy fixed list (services/people/…) into a dynamic collection, preserving ids so existing bindings keep resolving. */
function migrateLegacyCollection(id: string, name: string, rawItems: unknown): CmsCollection | null {
  if (!Array.isArray(rawItems) || rawItems.length === 0) return null
  const keys: string[] = []
  for (const it of rawItems) {
    if (it && typeof it === 'object') {
      for (const k of Object.keys(it as object)) if (k !== 'id' && !keys.includes(k)) keys.push(k)
    }
  }
  if (keys.length === 0) keys.push('title')
  const fields: CmsField[] = ensureCmsSystemFields(keys.map((k) => ({ id: k, name: LEGACY_FIELD_NAME[k] ?? k, type: LEGACY_FIELD_TYPE[k] ?? 'text' })))
  const toStr = (v: unknown): string => {
    if (typeof v === 'string') return v
    if (typeof v === 'number' || typeof v === 'boolean') return String(v)
    return ''
  }
  const items: CmsItem[] = rawItems.map((it, i) => {
    const obj = (it && typeof it === 'object' ? it : {}) as Record<string, unknown>
    const values: Record<string, string> = {}
    for (const field of fields) values[field.id] = toStr(obj[field.id])
    if (!values.status) values.status = 'Público'
    if (!values.created) values.created = new Date().toISOString().slice(0, 10)
    // Legacy fixed lists were always live: seed a published snapshot so they stay online.
    const publishedValues = values.status === 'Publicado' || values.status === 'Público' ? { ...values } : null
    return { id: typeof obj.id === 'string' ? obj.id : `${id}-${i + 1}`, values, publishedValues }
  })
  return { id, name, fields, items }
}

export function normalizeProjectContent(raw: unknown): ProjectContent {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Partial<ProjectContent> & Record<string, unknown>
  const normalizeItems = (items: unknown, fallback: ProjectContentItem[]) => (
    Array.isArray(items)
      ? items.map((item, index) => ({
        id: typeof item === 'object' && item && 'id' in item ? String((item as ProjectContentItem).id) : `item-${index + 1}`,
        ...(item && typeof item === 'object' ? item as Omit<ProjectContentItem, 'id'> : {}),
      }))
      : fallback
  )

  let collections: CmsCollection[]
  if (Array.isArray(input.collections)) {
    collections = input.collections.map((c, i) => normalizeCmsCollection(c, `collection-${i + 1}`))
  } else {
    collections = [
      migrateLegacyCollection('services', 'Servicios', input.services),
      migrateLegacyCollection('people', 'Personas', input.people),
      migrateLegacyCollection('testimonials', 'Testimonios', input.testimonials),
      migrateLegacyCollection('forms', 'Forms', input.forms),
    ].filter((c): c is CmsCollection => c !== null)
    // No auto-seeding: a project with no collections (and no legacy CMS fields)
    // starts with an empty list. Collections are created only when the user asks.
  }

  const normalizedCollections = collections.map(withCmsSlugs).map((collection) =>
    shouldReplaceGeneratedCmsItems(collection)
      ? withCmsSlugs({ ...collection, items: makeDefaultCmsItems(collection.name) })
      : collection
  )

  return {
    business: { ...DEFAULT_PROJECT_CONTENT.business, ...(input.business ?? {}) },
    navigation: {
      ...DEFAULT_PROJECT_CONTENT.navigation,
      ...(input.navigation ?? {}),
      items: normalizeItems(input.navigation?.items, DEFAULT_PROJECT_CONTENT.navigation.items),
    },
    collections: normalizedCollections,
    contact: { ...DEFAULT_PROJECT_CONTENT.contact, ...(input.contact ?? {}) },
    /* El botón de contacto flotante: tal cual, si lo hay (ver `ProjectContent`). */
    ...(input.contactFloat && typeof input.contactFloat === 'object' ? { contactFloat: input.contactFloat } : {}),
    /*
     * Las secciones reutilizables, tal cual se guardaron.
     *
     * Esto no es opcional en el sentido de «bonito tenerlo»: todo lo que no se
     * nombre aquí se pierde al abrir el proyecto, porque esta función construye
     * el contenido DESDE CERO. Sin esta línea, convertir una sección en
     * reutilizable funcionaría hasta la primera recarga y luego cada instancia
     * se quedaría sin original.
     *
     * Se filtra lo que no tenga forma de componente en vez de confiar: un
     * proyecto puede venir de una copia de seguridad antigua o de una versión
     * anterior del editor.
     */
    /* Las redes, sólo las que tienen nombre y dirección: sin esta línea se
       perdían al abrir el proyecto, igual que las secciones reutilizables. */
    ...(Array.isArray(input.socials)
      ? {
        socials: (input.socials as unknown[]).filter((item): item is ProjectSocial => (
          Boolean(item)
          && typeof item === 'object'
          && typeof (item as ProjectSocial).label === 'string'
          && typeof (item as ProjectSocial).url === 'string'
          && Boolean((item as ProjectSocial).url.trim())
        )),
      }
      : {}),
    ...(Array.isArray(input.components)
      ? {
        components: (input.components as unknown[]).filter((item): item is ProjectComponent => (
          Boolean(item)
          && typeof item === 'object'
          && typeof (item as ProjectComponent).id === 'string'
          && Boolean((item as ProjectComponent).node)
        )).map((component) => (component.slot === 'banner' ? { ...component, node: withCloseInRow(withWhatsappGlyph(component.node)) } : component)),
      }
      : {}),
  }
}

/**
 * El teléfono del banner que lleva a WhatsApp, con el logo de WhatsApp.
 *
 * Antes de que el catálogo tuviera ese icono salía con el globo de mensaje, y
 * el panel ya lo marca como «WhatsApp» por su enlace (`wa.me`): no quedaba
 * nada que pulsar para que cambiara. Devuelve el mismo nodo si no hay nada que
 * cambiar.
 */
function withWhatsappGlyph(node: ElementNode): ElementNode {
  const href = node.interaction?.href ?? node.href ?? ''
  const own = node.schemaId === 'banner-phone' && node.iconName === 'message' && href.includes('wa.me')
  const children = node.children?.map(withWhatsappGlyph) ?? []
  const childrenChanged = children.some((child, index) => child !== node.children[index])
  if (!own && !childrenChanged) return node
  return { ...node, ...(own ? { iconName: 'whatsapp' } : {}), children }
}

/**
 * La ✕ del banner, dentro de la fila y al final.
 *
 * Iba colgada de la franja y colocada contra su borde derecho, lejos de lo
 * demás en una pantalla ancha. Ahora es la última pieza de la fila, justo a
 * la derecha de lo último que haya. Un banner guardado así pasa a la nueva
 * colocación, sin la posición que la llevaba al borde.
 */
function withCloseInRow(banner: ElementNode): ElementNode {
  const close = banner.children.find((child) => child.schemaId === 'banner-close')
  const row = banner.children.find((child) => child.schemaId === 'banner-content')
  if (!close || !row) return banner
  const { position: _position, top: _top, right: _right, transform: _transform, ...desktop } = close.styles.desktop ?? {}
  const { right: _mobileRight, ...mobile } = close.styles.mobile ?? {}
  const { mobile: _oldMobile, ...otherStyles } = close.styles
  const moved: ElementNode = {
    ...close,
    styles: { ...otherStyles, desktop, ...(Object.keys(mobile).length ? { mobile } : {}) },
  }
  return {
    ...banner,
    children: banner.children
      .filter((child) => child !== close)
      .map((child) => (child === row ? { ...row, children: [...row.children, moved] } : child)),
  }
}

const NAV_BLOCK_PADDING_ZERO: Partial<StyleProperties> = {
  padding: '0px',
  paddingTop: '0px',
  paddingRight: '0px',
  paddingBottom: '0px',
  paddingLeft: '0px',
}

const FLUSH_FOOTER_CONTAINER_SCHEMA_IDS = new Set([
  'footer-brand',
  'footer-navigation',
  'footer-services',
  'footer-contact',
  'footer-legal',
])

const PADDING_STYLE_KEYS: Array<keyof StyleProperties> = [
  'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
]

/**
 * Los cuatro primeros catálogos se guardaban como secciones con este aire.
 * Al convertirlos en contenido, se quitan únicamente esos valores de fábrica:
 * si alguien cambió uno deliberadamente, se conserva.
 */
function withoutLegacyCatalogSectionPadding(styles: ElementStyles): ElementStyles {
  const next = Object.fromEntries(Object.entries(styles).map(([breakpoint, values]) => [
    breakpoint,
    { ...(values ?? {}) },
  ])) as ElementStyles
  const desktop = next.desktop
  const mobile = next.mobile
  if (desktop?.paddingTop === '48px') delete desktop.paddingTop
  if (desktop?.paddingRight === '24px') delete desktop.paddingRight
  if (desktop?.paddingBottom === '48px') delete desktop.paddingBottom
  if (desktop?.paddingLeft === '24px') delete desktop.paddingLeft
  if (mobile?.paddingRight === '18px') delete mobile.paddingRight
  if (mobile?.paddingLeft === '18px') delete mobile.paddingLeft
  return next
}

/** El nombre de capa de cada dato de una oferta, por campo. Ver `CMS_OFFER_FIELDS`. */
const OFFER_FIELD_LABEL_KEYS: Record<string, string> = {
  title: 'bp.offerTitle',
  description: 'bp.offerDescription',
  image: 'bp.offerImage',
  price: 'bp.offerPrice',
  duration: 'bp.offerDuration',
  cta: 'bp.offerButton',
  more: 'bp.offerMoreButton',
}

/*
 * Los nombres que las piezas de una oferta traían puestos antes de tener los
 * suyos: los del CMS —«CMS Title»— y los de la primera tarjeta de ofertas, en
 * los dos idiomas porque se guardaron ya traducidos. Un nombre que no esté aquí
 * lo puso la persona y se respeta.
 */
const LEGACY_OFFER_LABELS = new Set([
  'Offer name', 'Nombre de la oferta', 'Offer photo', 'Foto de la oferta',
  'Duration', 'Duración', 'Price', 'Precio', 'Button', 'Botón',
])

/** El nombre de oferta que le toca a una pieza cuyo nombre es uno heredado, si le toca alguno. */
function legacyOfferLabel(node: ElementNode): string | null {
  const key = OFFER_FIELD_LABEL_KEYS[node.contentBinding?.field ?? '']
  const label = node.label?.trim() ?? ''
  if (!key || !(label.startsWith('CMS') || LEGACY_OFFER_LABELS.has(label))) return null
  return translate(key)
}

/*
 * Los formularios insertados mientras la inserción no traducía su
 * configuración se guardaron con las claves del catálogo —«sample.name»,
 * «sample.send»— y así se veían en la página. Se traduce sólo lo que es una
 * clave de ejemplo conocida; un texto escrito a mano no empieza por `sample.`
 * y, si lo hiciera, `translate` lo devolvería igual.
 */
function sampleText(value: string): string {
  return value.startsWith('sample.') ? translate(value) : value
}

function withTranslatedSampleForm(form: NonNullable<ElementNode['form']>): NonNullable<ElementNode['form']> | null {
  const next = {
    ...form,
    name: sampleText(form.name),
    ...(form.description ? { description: sampleText(form.description) } : {}),
    /* Un formulario sin identificador no se puede publicar: los templates lo
       guardan vacío como marcador y una vía de inserción que no lo renovaba lo
       dejaba así —la página apuntaba a `/forms//submissions` y respondía «Not
       found», y al publicar el formulario ni se registraba—. Al cargar se le da
       uno. Sólo al que NO lo tiene: uno con id ya tiene una bandeja. */
    formId: form.formId || crypto.randomUUID(),
    fields: form.fields.map((field) => ({ ...field, label: sampleText(field.label) })),
    submitLabel: sampleText(form.submitLabel),
    successMessage: sampleText(form.successMessage),
  }
  return JSON.stringify(next) === JSON.stringify(form) ? null : next
}

/*
 * Las piezas de una cuenta atrás insertada antes de tener nombre propio se
 * llamaban con la clave pegada a un sufijo en español —«sample.unitSec valor»—,
 * que ya no era una clave y así se quedaba en Capas y en el lienzo. Por su
 * `schemaId` se sabe qué pieza es; el nombre sólo se cambia si es uno de esos.
 */
function legacyCountdownLabel(node: ElementNode): string | null {
  const match = /^countdown-(days|hours|minutes|seconds)-(value|label)$/.exec(node.schemaId ?? '')
  if (!match || !/ (valor|label)$/.test(node.label ?? '')) return null
  return translate(`bp.cd.${match[1]}.${match[2]}`)
}

function normalizeElementTree(rawNodes: unknown, insideCms = false, insideNav = false, insideOffers = false): ElementNode[] {
  if (!Array.isArray(rawNodes)) return []
  return rawNodes.filter((raw) => {
    const node = raw as ElementNode
    // Older automatic category fallbacks displayed the publication status.
    return !(insideCms && ['cms.categories', 'cms.description'].includes(node?.aiSlot ?? '') && node?.contentBinding?.field === 'status')
  }).map((raw) => {
    /* Un nodo sin \`styles\` —escrito a mano por un script o por el worker—
       tumbaba el normalizado entero, y con él la apertura del proyecto: todo lo
       de abajo da por hecho que están. Se le ponen vacíos. */
    const node = normalizeCmsDetailAction((raw && typeof raw === 'object' && !(raw as ElementNode).styles
      ? { ...(raw as ElementNode), styles: { desktop: {} } }
      : raw) as ElementNode)
    const desktopStyles = node.styles?.desktop ?? {}
    const isMediaArrow = node.schemaId === 'wb-slider-prev'
      || node.schemaId === 'wb-slider-next'
      || node.schemaId === 'wb-carousel-prev'
      || node.schemaId === 'wb-carousel-next'
    const isLegacyMediaArrow = isMediaArrow
      && desktopStyles.width === '32px'
      && desktopStyles.height === '32px'
      && desktopStyles.background === 'rgba(15, 23, 42, 0.32)'
      && desktopStyles.color === '#ffffff'
      && desktopStyles.borderColor === 'rgba(255, 255, 255, 0.16)'
    const isLegacySliderDots = node.schemaId === 'wb-slider-dots'
      && desktopStyles.background === 'rgba(15, 23, 42, 0.3)'
      && desktopStyles.paddingTop === '5px'
      && desktopStyles.paddingRight === '7px'
    const migratedControlStyles: ElementStyles = (() => {
      if (isLegacyMediaArrow) {
        const kept = { ...desktopStyles }
        delete kept.background
        delete kept.color
        delete kept.borderColor
        return {
          ...node.styles,
          desktop: {
            ...kept,
            width: '40px',
            height: '40px',
            ...(node.schemaId === 'wb-slider-prev' ? { left: '16px' } : {}),
            ...(node.schemaId === 'wb-slider-next' ? { right: '16px' } : {}),
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          },
        }
      }
      if (isLegacySliderDots) {
        const kept = { ...desktopStyles }
        delete kept.background
        return {
          ...node.styles,
          desktop: {
            ...kept,
            bottom: '16px',
            gap: '6px',
            paddingTop: '7px',
            paddingRight: '9px',
            paddingBottom: '7px',
            paddingLeft: '9px',
            borderWidth: '1px',
            borderStyle: 'solid',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
          },
        }
      }
      return node.styles
    })()
    const migratedControlRefs: ElementStyleRefs | undefined = isLegacyMediaArrow
      ? {
          ...(node.styleRefs ?? {}),
          colors: {
            ...(node.styleRefs?.colors ?? {}),
            background: 'background',
            color: 'text',
            borderColor: 'border',
          },
        }
      : isLegacySliderDots
        ? {
            ...(node.styleRefs ?? {}),
            colors: {
              ...(node.styleRefs?.colors ?? {}),
              background: 'background',
              borderColor: 'border',
            },
          }
        : node.styleRefs
    const inCmsContext = insideCms || Boolean(node.cmsRepeat) || Boolean(node.contentBinding) || node.label?.startsWith('CMS') === true
    const inNavContext = insideNav || node.behavior?.type === 'nav'
    const inOffersContext = insideOffers || node.cmsRepeat?.collection === CMS_OFFERS_SOURCE
    const offerLabel = insideOffers ? legacyOfferLabel(node) : null
    const sampleForm = node.form ? withTranslatedSampleForm(node.form) : null
    const countdownLabel = legacyCountdownLabel(node)
    const isLegacyNavLogo = node.schemaId === 'nav-logo' && !node.brandLogo
    if (isLegacyNavLogo) {
      return {
        ...node,
        type: 'div',
        label: 'Logo',
        brandLogo: { variant: 'auto' },
        themeRole: undefined,
        styleRefs: undefined,
        content: undefined,
        children: [],
        styles: {
          desktop: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            flex: '0 0 auto',
            ...NAV_BLOCK_PADDING_ZERO,
          },
          tablet: { ...NAV_BLOCK_PADDING_ZERO },
          mobile: { ...NAV_BLOCK_PADDING_ZERO },
        },
      }
    }
    const isCmsCard = inCmsContext && node.themeRole === 'card'
    const baseStyleRefs = node.themeRole === 'card' && !migratedControlRefs?.card
      ? { ...(migratedControlRefs ?? {}), card: 'card-default' }
      : node.type === 'icon' && node.themeRole === 'icon' && !migratedControlRefs?.icon
        ? { ...(migratedControlRefs ?? {}), icon: 'icon-primary' }
        : migratedControlRefs
    const styleRefs = isCmsCard
      ? (() => {
          const next: ElementStyleRefs = { ...(baseStyleRefs ?? {}), card: baseStyleRefs?.card ?? 'card-default' }
          const colors = { ...(next.colors ?? {}) }
          delete colors.background
          delete colors.backgroundColor
          delete colors.borderColor
          if (Object.keys(colors).length > 0) next.colors = colors
          else delete next.colors
          const overrides = (next.overrides ?? []).filter((key) => !CARD_CONTAINER_KEY_SET.has(key))
          if (overrides.length > 0) next.overrides = overrides
          else delete next.overrides
          return next
        })()
      : inCmsContext && baseStyleRefs?.typography
        ? (() => {
            const next: ElementStyleRefs = { ...baseStyleRefs }
            const colors = { ...(next.colors ?? {}) }
            // Typography tokens own their text color. Keep non-text fills such
            // as a CMS badge background, but remove the duplicate color link.
            delete colors.color
            if (Object.keys(colors).length > 0) next.colors = colors
            else delete next.colors
            return next
          })()
      : baseStyleRefs
    const styles = isCmsCard
      ? Object.fromEntries(Object.entries(node.styles).map(([breakpoint, values]) => [
          breakpoint,
          Object.fromEntries(Object.entries(values ?? {}).filter(([key]) => !CARD_CONTAINER_KEY_SET.has(key))),
        ])) as ElementStyles
      : migratedControlStyles
    const desktopLogoStyles = styles.desktop
    const hasLegacyBrandLogoBounds = Boolean(node.brandLogo)
      && desktopLogoStyles?.height === '36px'
      && desktopLogoStyles.maxHeight === '36px'
      && desktopLogoStyles.width === 'auto'
      && desktopLogoStyles.maxWidth === '200px'
      && desktopLogoStyles.overflow === 'hidden'
    const normalizedStyles = hasLegacyBrandLogoBounds
      ? {
          ...styles,
          desktop: Object.fromEntries(Object.entries(desktopLogoStyles ?? {}).filter(([key]) => (
            !['height', 'maxHeight', 'width', 'maxWidth', 'overflow'].includes(key)
          ))),
        }
      : styles
    const isFooterLogo = node.schemaId === 'footer-logo' && Boolean(node.brandLogo)
    const footerAdjustedStyles = isFooterLogo
      ? {
          ...normalizedStyles,
          desktop: {
            ...(normalizedStyles.desktop ?? {}),
            padding: '0px',
            paddingTop: '0px',
            paddingRight: '0px',
            paddingBottom: '0px',
            paddingLeft: '0px',
            background: 'transparent',
            backgroundColor: 'transparent',
          },
        }
      : normalizedStyles
    const isFlushFooterContainer = Boolean(node.schemaId && FLUSH_FOOTER_CONTAINER_SCHEMA_IDS.has(node.schemaId))
    const footerPaddingValues = Object.values(footerAdjustedStyles).flatMap((breakpointStyles) => (
      PADDING_STYLE_KEYS.map((key) => breakpointStyles?.[key]).filter((value) => value !== undefined)
    ))
    const hasOnlyUnsetOrZeroFooterPadding = footerPaddingValues.every((value) => value === '0' || value === '0px')
    const hasLegacyLegalPadding = node.schemaId === 'footer-legal'
      && (footerAdjustedStyles.desktop?.paddingTop === '12px' || footerAdjustedStyles.desktop?.paddingBottom === '12px')
      && footerPaddingValues.every((value) => value === '0' || value === '0px' || value === '12px')
    const footerContainerAdjustedStyles = isFlushFooterContainer && (hasOnlyUnsetOrZeroFooterPadding || hasLegacyLegalPadding)
      ? {
          ...footerAdjustedStyles,
          desktop: { ...(footerAdjustedStyles.desktop ?? {}), ...NAV_BLOCK_PADDING_ZERO },
          tablet: { ...(footerAdjustedStyles.tablet ?? {}), ...NAV_BLOCK_PADDING_ZERO },
          mobile: { ...(footerAdjustedStyles.mobile ?? {}), ...NAV_BLOCK_PADDING_ZERO },
        }
      : footerAdjustedStyles
    const isInternalNavBlock = insideNav && node.type === 'div'
    const navAdjustedStyles = isInternalNavBlock
      ? {
          ...footerContainerAdjustedStyles,
          desktop: { ...(footerContainerAdjustedStyles.desktop ?? {}), ...NAV_BLOCK_PADDING_ZERO },
          tablet: { ...(footerContainerAdjustedStyles.tablet ?? {}), ...NAV_BLOCK_PADDING_ZERO },
          mobile: { ...(footerContainerAdjustedStyles.mobile ?? {}), ...NAV_BLOCK_PADDING_ZERO },
        }
      : footerContainerAdjustedStyles
    const isFooterDivider = node.schemaId === 'footer-divider'
    const finalStyles = isFooterDivider
      ? {
          ...navAdjustedStyles,
          desktop: { ...(navAdjustedStyles.desktop ?? {}), opacity: '0.1' },
        }
      : navAdjustedStyles
    const finalStyleRefs = isFooterDivider
      ? {
          ...(styleRefs ?? {}),
          colors: { ...(styleRefs?.colors ?? {}), background: 'primary' as const },
        }
      : styleRefs
    const isLegacyFooterCopyright = node.schemaId === 'footer-copyright'
      && node.themeRole === 'paragraph'
      && finalStyleRefs?.typography === 'p'
    const copyrightStyles = isLegacyFooterCopyright && finalStyles.desktop?.fontSize === '13px'
      ? {
          ...finalStyles,
          desktop: Object.fromEntries(Object.entries(finalStyles.desktop).filter(([key]) => key !== 'fontSize')),
        }
      : finalStyles
    const copyrightStyleRefs = isLegacyFooterCopyright
      ? { ...(finalStyleRefs ?? {}), typography: 'small' }
      : finalStyleRefs
    const isLegacyCatalogSection = Boolean(node.catalog) && node.type === 'section'
    const catalogStyles = isLegacyCatalogSection
      ? withoutLegacyCatalogSectionPadding(copyrightStyles)
      : copyrightStyles
    const catalogStyleRefs = isLegacyCatalogSection
      ? (() => {
          if (!copyrightStyleRefs) return undefined
          const next: ElementStyleRefs = { ...copyrightStyleRefs }
          const colors = { ...(next.colors ?? {}) }
          if (colors.background === 'background') delete colors.background
          if (colors.color === 'text') delete colors.color
          if (Object.keys(colors).length > 0) next.colors = colors
          else delete next.colors
          return Object.keys(next).length > 0 ? next : undefined
        })()
      : copyrightStyleRefs
    const catalogBaseNode: ElementNode = { ...node }
    if (isLegacyCatalogSection) {
      delete catalogBaseNode.backgroundRole
      delete catalogBaseNode.containerWidth
      delete catalogBaseNode.styleRefs
    }
    return {
      ...catalogBaseNode,
      ...(isLegacyCatalogSection ? {
        type: 'div' as const,
        ...(catalogStyleRefs ? { styleRefs: catalogStyleRefs } : {}),
      } : catalogStyleRefs ? { styleRefs: catalogStyleRefs } : {}),
      ...(isLegacyMediaArrow ? { iconSize: 18 } : {}),
      ...(isFooterLogo ? { brandLogo: { ...node.brandLogo!, transparentMark: false } } : {}),
      ...(offerLabel ? { label: offerLabel } : {}),
      ...(sampleForm ? { form: sampleForm } : {}),
      ...(countdownLabel ? { label: countdownLabel } : {}),
      ...(node.schemaId === 'footer-copyright' ? { content: withCopyrightYear(node.content) } : {}),
      styles: catalogStyles,
      children: normalizeElementTree(node.children, inCmsContext, inNavContext, inOffersContext),
    }
  })
}

/**
 * El año automático en el aviso de derechos del pie que no lo tenía.
 *
 * Los pies de fábrica nacían con «© Tu empresa. Todos los derechos
 * reservados.», sin año. Se le pone `{year}` —que se pinta como el año en
 * curso, ver `dynamicYear`— detrás del «©», sólo si no trae ya un año: uno
 * escrito a mano («© 2019 Mi negocio») es una decisión y se respeta.
 */
export function withCopyrightYear(content: string | undefined): string | undefined {
  if (!content) return content
  if (content.includes('{year}') || /\b(19|20)\d{2}\b/.test(content)) return content
  const match = /^(\s*©\s*)/.exec(content)
  return match ? `${match[1]}{year} ${content.slice(match[1]!.length)}` : content
}

function uniqueCmsSlug(value: string, used: Set<string>, fallback: string) {
  const base = generateSlug(value) || generateSlug(fallback) || 'item'
  let slug = base.replace(/^\//, '')
  let i = 2
  while (used.has(slug)) {
    slug = `${base}-${i}`
    i += 1
  }
  used.add(slug)
  return slug
}

function withCmsSlugs(collection: CmsCollection): CmsCollection {
  const hasSlugField = collection.fields.some((field) => field.id === 'slug')
  const fields = hasSlugField
    ? collection.fields
    : [...collection.fields, { id: 'slug', name: 'Slug', type: 'text' as const }]
  const used = new Set<string>()
  let changed = !hasSlugField
  const titleField = fields.find((field) => field.type === 'title') ?? fields.find((field) => field.id === 'title')
  const items = collection.items.map((item, index) => {
    const current = item.values.slug?.trim()
    const slug = current && !used.has(current)
      ? (used.add(current), current)
      : uniqueCmsSlug(titleField ? item.values[titleField.id] ?? '' : '', used, `item-${index + 1}`)
    if (slug === item.values.slug && fields.every((field) => item.values[field.id] != null)) return item
    changed = true
    const values: Record<string, string> = { ...item.values, slug }
    for (const field of fields) if (values[field.id] == null) values[field.id] = ''
    return { ...item, values }
  })
  return changed ? { ...collection, fields, items } : collection
}

function cmsFieldBinding(field: string) {
  return { source: 'projectContent' as const, collection: '', field }
}

/** Binds to collection-level singleton content (name/description) — resolved by CanvasRenderer. */
function cmsCollectionMetaBinding(collectionId: string, field: '__name' | '__description') {
  return { source: 'projectContent' as const, collection: collectionId, field }
}

function patchCmsItemLinkPageSlug(nodes: ElementNode[], pageSlug: string): ElementNode[] {
  return nodes.map((node) => ({
    ...node,
    interaction: node.interaction?.cmsLink
      ? { ...node.interaction, cmsLink: { ...node.interaction.cmsLink, pageSlug } }
      : node.interaction,
    children: node.children.length ? patchCmsItemLinkPageSlug(node.children, pageSlug) : node.children,
  }))
}

/**
 * La LISTA del blog con la que se empieza: el nombre del blog y una rejilla de
 * tarjetas —portada, fecha y categorías, título, extracto y «Leer más»— que
 * repite las entradas. Se diseña como cualquier página; al publicar, el worker
 * repite la tarjeta con las entradas de cada momento (ver `BLOG_ITEM_START`).
 */
export function makeBlogIndexElements(): ElementNode[] {
  const text = (label: string, field: string, role: ElementNode['themeRole'], typography: string, color?: string, desktop: Record<string, string> = {}): ElementNode => ({
    id: crypto.randomUUID(),
    type: 'text',
    label,
    themeRole: role,
    styleRefs: { typography, ...(color ? { colors: { color } } : {}) },
    contentBinding: cmsFieldBinding(field),
    styles: { desktop },
    content: '',
    children: [],
  })
  return [{
    id: crypto.randomUUID(),
    type: 'section',
    label: 'Blog',
    /* Fija, como la del catálogo en su página: `/blog` existe para enseñar las
       entradas. Se viste y se le ponen secciones debajo, pero no se borra, no
       se mueve y nada va encima. Ver `pinned`. */
    pinned: true,
    backgroundRole: 'background',
    themeRole: 'section',
    styleRefs: { section: 'section-default' },
    styles: {
      desktop: { display: 'flex', flexDirection: 'column', gap: '40px', width: '100%', paddingTop: '80px', paddingRight: '24px', paddingBottom: '96px', paddingLeft: '24px' },
      mobile: { gap: '28px', paddingTop: '48px', paddingRight: '18px', paddingBottom: '64px', paddingLeft: '18px' },
    },
    children: [
      {
        id: crypto.randomUUID(),
        type: 'text',
        label: 'Blog title',
        textTag: 'h1',
        themeRole: 'heading1',
        styleRefs: { typography: 'h2' },
        contentBinding: cmsCollectionMetaBinding(CMS_BLOG_SOURCE, '__name'),
        styles: { desktop: {} },
        content: 'Blog',
        children: [],
      },
      {
        id: crypto.randomUUID(),
        type: 'div',
        label: 'Posts',
        pinned: true,
        /* Doce por página, con «‹ 1 2 3 ›» debajo. */
        cmsRepeat: { source: 'projectContent', collection: CMS_BLOG_SOURCE, perPage: 12 },
        styles: {
          desktop: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '40px 28px', width: '100%' },
          tablet: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
          mobile: { gridTemplateColumns: '1fr' },
        },
        children: [{
          id: crypto.randomUUID(),
          type: 'div',
          label: 'Post card',
          styles: { desktop: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '0' } },
          children: [
            {
              id: crypto.randomUUID(),
              type: 'image',
              label: 'Post cover',
              themeRole: 'image',
              styleRefs: { colors: { background: 'surface' } },
              contentBinding: cmsFieldBinding('image'),
              styles: {
                desktop: { width: '100%', height: '220px', objectFit: 'cover', display: 'block', borderRadius: '16px' },
                mobile: { height: '200px' },
              },
              src: '',
              children: [],
            },
            {
              id: crypto.randomUUID(),
              type: 'div',
              label: 'Post meta',
              styles: { desktop: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '8px' } },
              children: [
                text('Post date', 'date', 'paragraph', 'small', 'muted'),
                text('Post categories', 'categories', 'eyebrow', 'small', 'accent'),
              ],
            },
            text('Post title', 'title', 'heading3', 'h3'),
            text('Post excerpt', 'excerpt', 'paragraph', 'p', 'muted'),
            {
              id: crypto.randomUUID(),
              /* Botón secundario, como en las tarjetas de ofertas: se lee como la
                 acción de la tarjeta y no como un enlace suelto. */
              type: 'button',
              label: 'Read more',
              themeRole: 'buttonSecondary',
              styleRefs: { button: 'button-secondary' },
              content: translate('blog.readMore'),
              interaction: {
                action: 'link',
                cmsLink: { source: 'projectContent', collection: '', slugField: 'slug', pageSlug: '/blog/[slug]' },
              },
              styles: { desktop: { width: 'fit-content' } },
              children: [],
            },
          ],
        }],
      },
    ],
  }]
}

/**
 * La lista del blog con la que nace `/blog`: «Featured Post + Grid». La
 * entrada más reciente grande, con su portada al lado del texto, y las demás
 * en la rejilla de siempre debajo, de 12 en 12 desde la segunda. Es el diseño
 * de la biblioteca del mismo nombre (`scripts/blog-disenos.mjs`), hecho aquí
 * con las piezas de `makeBlogIndexElements` para que nazca al instante y sin
 * depender de la red. Los demás siguen en la galería del blog.
 */
export function makeBlogFeaturedIndexElements(): ElementNode[] {
  const [section] = makeBlogIndexElements()
  const [title, grid] = section!.children
  const card = grid!.children[0]!
  const [cover, meta, postTitle, excerpt, readMore] = card.children
  /* Las piezas de la tarjeta, otra vez y con ids propios: una misma pieza no
     puede estar en dos sitios del árbol. */
  const fresh = (node: ElementNode): ElementNode => ({ ...node, id: crypto.randomUUID(), children: node.children.map(fresh) })
  const featured: ElementNode = {
    id: crypto.randomUUID(),
    type: 'div',
    label: 'Featured post',
    cmsRepeat: { source: 'projectContent', collection: CMS_BLOG_SOURCE, limit: 1 },
    styles: { desktop: { display: 'block', width: '100%' } },
    children: [{
      id: crypto.randomUUID(),
      type: 'div',
      label: 'Featured card',
      styles: {
        desktop: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 1fr)', gap: '40px', alignItems: 'center', minWidth: '0' },
        tablet: { gridTemplateColumns: '1fr', gap: '24px' },
      },
      children: [
        { ...fresh(cover!), label: 'Featured cover', styles: { desktop: { ...cover!.styles.desktop, height: '420px', borderRadius: '20px' }, mobile: { height: '240px' } } },
        {
          id: crypto.randomUUID(),
          type: 'div',
          label: 'Featured text',
          styles: { desktop: { display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '14px', minWidth: '0' } },
          children: [
            fresh(meta!),
            { ...fresh(postTitle!), label: 'Featured title', textTag: 'h2', themeRole: 'heading2', styleRefs: { ...postTitle!.styleRefs, typography: 'h2' } },
            fresh(excerpt!),
            fresh(readMore!),
          ],
        },
      ],
    }],
  }
  /* La rejilla empieza en la segunda: la primera ya está arriba. */
  const rest: ElementNode = { ...grid!, label: 'More posts', cmsRepeat: { ...grid!.cmsRepeat!, offset: 1 } }
  return [{
    ...section!,
    styles: { ...section!.styles, desktop: { ...section!.styles.desktop, gap: '48px' } },
    /* Con la barra de filtros (buscar, tema, autor, orden): se quita en Templates › Blog. */
    children: [title!, {
      id: crypto.randomUUID(),
      type: 'div',
      label: 'Blog filters',
      schemaId: 'blog-widget-filters',
      blogWidget: { kind: 'filters' },
      styles: { desktop: { display: 'block', width: '100%', minWidth: '0' } },
      children: [],
    }, featured, rest],
  }]
}

/**
 * La barra y el pie del sitio sin los enlaces a páginas que se acaban de borrar.
 *
 * En automático se rehacen solos (`autoSiteChrome`), pero hechos a mano se
 * quedaban apuntando a una dirección que ya no existe: al borrar una colección,
 * el pie seguía enlazando su lista y sus fichas. Se quita el enlace que va a
 * la página exacta y, si era la página de fichas (`/recetas/[slug]`), también
 * los que van a cualquiera de ellas.
 */
export function withoutLinksToPages(content: ProjectContent, removed: Page[]): ProjectContent {
  if (!removed.length || !content.components?.some((component) => component.slot)) return content
  const clean = (slug: string) => `/${slug.replace(/^\/+|\/+$/g, '')}`.replace(/\/$/, '') || '/'
  const exact = new Set<string>()
  const prefixes: string[] = []
  for (const page of removed) {
    if (page.isHome) continue
    if (/\[[^\]]+\]/.test(page.slug)) prefixes.push(`${clean(page.slug.replace(/\/?\[[^\]]+\].*$/, ''))}/`)
    else exact.add(clean(page.slug))
  }
  const deadHref = (node: ElementNode) => {
    const href = (node.interaction?.href ?? node.href ?? '').split(/[?#]/)[0] ?? ''
    if (!href.startsWith('/')) return false
    const path = clean(href)
    return exact.has(path) || prefixes.some((prefix) => `${path}/`.startsWith(prefix) && path.length >= prefix.length)
  }
  let changed = false
  const prune = (node: ElementNode): ElementNode => {
    if (!node.children.length) return node
    const children = node.children
      .filter((child) => {
        const dead = (child.type === 'link' || child.type === 'button') && child.schemaId !== 'nav-cta' && deadHref(child)
        if (dead) changed = true
        return !dead
      })
      .map(prune)
    return children.some((child, index) => child !== node.children[index]) || children.length !== node.children.length ? { ...node, children } : node
  }
  const components = content.components.map((component) => {
    if (!component.slot || component.node.autoContent) return component
    const node = prune(component.node)
    return node === component.node ? component : { ...component, node, updatedAt: new Date().toISOString() }
  })
  return changed ? { ...content, components } : content
}

/**
 * Las páginas con que nace una colección (menos el blog): «Card Grid» para la
 * lista y «Classic Entry» para la de cada entrada, los de la galería, atados a
 * sus campos (2026-10-20, él lo pidió). Ver `cmsDefaultDesigns.ts`.
 */
/**
 * Sin colores propios donde manda un estilo global: un texto con tipografía
 * toma el color de su estilo y una tarjeta el fondo y el borde de la suya,
 * como lo que crea «Insertar». Los diseños de la biblioteca los traen puestos.
 */
function withoutTextColors(nodes: ElementNode[]): ElementNode[] {
  return nodes.map((node) => {
    const children = withoutTextColors(node.children ?? [])
    const refs = node.styleRefs
    if (!refs?.colors) return { ...node, children }
    const colors = { ...refs.colors }
    if (refs.typography) delete colors.color
    if (refs.card) { delete colors.background; delete colors.backgroundColor; delete colors.borderColor }
    return { ...node, children, styleRefs: { ...refs, colors } }
  })
}
/**
 * Una sección que entra DENTRO de otra (el blog, una lista del CMS): sin fondo
 * propio. Con el «auto» se pintaba el claro del tema y tapaba el de la sección
 * que la contiene —una caja blanca sobre un hero oscuro—. Uno elegido a mano
 * se respeta.
 */
function withoutOwnSectionBackground(node: ElementNode): ElementNode {
  if (node.type !== 'section' || (node.backgroundRole && node.backgroundRole !== 'auto')) return node
  /* Y sin el estilo de sección del tema: es él quien pone el fondo claro y el
     color de texto (`section-default`). Su relleno lo trae la plantilla. */
  const { section: _section, ...styleRefs } = node.styleRefs ?? {}
  return { ...node, backgroundRole: 'custom', styleRefs }
}

function defaultCmsListElements(collection: CmsCollection, itemPageSlug?: string): ElementNode[] {
  return bindCmsSlots(withoutTextColors(CMS_DEFAULT_LIST_DESIGN), { ...collection, fields: ensureCmsSystemFields(collection.fields) }, { itemPageSlug })
}
/**
 * La lista de una colección para ponerla en CUALQUIER página —no en la suya—:
 * el diseño de tarjetas de siempre, atado a sus campos, con los enlaces a la
 * página de cada ficha. Para eso cambian tres cosas respecto a la página de la
 * colección: el título baja a h2 (la página ya tiene su h1), sin la
 * descripción de la colección (casi siempre vacía) y sin el filtro de estado —
 * el sitio publicado ya esconde los borradores, y en el lienzo se ven, que es
 * lo que se quiere al acabar de agregar uno—.
 */
export function cmsListSectionFor(collection: CmsCollection, pages: readonly Page[]): ElementNode | null {
  const itemPageSlug = pages.find((page) => page.cmsDynamic?.collection === collection.id && page.cmsDynamic.kind !== 'collection')?.slug
  const [section] = defaultCmsListElements(collection, itemPageSlug)
  if (!section) return null
  const adapt = (node: ElementNode): ElementNode | null => {
    if (node.aiSlot === 'cms.collectionIntro') return null
    const children = (node.children ?? []).map(adapt).filter((child): child is ElementNode => child !== null)
    if (node.aiSlot === 'cms.collectionName') {
      return { ...node, children, themeRole: 'heading2', styleRefs: { ...node.styleRefs, typography: 'h2' } }
    }
    if (node.cmsRepeat) {
      const { filterField: _field, filterValue: _value, ...repeat } = node.cmsRepeat
      return { ...node, children, cmsRepeat: repeat }
    }
    return { ...node, children }
  }
  return adapt({ ...section, label: collection.name })
}

function defaultCmsEntryElements(collection: CmsCollection, collectionPageSlug?: string): ElementNode[] {
  return bindCmsSlots(withoutTextColors(CMS_DEFAULT_ENTRY_DESIGN), { ...collection, fields: ensureCmsSystemFields(collection.fields) }, { collectionPageSlug })
}

function makeCmsCollectionPageElements(collectionId: string, collectionName: string, itemPageSlug?: string): ElementNode[] {
  const sectionId = crypto.randomUUID()
  return [
    {
      id: sectionId,
      type: 'section',
      label: 'CMS Collection Page',
      backgroundRole: 'background',
      themeRole: 'section',
      styleRefs: { section: 'section-default' },
      styles: {
        desktop: {
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          width: '100%',
          minHeight: '620px',
          paddingTop: '48px',
          paddingRight: '24px',
          paddingBottom: '64px',
          paddingLeft: '24px',
        },
        mobile: {
          paddingTop: '30px',
          paddingRight: '18px',
          paddingBottom: '44px',
          paddingLeft: '18px',
        },
      },
      children: [
        {
          id: crypto.randomUUID(),
          type: 'text',
          label: 'Collection Title',
          themeRole: 'heading1',
          styleRefs: { typography: 'h1' },
          styles: { desktop: {} },
          contentBinding: cmsCollectionMetaBinding(collectionId, '__name'),
          content: collectionName,
          children: [],
        },
        {
          id: crypto.randomUUID(),
          type: 'text',
          label: 'Collection Intro',
          themeRole: 'paragraph',
          styleRefs: { typography: 'p' },
          styles: { desktop: { fontSize: '16px', lineHeight: '1.6' } },
          contentBinding: cmsCollectionMetaBinding(collectionId, '__description'),
          content: '',
          children: [],
        },
        {
          id: crypto.randomUUID(),
          type: 'div',
          label: 'CMS Collection Grid',
          cmsRepeat: {
            source: 'projectContent',
            collection: collectionId,
            limit: 12,
            sortField: 'created',
            sortDirection: 'desc',
            filterField: 'status',
            filterValue: 'Público',
          },
          styles: {
            desktop: {
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '18px',
              width: '100%',
            },
            tablet: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
            mobile: { gridTemplateColumns: '1fr' },
          },
          children: [
            {
              id: crypto.randomUUID(),
              type: 'div',
              label: 'CMS Card',
              themeRole: 'card',
              styleRefs: { card: 'card-default', colors: { color: 'text' } },
              styles: {
                desktop: {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  minHeight: '260px',
                  overflow: 'hidden',
                },
              },
              children: [
                {
                  id: crypto.randomUUID(),
                  type: 'image',
                  label: 'CMS Image',
                  themeRole: 'image',
                  styleRefs: { colors: { background: 'surface' } },
                  contentBinding: cmsFieldBinding('image'),
                  styles: {
                    desktop: {
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: '6px',
                    },
                  },
                  src: '',
                  children: [],
                },
                {
                  id: crypto.randomUUID(),
                  type: 'text',
                  label: 'CMS Date',
                  themeRole: 'eyebrow',
                  styleRefs: { typography: 'small' },
                  contentBinding: cmsFieldBinding('created'),
                  styles: { desktop: { fontSize: '11px', fontWeight: '650', lineHeight: '1.2' } },
                  content: '05/20/2026',
                  children: [],
                },
                {
                  id: crypto.randomUUID(),
                  type: 'text',
                  label: 'CMS Title',
                  themeRole: 'heading4',
                  styleRefs: { typography: 'h4' },
                  contentBinding: cmsFieldBinding('title'),
                  styles: { desktop: { fontSize: '18px', fontWeight: '700', lineHeight: '1.25' } },
                  content: 'Título del post',
                  children: [],
                },
                {
                  id: crypto.randomUUID(),
                  type: 'text',
                  label: 'CMS Summary',
                  themeRole: 'paragraph',
                  styleRefs: { typography: 'p' },
                  contentBinding: cmsFieldBinding('description'),
                  styles: { desktop: { fontSize: '13px', lineHeight: '1.5' } },
                  content: 'Resumen del contenido...',
                  children: [],
                },
                {
                  id: crypto.randomUUID(),
                  type: 'button',
                  label: 'CMS Post Button',
                  themeRole: 'buttonPrimary',
                  styleRefs: { button: 'button-primary' },
                  interaction: {
                    action: 'link',
                    cmsLink: {
                      source: 'projectContent',
                      collection: '',
                      slugField: 'slug',
                      ...(itemPageSlug ? { pageSlug: itemPageSlug } : {}),
                    },
                  },
                  styles: {
                    desktop: {
                      width: 'fit-content',
                    },
                  },
                  content: 'Ver post',
                  children: [],
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

function cmsFieldFallback(field: CmsField): string {
  switch (field.type) {
    case 'title': return 'Título del post'
    case 'richText': return '<p>Contenido CMS</p>'
    case 'code': return ''
    case 'image':
    case 'multiImage':
      return ''
    case 'date': return '05/20/2026'
    case 'number': return '123'
    case 'email': return 'correo@ejemplo.com'
    case 'phone': return '+1 (555) 000-0000'
    case 'link': return 'https://ejemplo.com'
    case 'video': return 'https://youtube.com/watch?v=...'
    case 'option': return field.options?.[0] ?? 'Opción'
    default: return field.name
  }
}

function cmsFieldNode(field: CmsField, index: number): ElementNode {
  if (field.type === 'image' || field.type === 'multiImage') {
    return {
      id: crypto.randomUUID(),
      type: 'image',
      label: `CMS ${field.name}`,
      themeRole: 'image',
      styleRefs: { colors: { background: 'surface' } },
      contentBinding: cmsFieldBinding(field.id),
      styles: {
        desktop: {
          width: '100%',
          height: index === 0 ? '360px' : '280px',
          objectFit: 'cover',
          display: 'block',
          borderRadius: '8px',
          alignSelf: 'center',
        },
        mobile: { height: index === 0 ? '240px' : '220px' },
      },
      src: '',
      children: [],
    }
  }

  if (field.type === 'code') {
    return {
      id: crypto.randomUUID(),
      type: 'embed',
      label: `CMS ${field.name}`,
      themeRole: 'paragraph',
      contentBinding: cmsFieldBinding(field.id),
      styles: {
        desktop: {
          display: 'block',
          width: '100%',
          alignSelf: 'center',
        },
      },
      content: '',
      children: [],
    }
  }

  if (field.type === 'richText') {
    return {
      id: crypto.randomUUID(),
      type: 'div',
      label: `CMS ${field.name}`,
      themeRole: 'paragraph',
      styleRefs: { typography: 'p' },
      contentMode: 'html',
      contentBinding: cmsFieldBinding(field.id),
      styles: {
        desktop: {
          display: 'block',
          width: '100%',
          alignSelf: 'center',
          fontSize: '17px',
          lineHeight: '1.75',
        },
        mobile: { fontSize: '16px' },
      },
      content: cmsFieldFallback(field),
      children: [],
    }
  }

  if (field.type === 'title') {
    return {
      id: crypto.randomUUID(),
      type: 'text',
      label: `CMS ${field.name}`,
      themeRole: 'heading1',
      styleRefs: { typography: 'h1' },
      contentBinding: cmsFieldBinding(field.id),
      styles: { desktop: { alignSelf: 'center' } },
      content: cmsFieldFallback(field),
      children: [],
    }
  }

  return {
    id: crypto.randomUUID(),
    type: 'div',
    label: `CMS ${field.name}`,
    themeRole: 'card',
    styleRefs: { card: 'card-default', colors: { color: 'text' } },
    styles: {
      desktop: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        width: '100%',
        alignSelf: 'center',
      },
    },
    children: [
      {
        id: crypto.randomUUID(),
        type: 'text',
        label: `CMS ${field.name} Label`,
        themeRole: 'eyebrow',
        styleRefs: { typography: 'small' },
        styles: { desktop: { fontSize: '11px', fontWeight: '700', lineHeight: '1.2', textTransform: 'uppercase' } },
        content: field.name,
        children: [],
      },
      {
        id: crypto.randomUUID(),
        type: 'text',
        label: `CMS ${field.name} Value`,
        themeRole: 'paragraph',
        styleRefs: { typography: 'p' },
        contentBinding: cmsFieldBinding(field.id),
        styles: { desktop: { fontSize: '15px', lineHeight: '1.45' } },
        content: cmsFieldFallback(field),
        children: [],
      },
    ],
  }
}

/**
 * La plantilla de entrada del blog con la que se empieza: un artículo, no una
 * ficha. Ruta, categorías, titular, entradilla, el autor con su foto, fecha y
 * tiempo de lectura, portada y el cuerpo, en una columna a ancho de lectura; al
 * final compartir, el perfil del autor y la anterior/siguiente. Cada pieza va enlazada a su campo
 * y con los tokens del tema; a partir de aquí se diseña como cualquier página.
 */
export function makeBlogTemplateElements(): ElementNode[] {
  const text = (field: string, role: ElementNode['themeRole'], typography: string, extra: Partial<ElementNode> = {}, desktop: Record<string, string> = {}): ElementNode => ({
    id: crypto.randomUUID(),
    type: 'text',
    label: `Blog ${field}`,
    themeRole: role,
    styleRefs: { typography, ...(extra.styleRefs ?? {}) },
    contentBinding: cmsFieldBinding(field),
    styles: { desktop: { maxWidth: '100%', ...desktop } },
    content: '',
    children: [],
    ...extra,
  })
  /* Las piezas que el sitio llena al servir: ruta, compartir, perfil del autor, anterior/siguiente. */
  const widget = (kind: 'breadcrumb' | 'share' | 'authorProfile' | 'adjacent', label: string): ElementNode => ({
    id: crypto.randomUUID(),
    type: 'div',
    label,
    schemaId: `blog-widget-${kind}`,
    blogWidget: { kind },
    styles: { desktop: { display: 'block', width: '100%', minWidth: '0' } },
    children: [],
  })
  const column = (children: ElementNode[], desktop: Record<string, string>): ElementNode => ({
    id: crypto.randomUUID(),
    type: 'div',
    label: 'Blog column',
    styles: { desktop: { display: 'flex', flexDirection: 'column', width: '100%', alignSelf: 'center', ...desktop } },
    children,
  })
  return [{
    id: crypto.randomUUID(),
    type: 'section',
    label: 'Blog post',
    backgroundRole: 'background',
    themeRole: 'section',
    styleRefs: { section: 'section-default' },
    styles: {
      desktop: { display: 'flex', flexDirection: 'column', gap: '40px', width: '100%', paddingTop: '72px', paddingRight: '24px', paddingBottom: '96px', paddingLeft: '24px' },
      mobile: { gap: '28px', paddingTop: '40px', paddingRight: '18px', paddingBottom: '64px', paddingLeft: '18px' },
    },
    children: [
      column([
        widget('breadcrumb', 'Breadcrumb'),
        text('categories', 'eyebrow', 'small', { styleRefs: { typography: 'small', colors: { color: 'primary' } } }),
        text('title', 'heading1', 'h1', { textTag: 'h1' }),
        text('excerpt', 'paragraph', 'p', { styleRefs: { typography: 'p' } }, { fontSize: '1.15em' }),
        {
          /* «Autor»: su foto y, al lado, el nombre con la fecha y el tiempo de lectura debajo. */
          id: crypto.randomUUID(),
          type: 'div',
          label: 'Blog byline',
          schemaId: 'blog-author',
          styles: { desktop: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' } },
          children: [
            {
              id: crypto.randomUUID(),
              type: 'image',
              label: 'Blog author photo',
              themeRole: 'image',
              contentBinding: cmsFieldBinding('authorAvatar'),
              styles: { desktop: { width: '40px', height: '40px', objectFit: 'cover', display: 'block', borderRadius: '999px', flexShrink: '0' } },
              src: '',
              children: [],
            },
            {
              id: crypto.randomUUID(),
              type: 'div',
              label: 'Blog author text',
              styles: { desktop: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '0' } },
              children: [
                text('author', 'paragraph', 'p', { styleRefs: { typography: 'p', colors: { color: 'text' } } }, { fontWeight: '600' }),
                {
                  id: crypto.randomUUID(),
                  type: 'div',
                  label: 'Blog meta',
                  styles: { desktop: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '8px' } },
                  children: [
                    text('date', 'paragraph', 'small', { styleRefs: { typography: 'small', colors: { color: 'muted' } } }),
                    text('readingTime', 'paragraph', 'small', { styleRefs: { typography: 'small', colors: { color: 'muted' } } }),
                  ],
                },
              ],
            },
          ],
        },
      ], { gap: '16px' }),
      {
        id: crypto.randomUUID(),
        type: 'image',
        label: 'Blog cover',
        themeRole: 'image',
        styleRefs: { colors: { background: 'surface' } },
        contentBinding: cmsFieldBinding('image'),
        styles: {
          desktop: { width: '100%', height: '520px', objectFit: 'cover', display: 'block', borderRadius: '20px', alignSelf: 'center' },
          mobile: { height: '240px', borderRadius: '14px' },
        },
        src: '',
        children: [],
      },
      column([{
        id: crypto.randomUUID(),
        type: 'div',
        label: 'Blog content',
        themeRole: 'paragraph',
        styleRefs: { typography: 'p' },
        contentMode: 'html',
        contentBinding: cmsFieldBinding('content'),
        styles: { desktop: { display: 'block', width: '100%', fontSize: '18px', lineHeight: '1.75' }, mobile: { fontSize: '17px' } },
        content: '',
        children: [],
      }, widget('share', 'Share'), widget('authorProfile', 'About the author'), widget('adjacent', 'Previous and next')], { gap: '40px' }),
    ],
  }]
}

/**
 * Si un movimiento toca un bloque fijo (`pinned`): moverlo, o dejar algo
 * entre él y su sitio —por encima del primero, o entre dos seguidos—.
 */
function pinnedBlocksMove(elements: ElementNode[], elementId: string, target?: { parentChildren: ElementNode[]; index: number }): boolean {
  if (findNodeById(elements, elementId)?.pinned) return true
  if (!target) return false
  /* El sitio de destino: no puede quedar antes del último fijo de su nivel. */
  const lastPinned = target.parentChildren.map((child) => Boolean(child.pinned)).lastIndexOf(true)
  return lastPinned !== -1 && target.index <= lastPinned
}

/**
 * Lo recién insertado en la raíz de la página no queda por encima de un bloque
 * fijo: si cayó antes del último, baja justo debajo. Insertar «antes» del
 * catálogo de una página de familia, o tras la barra (al principio de la
 * página), lo dejaba encima de lo que esa página existe para enseñar.
 */
function belowPinnedBlocks(elements: ElementNode[], insertedId: string): ElementNode[] {
  const at = elements.findIndex((node) => node.id === insertedId)
  const lastPinned = elements.map((node) => Boolean(node.pinned)).lastIndexOf(true)
  if (at === -1 || lastPinned === -1 || at > lastPinned) return elements
  const next = elements.slice()
  const [moved] = next.splice(at, 1)
  next.splice(lastPinned, 0, moved!)
  return next
}

/** Los hijos del padre de un nodo (la raíz si no tiene) y su posición. */
function siblingsOf(elements: ElementNode[], id: string): { children: ElementNode[]; index: number } | null {
  const rootIndex = elements.findIndex((node) => node.id === id)
  if (rootIndex !== -1) return { children: elements, index: rootIndex }
  const parent = findParentElement(elements, id)
  if (!parent) return null
  return { children: parent.children, index: parent.children.findIndex((child) => child.id === id) }
}

/** Si una página es la plantilla de las ofertas de un tipo (o de cualquiera, sin tipo). */
export function isOfferTemplatePage(page: Pick<Page, 'cmsDynamic'> | null | undefined, kind?: OfferTemplateKind): boolean {
  if (page?.cmsDynamic?.collection !== CMS_OFFERS_SOURCE || !page.cmsDynamic.offerKind) return false
  return kind ? page.cmsDynamic.offerKind === kind : true
}

/**
 * La página de plantilla de un tipo, tal como nace. La usa «Página de cada…»
 * al crearla y la publicación cuando el sitio aún no tiene la suya: así toda
 * oferta sale con el diseño del editor y no con la página antigua del worker.
 */
export function offerTemplatePageFor(kind: OfferTemplateKind, theme: Page['theme']): Page {
  const now = new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    name: translate(`offerTemplate.pageName.${kind}`),
    /* Nunca se publica en esta dirección: sale una vez, con marcadores, y la
       sirve el worker en la de cada oferta. Ver `offerTemplatePath`. */
    slug: `/_offer-${kind}/[slug]`,
    cmsDynamic: { kind: 'item', collection: CMS_OFFERS_SOURCE, slugField: 'slug', offerKind: kind },
    isHome: false,
    isSlugCustom: true,
    elements: makeOfferTemplateElements(kind),
    theme,
    pageStyles: DEFAULT_PAGE_STYLES,
    seoTitle: '',
    seoDescription: '',
    canonicalUrl: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: null,
    accessMode: 'public',
    password: '',
    indexable: true,
    includeInSitemap: false,
    redirect301: null,
    customHeadCode: '',
    thumbnailUrl: null,
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * La plantilla de partida de la página de una oferta: la de una tienda.
 *
 * A la izquierda la galería; a la derecha el nombre, la descripción y la zona
 * de compra. Todo enlazado a la oferta (`CMS_OFFERS_SOURCE`), así que se diseña
 * una vez y vale para todas. Debajo cabe lo que se quiera: son secciones
 * normales.
 */
export function makeOfferTemplateElements(kind: OfferTemplateKind): ElementNode[] {
  const text = (field: string, role: ElementNode['themeRole'], typography: string, extra: Partial<ElementNode> = {}, desktop: Record<string, string> = {}): ElementNode => ({
    id: crypto.randomUUID(),
    type: 'text',
    label: `Offer ${field}`,
    themeRole: role,
    styleRefs: { typography, ...(extra.styleRefs ?? {}) },
    contentBinding: cmsFieldBinding(field),
    styles: { desktop: { maxWidth: '100%', ...desktop } },
    content: '',
    children: [],
    ...extra,
  })
  const column = (label: string, children: ElementNode[], desktop: Record<string, string>): ElementNode => ({
    id: crypto.randomUUID(),
    type: 'div',
    label,
    styles: { desktop: { display: 'flex', flexDirection: 'column', width: '100%', minWidth: '0', ...desktop } },
    children,
  })
  /* Una reserva o un servicio se entienden sin fotos grandes: la descripción
     manda y la galería va más pequeña. Un producto se compra por cómo se ve. */
  const product = kind === 'products'
  return [{
    id: crypto.randomUUID(),
    type: 'section',
    label: 'Offer',
    /* Fija: la compra va siempre primero. Ver `pinned`. */
    pinned: true,
    backgroundRole: 'background',
    themeRole: 'section',
    styleRefs: { section: 'section-default' },
    styles: {
      desktop: { display: 'grid', gridTemplateColumns: product ? '1.15fr 1fr' : '1fr 1fr', gap: '56px', alignItems: 'start', width: '100%', paddingTop: '64px', paddingRight: '24px', paddingBottom: '96px', paddingLeft: '24px' },
      mobile: { gridTemplateColumns: '1fr', gap: '28px', paddingTop: '28px', paddingRight: '18px', paddingBottom: '64px', paddingLeft: '18px' },
    },
    children: ([
      {
        id: crypto.randomUUID(),
        type: 'div',
        label: 'Offer gallery',
        behavior: { type: 'offerGallery' },
        styles: { desktop: { display: 'block', width: '100%', borderRadius: '18px' } },
        children: [],
      },
      column('Offer details', [
        text('type', 'eyebrow', 'small', { styleRefs: { typography: 'small', colors: { color: 'primary' } } }),
        text('title', 'heading1', 'h1', { textTag: 'h1' }),
        text('description', 'paragraph', 'p', { styleRefs: { typography: 'p' } }, { whiteSpace: 'pre-line' }),
        {
          id: crypto.randomUUID(),
          type: 'div',
          label: 'Offer purchase',
          behavior: { type: 'offerBuy' },
          styles: { desktop: { display: 'block', width: '100%', paddingTop: '8px' } },
          children: [],
        },
      ], { gap: '16px', position: 'sticky', top: '96px' }),
    ] as ElementNode[]).map((node, index) => (index === 1 ? { ...node, styles: { ...node.styles, mobile: { position: 'static' } } } : node)),
  }, makeOfferDetailsSection()]
}

/**
 * La sección fija de «Información del producto», justo debajo de la oferta:
 * los campos de «Información adicional» solos, en pestañas (acordeón en el
 * teléfono). El ancho y el aire, del estilo de sección.
 */
export function makeOfferDetailsSection(): ElementNode {
  return {
    id: crypto.randomUUID(),
    type: 'section',
    label: 'Offer details',
    pinned: true,
    themeRole: 'section',
    styleRefs: { section: 'section-default' },
    styles: { desktop: { display: 'flex', flexDirection: 'column', width: '100%' } },
    children: [{
      id: crypto.randomUUID(),
      type: 'div',
      label: 'Offer details',
      behavior: { type: 'offerDetails', layout: 'tabs' },
      styles: { desktop: { display: 'block', width: '100%' } },
      children: [],
    }],
  }
}

/** Una plantilla de oferta de antes del bloque de información: lo recibe, y la compra queda fija. */
function withOfferDetails(page: Page): Page {
  const has = (nodes: ElementNode[]): boolean => nodes.some((node) => node.behavior?.type === 'offerDetails' || has(node.children ?? []))
  if (has(page.elements)) return page
  const [first, ...rest] = page.elements
  if (!first) return page
  return { ...page, elements: [{ ...first, pinned: true }, makeOfferDetailsSection(), ...rest], updatedAt: new Date().toISOString() }
}

function makeCmsDynamicPageElements(collection: CmsCollection): ElementNode[] {
  const sectionId = crypto.randomUUID()
  const fields = collection.fields
    .filter((field) => isPlaceableCmsField(field) && !(collection.hiddenFields ?? []).includes(field.id))
  const orderedFields = [
    ...fields.filter((field) => field.type === 'title'),
    ...fields.filter((field) => field.type === 'image' || field.type === 'multiImage'),
    ...fields.filter((field) => field.type !== 'title' && field.type !== 'image' && field.type !== 'multiImage' && field.type !== 'richText'),
    ...fields.filter((field) => field.type === 'richText'),
  ]
  const fieldNodes = orderedFields.length
    ? orderedFields.map((field, index) => cmsFieldNode(field, index))
    : [cmsFieldNode({ id: 'title', name: 'Título', type: 'title' }, 0)]
  return [
    {
      id: sectionId,
      type: 'section',
      label: 'CMS Dynamic Page',
      backgroundRole: 'background',
      themeRole: 'section',
      styleRefs: { section: 'section-default' },
      styles: {
        desktop: {
          display: 'flex',
          flexDirection: 'column',
          gap: '22px',
          width: '100%',
          minHeight: '620px',
          paddingTop: '48px',
          paddingRight: '24px',
          paddingBottom: '64px',
          paddingLeft: '24px',
        },
        mobile: {
          paddingTop: '30px',
          paddingRight: '18px',
          paddingBottom: '44px',
          paddingLeft: '18px',
        },
      },
      children: fieldNodes,
    },
  ]
}

function cmsSlugPart(value: string, fallback: string): string {
  return generateSlug(value).replace(/^\//, '') || fallback
}

function makeDefaultCmsItems(collectionName: string): CmsItem[] {
  const today = new Date()
  const entries = [
    {
      title: 'Resumen',
      description: 'Una descripción general para presentar este contenido.',
      content: 'Este contenido resume la información principal de esta colección.',
      image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&h=800&fit=crop&auto=format',
    },
    {
      title: 'Características',
      description: 'Puntos clave y detalles importantes.',
      content: 'Usa esta entrada para explicar características, detalles o información relevante.',
      image: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1200&h=800&fit=crop&auto=format',
    },
    {
      title: 'Beneficios',
      description: 'Ventajas principales para el usuario.',
      content: 'Describe aquí los beneficios principales de este contenido o servicio.',
      image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1200&h=800&fit=crop&auto=format',
    },
    {
      title: 'Proceso',
      description: 'Una explicación simple del flujo o método.',
      content: 'Explica el proceso, pasos o metodología de forma clara y breve.',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=800&fit=crop&auto=format',
    },
    {
      title: 'Contacto',
      description: 'Información final o llamada a la acción.',
      content: 'Agrega una conclusión, llamada a la acción o información de contacto.',
      image: 'https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?w=1200&h=800&fit=crop&auto=format',
    },
  ]
  return entries.map((entry, index) => {
    const date = new Date(today)
    date.setDate(today.getDate() - index)
    const slug = cmsSlugPart(entry.title, `item-${index + 1}`)
    return {
      id: `item-${index + 1}`,
      values: {
        title: entry.title,
        image: entry.image,
        status: 'Público',
        slug,
        created: date.toISOString().slice(0, 10),
        categories: 'General',
        description: entry.description,
        content: `<p>${entry.content}</p><p>Puedes adaptar esta entrada para ${collectionName}.</p>`,
      },
    }
  })
}

function shouldReplaceGeneratedCmsItems(collection: CmsCollection): boolean {
  // An empty collection stays empty — never auto-fill it with demo items.
  if (collection.items.length === 0) return false
  if (
    collection.id === 'services' &&
    collection.items.length === 1 &&
    collection.items[0]?.values.title === 'Servicio principal' &&
    collection.items[0]?.values.description === 'Describe este servicio.'
  ) {
    return true
  }
  if (collection.items.length !== 5) return false
  const titles = collection.items.map((item) => item.values.title)
  const previousDemoTitles = ['Guía inicial', 'Ideas principales', 'Caso destacado', 'Recursos útiles', 'Próximos pasos']
  const postDemoTitles = ['Post 1', 'Post 2', 'Post 3', 'Post 4', 'Post 5']
  return titles.every((title, index) => title === previousDemoTitles[index])
    || titles.every((title, index) => title === postDemoTitles[index])
}

/**
 * Con qué nace una colección: el morado y el dibujo de base de datos.
 *
 * Antes nacían sin ninguno de los dos, y quien los ponía era quien las dibujaba:
 * el raíl elegía un color de su paleta por la POSICIÓN de la colección en la
 * lista. Eso hacía que el color no fuera de la colección sino de su sitio —
 * borrar una de en medio le cambiaba el color a todas las de abajo, y cada
 * pantalla que llevara su propia cuenta enseñaba otro color para la misma cosa.
 *
 * Quedan escritos en la colección desde el primer momento: así el color es un
 * dato suyo, viaja con ella y se cambia donde se cambian sus cosas —ajustes de
 * la colección—, no como efecto secundario de reordenar una lista.
 */
export const CMS_DEFAULT_COLOR = '#8B5CF6'
export const CMS_DEFAULT_ICON = 'database'

function makeDefaultCmsCollection(id: string, name: string): CmsCollection {
  return {
    id,
    name,
    description: '',
    icon: CMS_DEFAULT_ICON,
    color: CMS_DEFAULT_COLOR,
    fields: [
      { id: 'title', name: 'Título', type: 'title' },
      { id: 'image', name: 'Imagen', type: 'image' },
      { id: 'status', name: 'Estado', type: 'text' },
      { id: 'slug', name: 'Slug', type: 'text' },
      { id: 'created', name: 'Fecha de creación', type: 'text' },
      { id: 'categories', name: 'Categorías', type: 'text' },
    ],
    // Start empty — collections only get content the user adds, never demo items.
    items: [],
  }
}

function makeCmsPageBundle(
  collection: CmsCollection,
  existingPages: Page[],
  theme: PageTheme | ProjectTheme | undefined,
  now: string,
  baseSlug?: string,
): { collectionPage: Page; dynamicPage: Page } {
  const bundleCollection = { ...collection, fields: ensureCmsSystemFields(collection.fields) }
  const cleanBaseSlug = baseSlug?.replace(/\/$/, '')
  const collectionSlug = cleanBaseSlug
    ? `${cleanBaseSlug}/cms`
    : `/${cmsSlugPart(collection.name, collection.id)}`
  const collectionPageSlug = ensureUniqueSlug(collectionSlug || '/cms', existingPages)
  const dynamicPageSlug = ensureUniqueSlug(`${collectionPageSlug}/[slug]`, existingPages)
  const collectionPage: Page = {
    id: crypto.randomUUID(),
    name: collection.name,
    slug: collectionPageSlug,
    cmsDynamic: { kind: 'collection', collection: collection.id, slugField: 'slug' },
    isHome: false,
    isSlugCustom: false,
    elements: defaultCmsListElements(collection, dynamicPageSlug),
    theme: theme ?? DEFAULT_PAGE_THEME,
    pageStyles: DEFAULT_PAGE_STYLES,
    seoTitle: '',
    seoDescription: '',
    canonicalUrl: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: null,
    accessMode: 'public',
    password: '',
    indexable: true,
    includeInSitemap: true,
    redirect301: null,
    customHeadCode: '',
    thumbnailUrl: null,
    createdAt: now,
    updatedAt: now,
  }
  const dynamicPage: Page = {
    id: crypto.randomUUID(),
    name: `${collection.name} Template`,
    slug: dynamicPageSlug,
    cmsDynamic: { kind: 'item', collection: collection.id, slugField: 'slug' },
    isHome: false,
    isSlugCustom: true,
    elements: defaultCmsEntryElements(bundleCollection, collectionPageSlug),
    theme: theme ?? DEFAULT_PAGE_THEME,
    pageStyles: DEFAULT_PAGE_STYLES,
    seoTitle: '',
    seoDescription: '',
    canonicalUrl: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: null,
    accessMode: 'public',
    password: '',
    indexable: true,
    includeInSitemap: true,
    redirect301: null,
    customHeadCode: '',
    thumbnailUrl: null,
    createdAt: now,
    updatedAt: now,
  }
  return { collectionPage, dynamicPage }
}

export function ensureCmsPagesForCollections(
  pages: Page[],
  projectContent: ProjectContent,
  theme: PageTheme | ProjectTheme | undefined,
): { pages: Page[]; changed: boolean } {
  let nextPages = pages
  let changed = false
  for (const collection of projectContent.collections) {
    const hasCollectionPage = nextPages.some((page) =>
      page.cmsDynamic?.collection === collection.id && page.cmsDynamic.kind === 'collection'
    )
    const hasDynamicPage = nextPages.some((page) =>
      page.cmsDynamic?.collection === collection.id && page.cmsDynamic.kind !== 'collection'
    )
    if (hasCollectionPage && hasDynamicPage) continue

    const now = new Date().toISOString()
    const { collectionPage, dynamicPage } = makeCmsPageBundle(collection, nextPages, theme ?? DEFAULT_PAGE_THEME, now)
    const additions = [
      ...(hasCollectionPage ? [] : [collectionPage]),
      ...(hasDynamicPage ? [] : [dynamicPage]),
    ]
    if (additions.length === 0) continue
    nextPages = [...nextPages, ...additions]
    changed = true
  }
  return { pages: nextPages, changed }
}

/** Fill in defaults for any missing Page fields (backward compat with old projects) */
/** Lo guardado en `page.layout`, sólo con valores que existen. */
function readPageLayout(raw: unknown): PageLayout | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const value = raw as Record<string, unknown>
  const out: PageLayout = {}
  if (value.nav === 'show' || value.nav === 'minimal' || value.nav === 'hide') out.nav = value.nav
  if (value.footer === 'show' || value.footer === 'hide') out.footer = value.footer
  if (value.banner === 'show' || value.banner === 'hide') out.banner = value.banner
  return Object.keys(out).length ? out : undefined
}

/**
 * Las secciones de oferta destacada hechas cuando se quitó su ancho fijo: las
 * de fondo alterno, superficie o a sangre se quedaron sin estilo de sección y
 * se estiraban de borde a borde, sin hacer caso a «Limitado». Una sección de
 * primer nivel con una lista de ofertas dentro y sin ancho ni estilo de
 * sección recibe el de por defecto (ver `offerHeroBlueprint`). Lo mismo las
 * del blog con una lista de entradas (ver `blogLayoutBlueprint`).
 */
/**
 * Las tarjetas de oferta con el pie en fila (precio y botón lado a lado), como
 * las que dejaban los diseños de tienda de la IA: se apilan al abrir, precio
 * arriba y botón debajo. Ver `stackOfferCardFooters`.
 */
function withStackedOfferFooters(node: ElementNode): ElementNode {
  const repeats: ElementNode[] = []
  const find = (current: ElementNode) => {
    if (current.cmsRepeat?.collection === CMS_OFFERS_SOURCE) repeats.push(current)
    else (current.children ?? []).forEach(find)
  }
  find(node)
  if (!repeats.length) return node
  const copy = structuredClone(node)
  const stacked: ElementNode[] = []
  const collect = (current: ElementNode) => {
    if (current.cmsRepeat?.collection === CMS_OFFERS_SOURCE) stacked.push(current)
    else (current.children ?? []).forEach(collect)
  }
  collect(copy)
  return stacked.map((repeat) => stackOfferCardFooters(repeat.children)).some(Boolean) ? copy : node
}

function withOfferSectionStyle(node: ElementNode): ElementNode {
  if (node.type !== 'section' || node.styleRefs?.section || node.containerWidth) return node
  /* Las del panel Blog tenían el mismo fallo: una entrada destacada sobre el
     fondo alterno o la portada a sangre. */
  const hasOffers = (current: ElementNode): boolean => current.cmsRepeat?.collection === CMS_OFFERS_SOURCE || current.cmsRepeat?.collection === CMS_BLOG_SOURCE || (current.children ?? []).some(hasOffers)
  if (!(node.children ?? []).some(hasOffers)) return node
  return { ...node, styleRefs: { ...(node.styleRefs ?? {}), section: 'section-default' } }
}

/** Repair the original gallery-cover recipe, whose inline white color was
 * overridden by the typography token while automatic contrast was disabled. */
function withGalleryCoverContrast(node: ElementNode): ElementNode {
  if (node.type !== 'section' || node.label !== 'Gallery cover' || !findBackgroundPhotoLayer(node)) return node
  const repair = (current: ElementNode): ElementNode => {
    const children = current.children.map(repair)
    const isLegacyHeading = current.type === 'text'
      && (current.label === 'Title' || current.label === 'Subtitle')
      && current.textContrastMode === 'off'
      && current.styles.desktop.color?.toLowerCase() === '#ffffff'
      && !current.styleRefs?.overrides?.includes('color')
      && !current.styleRefs?.colors?.color
    if (isLegacyHeading) {
      const { color: _color, ...desktop } = current.styles.desktop
      return { ...current, children, textContrastMode: 'auto', styles: { ...current.styles, desktop } }
    }
    return children.some((child, index) => child !== current.children[index]) ? { ...current, children } : current
  }
  const repaired = repair(node)
  if (repaired.styles.desktop.background === 'color-mix(in srgb, var(--wb-primary) 35%, #17201e)'
    && !repaired.styleRefs?.overrides?.includes('background')) {
    return { ...repaired, styleRefs: { ...repaired.styleRefs, overrides: [...(repaired.styleRefs?.overrides ?? []), 'background'] } }
  }
  return repaired
}

export function normalizePage(raw: Record<string, unknown>): Page {
  const now = new Date().toISOString()
  const isHome = (raw.isHome as boolean) ?? false
  const name = (raw.name as string) ?? 'Page'
  const defaultSlug = isHome ? '/' : generateSlug(name) || '/pagina'
  return {
    id: (raw.id as string) ?? crypto.randomUUID(),
    name,
    slug: (raw.slug as string) ?? defaultSlug,
    cmsDynamic: raw.cmsDynamic && typeof raw.cmsDynamic === 'object'
      ? raw.cmsDynamic as Page['cmsDynamic']
      : undefined,
    isHome,
    isSlugCustom: (raw.isSlugCustom as boolean) ?? false,
    ...(readPageLayout(raw.layout) ? { layout: readPageLayout(raw.layout) } : {}),
    /* La barra y el footer sueltan lo que su estilo de sección ya pone (ver
       `releaseStructuralOverrides`): si no, editar ese estilo no se notaba. */
    elements: normalizeElementTree(raw.elements).map(releaseStructuralOverrides).map(withOfferSectionStyle).map(withStackedOfferFooters).map(withGalleryCoverContrast),
    theme: normalizeTheme(raw.theme),
    pageStyles: {
      ...DEFAULT_PAGE_STYLES,
      ...((raw.pageStyles && typeof raw.pageStyles === 'object') ? raw.pageStyles as Partial<ElementStyles> : {}),
      desktop: {
        ...DEFAULT_PAGE_STYLES.desktop,
        ...((raw.pageStyles && typeof raw.pageStyles === 'object')
          ? ((raw.pageStyles as Partial<ElementStyles>).desktop ?? {})
          : {}),
      },
    },
    seoTitle: (raw.seoTitle as string) ?? '',
    seoDescription: (raw.seoDescription as string) ?? '',
    canonicalUrl: (raw.canonicalUrl as string) ?? '',
    ogTitle: (raw.ogTitle as string) ?? '',
    ogDescription: (raw.ogDescription as string) ?? '',
    ogImage: (raw.ogImage as string | null) ?? null,
    ...(raw.unpublished === true ? { unpublished: true } : {}),
    accessMode: (raw.accessMode as Page['accessMode']) ?? 'public',
    password: (raw.password as string) ?? '',
    indexable: (raw.indexable as boolean) ?? true,
    includeInSitemap: (raw.includeInSitemap as boolean) ?? true,
    redirect301: (raw.redirect301 as string | null) ?? null,
    customHeadCode: (raw.customHeadCode as string) ?? '',
    thumbnailUrl: (raw.thumbnailUrl as string | null) ?? null,
    createdAt: (raw.createdAt as string) ?? now,
    updatedAt: (raw.updatedAt as string) ?? now,
  }
}

// Breakpoint is re-exported from style-engine above

// ─── Tree helpers (moved to ./treeHelpers) ───────────────────────────────────
export { findPathToElement, findParentElement } from './treeHelpers'
// ─── Store types ──────────────────────────────────────────────────────────────

export type ExtDropTarget = {
  imageSlot?: 'before' | 'after'
  background?: boolean
  /**
   * La ficha de la tarjeta donde se soltó, cuando la imagen está en una lista
   * (ofertas, blog, CMS): todas las copias comparten id, y esto dice CUÁL.
   */
  item?: { collectionId: string; itemId: string }
  targetId: string
  position: 'before' | 'after' | 'inside' | 'replace'
}

type EditorState = {
  pages: Page[]
  projectTheme: ProjectTheme
  projectContent: ProjectContent
  activePage: string | null
  /** Collection selected in the CMS workspace. Mirrored from CmsWorkspace so the
   *  topbar breadcrumb can show the active collection name. Transient (not persisted). */
  activeCmsCollectionId: string | null
  /** Post/item selected in the CMS workspace, for the breadcrumb's second level. Transient. */
  activeCmsItemId: string | null
  /**
   * Si el asistente AI ocupa la columna del inspector.
   *
   * Vive aquí y no en `App` porque quien lo enciende y lo apaga es el raíl del
   * inspector, tres componentes más abajo: pasar por su icono lo abre y pasar
   * por un grupo lo cierra, igual que el raíl de la izquierda cambia de panel
   * al pasar por encima. Por evento era un cable suelto de más.
   */
  aiPanelOpen: boolean
  /**
   * La primera frase escrita en la barra del lienzo, esperando a que el panel
   * la recoja.
   *
   * No se manda por evento porque el asistente puede no estar montado todavía
   * —abrir el panel y mandar el texto ocurren en el mismo gesto—, y un evento
   * disparado antes de que exista quien escucha se pierde sin más. Aquí espera
   * hasta que el panel aparece y lo consume.
   */
  aiPendingPrompt: string | null
  /**
   * El lienzo se está desplazando.
   *
   * Lo que flota sobre él —la barra contextual, el hueco entre secciones— se
   * coloca a partir de coordenadas medidas una vez, así que mientras el lienzo
   * corre se quedan atrás y persiguen a lo que señalan. Se retiran hasta que
   * pare. Vive en el store porque quien lo sabe es el lienzo y quienes tienen
   * que reaccionar cuelgan de otras ramas.
   */
  canvasScrolling: boolean
  /** Se está arrastrando un elemento dentro del lienzo. */
  canvasDragging: boolean
  /** Se está ajustando padding o gap directamente sobre el lienzo. */
  canvasSpacingAdjusting: boolean
  /**
   * Dónde está la línea de «agregar sección», en coordenadas de la ventana.
   *
   * La barra del elemento seleccionado la necesita para no aterrizar encima:
   * las dos se colocan centradas —una sobre el elemento, la otra sobre la
   * frontera— y cuando esa frontera cae junto a un canto del elemento se
   * disputan el mismo punto. `null` cuando no hay hueco abierto.
   */
  sectionGapY: number | null
  activeElement: string | null
  /**
   * La fila del bloque de preguntas sobre la que se pulsó.
   *
   * Las piezas de un FAQ comparten identificador —pulsar la respuesta de la
   * tercera selecciona «la respuesta», que es lo que mantiene el bloque parejo
   * al darle estilo—, así que la fila no cabe en `activeElement`. Y hace falta:
   * el enlace al CMS es de UNA pregunta, y sin saber cuál se ataría siempre la
   * primera.
   */
  activeFaqItem: string | null
  activePageLayer: boolean
  hoveredElement: string | null
  /** All selected element IDs (multi-select). Empty = single-select mode. */
  selectedElements: string[]
  breakpoint: Breakpoint
  /**
   * Los cambios valen para todos los tamaños, sea cual sea la vista.
   *
   * En tableta y teléfono se edita con plantillas y estilos, no a medida por
   * tamaño: cambiar un color viendo la vista de celular tiene que cambiarlo
   * también en la computadora. `breakpoint` sigue siendo la vista (se puede
   * mirar cómo queda en cada tamaño), pero lo que se escribe va a la base
   * (`desktop`) y suelta lo mismo de tablet y móvil. Ver `lib/editScope.ts`.
   */
  editsAllSizes: boolean
  /** Manual zoom (0.1–4). Used only when zoomMode === 'manual'. */
  zoom: number
  /** 'auto' = fit-to-width (canvas scales to fill the available width automatically);
   *  'manual' = the canvas renders at a fixed `manualWidthPx`, scaled by `zoom`. */
  zoomMode: 'auto' | 'manual'
  /** Exact canvas (logical viewport) width in px, used only in manual mode. */
  manualWidthPx: number
  /** Fit-to-width scale (desktop): band / logicalWidth, ≤ 1. Measured by App from the
   *  available canvas width so the ≥1280px canvas is scaled to fill 100% of the width.
   *  On-screen scale = zoomMode === 'auto' ? fitScale : zoom. */
  fitScale: number
  /** Screen-space rect of the selected element (set by Canvas) so the contextual
   *  toolbar can anchor itself directly below the selection. */
  selectionFrame: { left: number; top: number; width: number; height: number } | null
  /**
   * Se está escribiendo dentro de un elemento del lienzo.
   *
   * Vive aquí porque lo miran dos sitios que no se conocen: el lienzo, que
   * oculta sus propios controles, y la barra contextual, que se aparta para no
   * competir con la barra de formato que aparece sobre el texto.
   */
  isEditingText: boolean
  /** Set by Canvas while an external (panel) drag is hovering over the iframe */
  extDropTarget: ExtDropTarget | null
  /**
   * Live style overrides shown in the right panel during a canvas drag (resize
   * or spacing). Written on every mousemove, cleared on mouseup. Never stored
   * in `elements` so `srcDoc` is never rebuilt mid-drag.
   */
  previewStyles: Partial<StyleProperties> | null
  /** While dragging in the Layers panel: a transient copy of `pages` with the dragged
   *  element already moved to the hovered drop target. ONLY the Canvas renders this (so it
   *  reflows to show exactly where the element will land); the real `pages` stay untouched
   *  until drop, so autosave/undo/the panel are unaffected. */
  layerDragPreviewPages: Page[] | null
  /** La sección que se está arrastrando desde Capas: el lienzo la levanta y
   *  oscurece las demás mientras dura (ver `MSG_DRAG_FOCUS`). */
  layerDraggingId: string | null
  /** Qué se arrastra: una sección (se levanta y oscurece lo demás) o una pieza dentro (sólo brilla). */
  layerDragKind: 'section' | 'element'
  /** Element copied via Cmd+C or context menu — persists until overwritten */
  clipboard: ElementNode | null
  /** Visual style copied via ⌥⌘C ("copy style") — applied with ⌥⌘V */
  styleClipboard: StyleClipboard | null
  /** The container the user has "entered" (Figma-style deep selection) */
  enteredContainer: string | null
  /**
   * La instancia de seccion reutilizable en la que se ha entrado a editar.
   *
   * Editar dentro de una reutilizable cambia todas las paginas donde este
   * puesta, asi que entrar es un gesto aparte —doble clic— y no algo que pase
   * por pulsar. Un clic simple selecciona la instancia entera y avisa de lo que
   * es; el doble clic es el que da permiso.
   *
   * Guarda el id de la INSTANCIA, no el del componente: el contenido resuelto
   * repite los ids del original en cada instancia, y hace falta saber en cual
   * se entro para dibujar el resto de la pagina apagado alrededor de esa.
   */
  enteredComponentInstance: string | null
  /**
   * True for a short window after the user edits a property from the inspector.
   * The canvas hides the selection ring while true so the change is fully visible
   * (the element stays selected).
   */
  inspectorEditing: boolean
  /** Element id whose dropdown/popover panel is shown open in the canvas for editing.
   * Transient (not persisted); cleared on page change or "close". */
  editOpenId: string | null
  /** Id of the `embed` element whose custom-code editor modal is open. Transient. */
  codeEditId: string | null
  /** Interaction state the inspector is editing (hover/focus/…). `normal` = base styles.
   * The canvas previews the selected element in this state via `data-wb-active-state`. */
  activeInteractionState: InteractionState
}

export type StyleClipboard = {
  styles: ElementStyles
  iconSize?: number
  styleRefs?: ElementStyleRefs
  themeRole?: ThemeRole
  backgroundRole?: SectionBackgroundRole
}

type EditorActions = {
  /** Switch the active page */
  setActivePage: (id: string) => void
  setActiveCmsCollectionId: (id: string | null) => void
  setActiveCmsItemId: (id: string | null) => void
  setAiPanelOpen: (open: boolean) => void
  setAiPendingPrompt: (text: string | null) => void
  setCanvasScrolling: (scrolling: boolean) => void
  setCanvasDragging: (dragging: boolean) => void
  setCanvasSpacingAdjusting: (adjusting: boolean) => void
  setSectionGapY: (y: number | null) => void
  /** Show/hide a dropdown/popover panel as open in the canvas for editing. */
  setEditOpenId: (id: string | null) => void
  /** Open/close the custom-code editor modal for an `embed` element. */
  setCodeEditId: (id: string | null) => void
  /** Switch which interaction state the inspector edits + the canvas previews. */
  setActiveInteractionState: (state: InteractionState) => void
  /** Select an element (null clears selection). Always exits multi-select mode. */
  setActiveElement: (id: string | null) => void
  /** Ver `activeFaqItem`. Lo manda el lienzo con cada clic. */
  setActiveFaqItem: (id: string | null) => void
  /** Select the page root layer explicitly from Layers or breadcrumb. */
  setActivePageLayer: (active: boolean) => void
  /** Hover an element from an external panel (e.g. layers). Triggers canvas highlight. */
  setHoveredElement: (id: string | null) => void
  /**
   * Shift+click: add/remove an element from the multi-selection.
   * If called for the first time (selectedElements is empty), anchors
   * the current activeElement + the toggled id into the group.
   */
  toggleElementSelection: (id: string) => void
  setBreakpoint: (breakpoint: Breakpoint) => void
  setEditsAllSizes: (value: boolean) => void
  /** zoom: 0.1–4, where 1 = 100% */
  setZoom: (zoom: number) => void
  setFitScale: (fitScale: number) => void
  setZoomMode: (mode: 'auto' | 'manual') => void
  setManualWidthPx: (px: number) => void
  /** Briefly flag that the inspector is editing a property (hides the canvas selection ring). */
  pulseInspectorEditing: () => void

  /**
   * Update any fields on an existing element.
   * Pass `styles` as a full replacement, not a merge.
   * To merge styles, spread the existing ones first:
   *   updateElement(pageId, id, { styles: { ...old.styles, color: 'red' } })
   */
  updateElement: (pageId: string, elementId: string, updates: Partial<ElementNode>) => void
  /**
   * Convierte un elemento en sección reutilizable y lo deja como instancia.
   *
   * El nodo se muda al proyecto y en la página queda la referencia. A partir de
   * ahí, colocarlo en otra página es colocar la misma cosa, no una copia.
   */
  createComponentFromElement: (pageId: string, elementId: string, name: string) => string | null
  /** Edita el original; todas sus instancias cambian con él. Deshacible. */
  updateComponent: (componentId: string, node: ElementNode) => void
  /**
   * Pone la barra o el pie del sitio —o lo quita, con `null`—. Es uno para
   * todas las páginas: ver `withSiteLayout`.
   */
  setSiteSlot: (slot: SiteSlot, node: ElementNode | null) => void
  /** Si una página enseña la barra y el pie del sitio, y cómo. */
  setPageLayout: (pageId: string, layout: PageLayout) => void
  /** Suelta una instancia: se queda el contenido y deja de seguir al original. */
  detachComponentInstance: (pageId: string, elementId: string) => void
  renameComponent: (componentId: string, name: string) => void
  /**
   * Borra el original y suelta sus instancias con el contenido dentro.
   *
   * No las borra: quien quita un componente quiere dejar de mantenerlo en un
   * sitio, no vaciar cinco páginas. Borrarlas sería destruir trabajo sin
   * decirlo, y es exactamente el tipo de acción que la gente no perdona.
   */
  deleteComponent: (componentId: string) => void

  /** Add element as a child of parentId, or at page root if parentId is null */
  addElement: (pageId: string, parentId: string | null, element: ElementNode) => void

  /** Remove element and all its descendants */
  deleteElement: (pageId: string, elementId: string) => void

  /** Move an element to a new position in the tree. Undoable. */
  moveElement: (
    pageId: string,
    elementId: string,
    targetId: string,
    position: 'before' | 'after' | 'inside'
  ) => void

  /** Move an element to an exact child index under parentId. Undoable. */
  moveElementToIndex: (
    pageId: string,
    elementId: string,
    parentId: string | null,
    index: number
  ) => void

  /** Insert a brand-new element relative to targetId. Undoable. */
  insertElementAt: (
    pageId: string,
    element: ElementNode,
    targetId: string,
    position: 'before' | 'after' | 'inside'
  ) => void

  setSelectionFrame: (frame: { left: number; top: number; width: number; height: number } | null) => void
  setIsEditingText: (editing: boolean) => void
  setExtDropTarget: (target: ExtDropTarget | null) => void
  /** Set / clear the Canvas-only live-reflow preview for a Layers-panel drag. `target === null`
   *  clears it (Canvas shows the real, unmoved page). Never touches the real `pages`. */
  setLayerDragPreview: (elementId: string, target: { parentId: string | null; index: number } | null) => void
  clearLayerDragPreview: () => void
  setLayerDraggingId: (id: string | null, kind?: 'section' | 'element') => void
  /** Set / clear live preview overrides during a canvas drag */
  setPreviewStyles: (patch: Partial<StyleProperties> | null) => void
  /** Enter a container for deep selection (Figma-style). null exits all. */
  setEnteredContainer: (id: string | null) => void
  setEnteredComponentInstance: (id: string | null) => void
  /** Copy element to clipboard (deep clone, no undo) */
  copyElement: (elementId: string) => void
  /** Set clipboard directly from a node (deep clone, no undo). */
  setClipboardElement: (element: ElementNode | null) => void
  /** Paste clipboard as sibling after activeElement, or at root. Undoable. */
  pasteElement: (pageId: string) => void
  /** Copy + paste as next sibling in one step. Undoable. */
  duplicateElement: (pageId: string, elementId: string) => void
  /** Añade un panel de pestañas Y su botón, que van siempre en pareja. */
  addTabsPanel: (pageId: string, hostId: string) => void
  /** Quita el panel de esa posición y su botón. */
  removeTabsPanel: (pageId: string, hostId: string, index: number) => void
  /** Copy an element's visual style (appearance only — no size/position/layout). No undo. */
  copyElementStyle: (elementId: string) => void
  /** Apply the copied style onto the given elements (merged over their styles). Undoable. */
  pasteElementStyle: (pageId: string, elementIds: string[]) => void
  /** Move a node one step up or down among its siblings. Undoable. */
  /** Mueve un elemento entre sus hermanos. `steps` son los puestos: una fila
   *  entera cuando el padre es una cuadrícula, uno en todo lo demás. */
  reorderElement: (pageId: string, elementId: string, direction: 'up' | 'down', steps?: number) => void
  /**
   * Wrap the given sibling elements in a new div container. Undoable.
   * ids must all be siblings at the same level in the tree.
   */
  groupElements: (pageId: string, ids: string[]) => void
  /** Wrap a single element in a new block (div) container. */
  wrapElement: (pageId: string, elementId: string) => void
  /**
   * Apply a style patch to every element in `ids` simultaneously.
   * Merges into the element's existing styles at the given breakpoint.
   * Saves a single history snapshot for the whole batch.
   */
  updateMultipleStyles: (
    pageId: string,
    ids: string[],
    breakpoint: Breakpoint,
    patch: Partial<StyleProperties>
  ) => void

  /** Add and activate a page, optionally initialized from a page template. */
  /** `slug`: una dirección fija (la de una página del catálogo), en vez de sacarla del nombre. */
  addPage: (name?: string, elements?: ElementNode[], options?: { slug?: string }) => void
  /**
   * Abre la plantilla de la página de cada colección, creándola con `build` si
   * aún no existe, y la enseña con la colección `previewSlug` si se pide.
   */
  openCollectionTemplatePage: (build: () => Page, previewSlug?: string) => void
  /** Un diseño propio para una colección: copia del general (o `elements`), que deja de seguirlo. */
  ownCollectionTemplatePage: (build: () => Page, slug: string, title: string, elements?: ElementNode[]) => void
  /** Quita el diseño propio de una colección: vuelve a verse con el general. */
  dropCollectionTemplatePage: (slug: string) => void
  /** Create/activate a dynamic page template for a CMS collection. */
  createCmsDynamicPage: (collectionId: string) => void
  /** Abre —o crea— la plantilla de la página de cada oferta de un tipo. */
  createOfferTemplatePage: (kind: OfferTemplateKind) => void
  /** Create/activate the index/listing page for a CMS collection. */
  createCmsCollectionPage: (collectionId: string) => void
  /** Delete a page (no-op if it's the last one or home page). Activates the previous page. */
  deletePage: (pageId: string) => void
  /** Rename a page */
  renamePage: (pageId: string, name: string) => void
  /** Duplicate a page (deep-clone elements with fresh IDs) and activate the copy */
  duplicatePage: (pageId: string) => void
  /** Reorder pages by providing the new ordered list of page IDs */
  reorderPages: (orderedIds: string[]) => void
  /** Update page settings (SEO, OG, advanced, etc.) */
  updatePageSettings: (pageId: string, settings: Partial<Omit<Page, 'id' | 'elements'>>) => void
  /** Cambia todo el contenido de una página de una vez (aplicar un diseño), con deshacer. */
  replacePageElements: (pageId: string, elements: ElementNode[]) => void
  /** Update the shared visual theme for one page. Undoable. */
  updatePageTheme: (pageId: string, theme: PageTheme) => void
  /** Update the shared project-level visual system. Undoable. */
  updateProjectTheme: (theme: ProjectTheme) => void
  patchProjectTheme: (updater: (theme: ProjectTheme) => ProjectTheme) => void
  /** Update shared project content (business/contact/navigation/collections). Undoable. */
  updateProjectContent: (patch: Partial<ProjectContent>) => void
  /** Carga las entradas del blog como la colección virtual `CMS_BLOG_SOURCE`. Sin historial: no es un cambio del proyecto. */
  setBlogCollection: (collection: CmsCollection | null) => void
  // ── Dynamic CMS collection CRUD ──
  addCmsCollection: () => void
  createCmsCollection: (config: { name: string; url?: string; fields?: { name: string; type: CmsFieldType; options?: string[] }[]; icon?: string; color?: string }) => string
  updateCmsCollection: (collectionId: string, config: { name: string; url?: string; fields?: { id?: string; name: string; type: CmsFieldType; options?: string[] }[] }) => void
  renameCmsCollection: (collectionId: string, name: string) => void
  setCmsCollectionDescription: (collectionId: string, description: string) => void
  setCmsCollectionImage: (collectionId: string, image: string) => void
  setCmsCollectionIcon: (collectionId: string, icon: string) => void
  setCmsCollectionColor: (collectionId: string, color: string) => void
  /** Declara (o quita) los campos de fecha que dan vigencia a la colección. */
  setCmsCollectionSchedule: (collectionId: string, schedule: ScheduleWindow | null) => void
  toggleCmsFieldHidden: (collectionId: string, fieldId: string) => void
  deleteCmsCollection: (collectionId: string) => void
  /**
   * Añade un campo y devuelve su id.
   *
   * `near` lo coloca junto a otro en vez de al final: al insertar una columna
   * desde la tabla se sabe dónde tiene que caer, y devolverla al final para
   * después moverla habría hecho parpadear toda la fila.
   */
  addCmsField: (collectionId: string, type: CmsFieldType, near?: { fieldId: string; side: 'before' | 'after' }) => string
  /** Copia un campo con sus datos, justo detrás del original. Devuelve su id. */
  duplicateCmsField: (collectionId: string, fieldId: string) => string
  renameCmsField: (collectionId: string, fieldId: string, name: string) => void
  setCmsFieldType: (collectionId: string, fieldId: string, type: CmsFieldType) => void
  setCmsFieldOptions: (collectionId: string, fieldId: string, options: string[]) => void
  /** Guarda el orden de las columnas de la tabla, de izquierda a derecha. */
  setCmsColumnOrder: (collectionId: string, order: string[]) => void
  /** Fija el color de una opción/categoría. Con `''` vuelve al automático. */
  setCmsFieldOptionColor: (collectionId: string, fieldId: string, option: string, color: string) => void
  /** Borra una opción/categoría del campo y de todas las fichas que la usaban. */
  removeCmsFieldOption: (collectionId: string, fieldId: string, option: string) => void
  deleteCmsField: (collectionId: string, fieldId: string) => void
  addCmsItem: (collectionId: string) => string
  duplicateCmsItem: (collectionId: string, itemId: string) => void
  /**
   * Cambia un valor de una ficha.
   *
   * `syncSlug` sólo mientras se CREA. La URL sale del título la primera vez y
   * después es suya: es la dirección por la que se publica el post, así que
   * reescribirla en cada retoque del título rompe el enlace que alguien pudo
   * compartir o que ya está indexado. Por eso viene apagada salvo que quien
   * llama diga expresamente que estamos en el alta.
   */
  updateCmsItemValue: (collectionId: string, itemId: string, fieldId: string, value: string, options?: { syncSlug?: boolean }) => void
  replaceCmsItemValues: (collectionId: string, itemId: string, values: Record<string, string>) => void
  publishCmsItem: (collectionId: string, itemId: string) => void
  unpublishCmsItem: (collectionId: string, itemId: string) => void
  discardCmsItemChanges: (collectionId: string, itemId: string) => void
  deleteCmsItem: (collectionId: string, itemId: string) => void
  /** Make the given page the home page. Transfers isHome from the current home. */
  setHomePage: (pageId: string) => void
}

type EditorStore = EditorState & EditorActions

// ─── Initial state ────────────────────────────────────────────────────────────


const DEFAULT_PAGE: Page = {
  id: crypto.randomUUID(),
  name: 'Página principal',
  slug: '/',
  isHome: true,
  isSlugCustom: false,
  elements: [],
  theme: DEFAULT_PAGE_THEME,
  pageStyles: DEFAULT_PAGE_STYLES,
  seoTitle: '',
  seoDescription: '',
  canonicalUrl: '',
  ogTitle: '',
  ogDescription: '',
  ogImage: null,
  accessMode: 'public',
  password: '',
  indexable: true,
  includeInSitemap: true,
  redirect301: null,
  customHeadCode: '',
  thumbnailUrl: null,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
}

const DEFAULT_PROJECT_THEME = normalizeProjectTheme(DEFAULT_PAGE_THEME)
const DEFAULT_INITIAL_PROJECT = (() => {
  const cmsPages = ensureCmsPagesForCollections([DEFAULT_PAGE], DEFAULT_PROJECT_CONTENT, DEFAULT_PROJECT_THEME)
  return {
    pages: cmsPages.pages,
    projectTheme: DEFAULT_PROJECT_THEME,
    projectContent: DEFAULT_PROJECT_CONTENT,
  }
})()

// ─── Store ────────────────────────────────────────────────────────────────────

let inspectorEditingTimer: ReturnType<typeof setTimeout> | null = null

/** Snapshot + immutably replace one CMS collection by id. */
function mapCollection(
  state: EditorStore,
  collectionId: string,
  fn: (c: CmsCollection) => CmsCollection,
): Partial<EditorStore> {
  useHistoryStore.getState().pushSnapshot(state.pages)
  return {
    projectContent: {
      ...state.projectContent,
      collections: state.projectContent.collections.map((c) => (c.id === collectionId ? fn(c) : c)),
    },
  }
}

function pickStyleRecord(rec: Partial<StyleProperties> | undefined): Partial<StyleProperties> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(rec ?? {})) {
    if (value != null) out[key] = value
  }
  return out as Partial<StyleProperties>
}

function pickAppearanceStyles(styles: ElementStyles): ElementStyles {
  return {
    desktop: pickStyleRecord(styles.desktop) as StyleProperties,
    ...(styles.tablet ? { tablet: pickStyleRecord(styles.tablet) } : {}),
    ...(styles.mobile ? { mobile: pickStyleRecord(styles.mobile) } : {}),
  }
}

function mergeAppearanceStyles(target: ElementStyles, src: ElementStyles): ElementStyles {
  return {
    desktop: { ...target.desktop, ...src.desktop },
    ...(target.tablet || src.tablet ? { tablet: { ...(target.tablet ?? {}), ...(src.tablet ?? {}) } } : {}),
    ...(target.mobile || src.mobile ? { mobile: { ...(target.mobile ?? {}), ...(src.mobile ?? {}) } } : {}),
  }
}


/**
 * Un hijo cuya EXISTENCIA la gestiona el widget.
 *
 * Los paneles de unas pestañas y sus botones no son elementos sueltos que
 * casualmente viven ahí: son una lista, y los dos lados tienen que cuadrar —un
 * panel de más sin su botón no se puede abrir, y un botón sin panel abre el
 * vacío. Borrar uno en el lienzo dejaba el widget descuadrado sin decir nada.
 *
 * Así que aquí se añaden y se quitan desde los ajustes del bloque, que es donde
 * se puede mantener la pareja. Dentro se sigue pudiendo diseñar todo lo demás:
 * lo que se cierra es CREAR y BORRAR, no editar.
 */
/** El descendiente con ese `schemaId`, para llegar al carril o a la barra. */
function findBySchema(node: ElementNode, schemaId: string): ElementNode | null {
  if (node.schemaId === schemaId) return node
  for (const child of node.children ?? []) {
    const hit = findBySchema(child, schemaId)
    if (hit) return hit
  }
  return null
}

/** Los contenedores cuya lista de hijos mantiene el widget. */
function isWidgetManagedParent(node: ElementNode | null | undefined): boolean {
  return node?.schemaId === 'wb-tabs-track' || node?.schemaId === 'wb-tabs-nav'
}

function isWidgetManagedChild(elements: ElementNode[], elementId: string): boolean {
  return isWidgetManagedParent(findParentElement(elements, elementId))
}

function owningTreeElements(state: { pages: Page[]; projectContent: ProjectContent }, pageId: string, elementId: string): ElementNode[] {
  const elements = state.pages.find((page) => page.id === pageId)?.elements ?? []
  if (findNodeById(elements, elementId)) return elements
  const owner = componentOwning(state.projectContent, elementId)
  return owner ? [owner.node] : elements
}

/**
 * Aplica un cambio al árbol que DE VERDAD contiene el elemento.
 *
 * Hay dos sitios donde puede vivir un nodo: la página, o el original de una
 * sección reutilizable. Los hijos de una instancia no están en la página —salen
 * del original al dibujar—, así que buscarlos siempre en la página hace que la
 * mitad de las acciones no encuentren nada y no hagan nada, en silencio.
 *
 * Esto se escribió una vez porque son cuatro las acciones que lo necesitan
 * —editar, borrar, mover y reordenar—, y la quinta que se añada lo va a
 * necesitar igual. Repetido en cada una, la que se olvide será la que falle sin
 * decir por qué.
 *
 * Devuelve el parche de estado, o `null` si el elemento no está en ninguno de
 * los dos sitios.
 */
function applyToOwningTree(
  state: { pages: Page[]; projectContent: ProjectContent },
  pageId: string,
  elementId: string,
  transform: (nodes: ElementNode[]) => ElementNode[],
): { pages?: Page[]; projectContent?: ProjectContent } | null {
  const page = state.pages.find((item) => item.id === pageId)
  if (page && findNodeById(page.elements, elementId)) {
    return {
      pages: state.pages.map((item) => (
        item.id !== pageId ? item : { ...item, elements: transform(item.elements) }
      )),
    }
  }

  const owner = componentOwning(state.projectContent, elementId)
  if (!owner) return null
  const [updated] = transform([owner.node])
  if (!updated) return null
  return {
    projectContent: {
      ...state.projectContent,
      components: (state.projectContent.components ?? []).map((component) => (
        component.id === owner.id
          ? { ...component, node: updated, updatedAt: new Date().toISOString() }
          : component
      )),
    },
  }
}

/**
 * Meter algo DENTRO de una sección reutilizable.
 *
 * `applyToOwningTree` decide dónde escribir preguntando «¿este id está en la
 * página?». Para un HIJO del original la respuesta es no, y por eso acierta. Para
 * la instancia misma la respuesta es sí —la instancia sí vive en la página— así
 * que escribía sus hijos ahí… y ahí no los lee nadie: al dibujar,
 * `resolveComponentInstances` sustituye los hijos de la instancia por los del
 * original. El elemento se guardaba y no aparecía en ninguna parte.
 *
 * Era el mismo agujero por tres puertas: el «+» de la barra, pegar, y soltar algo
 * dentro desde el panel de elementos.
 *
 * Devuelve `null` cuando el destino no es una instancia, que es el caso normal.
 */
function addInsideInstance(
  state: { pages: Page[]; projectContent: ProjectContent },
  pageId: string,
  parentId: string,
  place: (children: ElementNode[]) => ElementNode[],
): { projectContent: ProjectContent } | null {
  const page = state.pages.find((item) => item.id === pageId)
  const componentId = (page ? findNodeById(page.elements, parentId) : undefined)?.componentId
  if (!componentId) return null
  const components = state.projectContent.components ?? []
  const master = components.find((item) => item.id === componentId)
  /* Sin original no hay dónde meterlo, y la instancia se queda con lo que
     tuviera dentro: escribir en la página aquí sí vale, porque sin original ya
     no se sustituye nada al dibujar. */
  if (!master) return null
  return {
    projectContent: {
      ...state.projectContent,
      components: components.map((item) => (
        item.id !== componentId ? item : {
          ...item,
          node: { ...item.node, children: place(item.node.children) },
          updatedAt: new Date().toISOString(),
        }
      )),
    },
  }
}

/**
 * El nodo, esté en la página o dentro de una sección reutilizable.
 *
 * `findNodeById` sobre la página no basta: los hijos de una instancia no están
 * en su árbol —la instancia los tiene vacíos y salen del original al dibujar—,
 * así que buscar sólo ahí devuelve `undefined` para todo lo que se edite dentro
 * de un componente.
 */
function findNodeAnywhere(
  state: { pages: Page[]; projectContent: ProjectContent },
  pageId: string,
  elementId: string,
): ElementNode | undefined {
  const page = state.pages.find((item) => item.id === pageId)
  const inPage = page ? findNodeById(page.elements, elementId) : undefined
  if (inPage) return inPage
  const owner = componentOwning(state.projectContent, elementId)
  return owner ? findNodeById([owner.node], elementId) : undefined
}

const RADIUS_KEYS = ['borderRadius', 'borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomLeftRadius', 'borderBottomRightRadius'] as const

/**
 * Cambiar a mano el radio de una foto lo hace suyo.
 *
 * Las fotos redondeadas siguen el radio de las tarjetas del estilo (ver
 * `applyMediaRadius` en el render), salvo que su `borderRadius` esté en
 * `overrides`. Sin anotarlo aquí, el valor nuevo quedaba debajo del del estilo
 * y el control parecía bloqueado. En el editor de bloques del admin no se anota:
 * allí se define el radio del bloque, el que después sigue al estilo.
 */
export function withMediaRadiusOverride(node: ElementNode | undefined, updates: Partial<ElementNode>, pageId: string): Partial<ElementNode> {
  if (!node || !updates.styles || pageId.startsWith('template:')) return updates
  if (!isMediaNode(node)) return updates
  const touched = Object.entries(updates.styles).some(([breakpoint, next]) => {
    const before = (node.styles as Record<string, Record<string, unknown> | undefined>)[breakpoint]
    return RADIUS_KEYS.some((key) => (next as Record<string, unknown> | undefined)?.[key] !== before?.[key])
  })
  if (!touched) return updates
  const refs = updates.styleRefs ?? node.styleRefs ?? {}
  if (refs.overrides?.includes('borderRadius')) return updates
  return { ...updates, styleRefs: { ...refs, overrides: [...(refs.overrides ?? []), 'borderRadius'] } }
}

export const useEditorStore = create<EditorStore>()(
  devtools(
    /* `get` hace falta para leer el árbol antes de escribirlo: crear un
       componente necesita el nodo de origen, y sólo se sabe cuál mirando. */
    (set, get) => ({
      // State
      pages: DEFAULT_INITIAL_PROJECT.pages,
      projectTheme: DEFAULT_INITIAL_PROJECT.projectTheme,
      projectContent: DEFAULT_INITIAL_PROJECT.projectContent,
      activePage: DEFAULT_PAGE.id,
      activeCmsCollectionId: null,
      activeCmsItemId: null,
      aiPanelOpen: false,
      aiPendingPrompt: null,
      canvasScrolling: false,
      canvasDragging: false,
      canvasSpacingAdjusting: false,
      sectionGapY: null,
      activeElement: null,
      activeFaqItem: null,
      activePageLayer: false,
      hoveredElement: null,
      selectedElements: [],
      breakpoint: 'desktop',
      editsAllSizes: false,
      zoom: 1,
      zoomMode: 'auto',
      manualWidthPx: 1280,
      fitScale: 1,
      selectionFrame: null,
      isEditingText: false,
      extDropTarget: null,
      previewStyles: null,
      layerDragPreviewPages: null,
      layerDraggingId: null,
      layerDragKind: 'section',
      clipboard: null,
      styleClipboard: null,
      enteredContainer: null,
      enteredComponentInstance: null,
      inspectorEditing: false,
      editOpenId: null,
      codeEditId: null,
      activeInteractionState: 'normal',

      // Actions
      setActivePage: (id) =>
        set({ activePage: id, activeElement: null, activePageLayer: false, enteredContainer: null, enteredComponentInstance: null, editOpenId: null, activeInteractionState: 'normal' }, false, 'setActivePage'),
      setActiveCmsCollectionId: (id) => set({ activeCmsCollectionId: id }, false, 'setActiveCmsCollectionId'),
      setActiveCmsItemId: (id) => set({ activeCmsItemId: id }, false, 'setActiveCmsItemId'),
      setAiPanelOpen: (open) => set({ aiPanelOpen: open }, false, 'setAiPanelOpen'),
      setAiPendingPrompt: (text) => set({ aiPendingPrompt: text }, false, 'setAiPendingPrompt'),
      setCanvasScrolling: (scrolling) => set({ canvasScrolling: scrolling }, false, 'setCanvasScrolling'),
      setCanvasDragging: (dragging) => set({ canvasDragging: dragging }, false, 'setCanvasDragging'),
      setCanvasSpacingAdjusting: (adjusting) => set({ canvasSpacingAdjusting: adjusting }, false, 'setCanvasSpacingAdjusting'),
      setSectionGapY: (y) => set({ sectionGapY: y }, false, 'setSectionGapY'),
      setEditOpenId: (id) => set({ editOpenId: id }, false, 'setEditOpenId'),
      setCodeEditId: (id) => set({ codeEditId: id }, false, 'setCodeEditId'),
      setActiveInteractionState: (state) => set({ activeInteractionState: state }, false, 'setActiveInteractionState'),

      setActiveFaqItem: (id) => set({ activeFaqItem: id }, false, 'setActiveFaqItem'),

      setActiveElement: (requested) =>
        set(
          (state) => {
          /* La foto de fondo es parte de su caja, no una capa: pedir elegirla
             elige la caja (ver `lib/backgroundLayer.ts`). Así no aparece un
             panel de imagen cuyos ajustes de tamaño no mandan sobre nada. */
          const page = requested ? state.pages.find((item) => item.id === state.activePage) : undefined
          const asked = page && requested ? findNodeById(page.elements, requested) : undefined
          const id = asked && isBackgroundImage(asked) ? findParentElement(page!.elements, asked.id)?.id ?? requested : requested
          return ({
            activeElement: id,
            activePageLayer: false,
            selectedElements: [],
            // Clicking outside (null) exits all containers
            enteredContainer: id === null ? null : state.enteredContainer,
            // Y sale de la reutilizable: el modo es donde esta el cursor, asi
            // que soltar la seleccion es salir.
            enteredComponentInstance: id === null ? null : state.enteredComponentInstance,
            // Y cierra el asistente: soltar la selección es empezar de cero, y
            // dejarlo abierto sobre un inspector vacío contradecía al raíl, que
            // ahí se apaga entero. Seleccionar OTRO elemento no lo cierra —eso
            // es justamente darle contexto—.
            aiPanelOpen: id === null ? false : state.aiPanelOpen,
          })
          },
          false,
          'setActiveElement'
        ),

      setActivePageLayer: (active) =>
        set({
          activePageLayer: active,
          activeElement: null,
          selectedElements: [],
          enteredContainer: null,
          enteredComponentInstance: null,
        }, false, 'setActivePageLayer'),

      setEnteredContainer: (id) =>
        set({ enteredContainer: id }, false, 'setEnteredContainer'),

      setEnteredComponentInstance: (id) =>
        set({ enteredComponentInstance: id }, false, 'setEnteredComponentInstance'),

      setHoveredElement: (id) =>
        set({ hoveredElement: id }, false, 'setHoveredElement'),

      toggleElementSelection: (id) =>
        set(
          (state) => {
            const current = state.selectedElements
            // First shift+click: anchor current active + this id
            if (current.length === 0) {
              const active = state.activeElement
              if (!active || active === id) return {}
              return { selectedElements: [active, id], activeElement: id, activePageLayer: false }
            }
            // Toggle off
            if (current.includes(id)) {
              const next = current.filter((x) => x !== id)
              return {
                selectedElements: next,
                activeElement: next.length > 0 ? (next[next.length - 1] ?? state.activeElement) : state.activeElement,
                activePageLayer: false,
              }
            }
            // Toggle on
            return { selectedElements: [...current, id], activeElement: id, activePageLayer: false }
          },
          false,
          'toggleElementSelection'
        ),

      setBreakpoint: (breakpoint) =>
        set({ breakpoint }, false, 'setBreakpoint'),

      setEditsAllSizes: (editsAllSizes) =>
        set({ editsAllSizes }, false, 'setEditsAllSizes'),

      setZoom: (zoom) =>
        set({ zoom: Math.min(4, Math.max(0.1, zoom)) }, false, 'setZoom'),
      setFitScale: (fitScale) =>
        set({ fitScale: fitScale > 0 ? fitScale : 1 }, false, 'setFitScale'),
      setZoomMode: (mode) => set({ zoomMode: mode }, false, 'setZoomMode'),
      setManualWidthPx: (px) =>
        set({ manualWidthPx: Math.min(5000, Math.max(1280, Math.round(px))) }, false, 'setManualWidthPx'),

      pulseInspectorEditing: () => {
        set({ inspectorEditing: true }, false, 'pulseInspectorEditing')
        if (inspectorEditingTimer) clearTimeout(inspectorEditingTimer)
        inspectorEditingTimer = setTimeout(() => {
          inspectorEditingTimer = null
          set({ inspectorEditing: false }, false, 'pulseInspectorEditing/end')
        }, 650)
      },

      createComponentFromElement: (pageId, elementId, name) => {
        const state = get()
        const page = state.pages.find((item) => item.id === pageId)
        const source = page ? findNodeById(page.elements, elementId) : undefined
        if (!source) return null
        const componentId = crypto.randomUUID()
        /* El original se guarda con los ids que ya tenía: son los que van a ver
           todas las instancias, y cambiarlos aquí obligaría a reescribir la
           página de la que salió. */
        const component: ProjectComponent = {
          id: componentId,
          name: name.trim() || 'Sección',
          node: source,
          updatedAt: new Date().toISOString(),
        }
        set((current) => {
          useHistoryStore.getState().pushSnapshot(current.pages)
          return {
            projectContent: {
              ...current.projectContent,
              components: [...(current.projectContent.components ?? []), component],
            },
            /* Y en la página queda la cáscara: sin hijos, porque a partir de
               ahora los pone el original. Dejarlos duplicaría la verdad justo
               en el sitio donde acabamos de decidir que sólo hay una. */
            pages: current.pages.map((item) => (
              item.id !== pageId
                ? item
                : { ...item, elements: updateNodeInTree(item.elements, elementId, { componentId, children: [] }) }
            )),
          }
        }, false, 'createComponentFromElement')
        return componentId
      },

      renameComponent: (componentId, name) =>
        set(
          (state) => ({
            projectContent: {
              ...state.projectContent,
              components: (state.projectContent.components ?? []).map((component) => (
                component.id === componentId
                  ? { ...component, name: name.trim() || component.name, updatedAt: new Date().toISOString() }
                  : component
              )),
            },
          }),
          false,
          'renameComponent',
        ),

      deleteComponent: (componentId) =>
        set(
          (state) => {
            const master = (state.projectContent.components ?? []).find((item) => item.id === componentId)
            if (!master) return {}
            useHistoryStore.getState().pushSnapshot(state.pages)
            /* Cada instancia se queda con el contenido y con ids nuevos: dos que
               vinieran del mismo original traerían los mismos, y a partir de ahí
               seleccionar una seleccionaría las dos. */
            const loosen = (nodes: ElementNode[]): ElementNode[] => nodes.map((node) => (
              node.componentId === componentId
                ? { ...regenIds({ ...node, children: master.node.children }), componentId: undefined }
                : node.children.length
                  ? { ...node, children: loosen(node.children) }
                  : node
            ))
            return {
              pages: state.pages.map((page) => ({ ...page, elements: loosen(page.elements) })),
              projectContent: {
                ...state.projectContent,
                components: (state.projectContent.components ?? []).filter((item) => item.id !== componentId),
              },
            }
          },
          false,
          'deleteComponent',
        ),

      setSiteSlot: (slot, node) =>
        set(
          (current) => {
            useHistoryStore.getState().pushSnapshot(current.pages)
            const others = (current.projectContent.components ?? []).filter((component) => component.slot !== slot)
            /* En automático al nacer: una barra o un pie nuevos se llenan solos
               con las páginas, el catálogo y el contacto (ver `autoSiteChrome`).
               Al reemplazar uno se respeta lo que tenía el anterior, y lo que
               traiga el propio nodo manda sobre los dos. */
            const previous = (current.projectContent.components ?? []).find((component) => component.slot === slot)
            /* El banner no: no tiene menú ni contacto que rellenar. */
            const withAuto = node && slot !== 'banner' ? { ...node, autoContent: node.autoContent ?? previous?.node.autoContent ?? true } : node
            const component: ProjectComponent | null = withAuto
              ? { id: crypto.randomUUID(), name: slot === 'nav' ? 'Nav' : slot === 'footer' ? 'Footer' : 'Banner', node: withAuto, slot, updatedAt: new Date().toISOString() }
              : null
            return {
              projectContent: {
                ...current.projectContent,
                components: component ? [...others, component] : others,
              },
            }
          },
          false,
          'setSiteSlot',
        ),

      setPageLayout: (pageId, layout) =>
        set(
          (current) => {
            useHistoryStore.getState().pushSnapshot(current.pages)
            return {
              pages: current.pages.map((page) => {
                if (page.id !== pageId) return page
                const next: PageLayout = { ...(page.layout ?? {}), ...layout }
                /* «Enseñar» es lo de siempre: no se guarda, para que una página
                   sin tocar siga sin `layout`. */
                if (next.nav === 'show') delete next.nav
                if (next.footer === 'show') delete next.footer
                const { layout: _old, ...rest } = page
                return Object.keys(next).length ? { ...rest, layout: next } : rest
              }),
            }
          },
          false,
          'setPageLayout',
        ),

      updateComponent: (componentId, node) =>
        set(
          (current) => {
            useHistoryStore.getState().pushSnapshot(current.pages)
            return {
              projectContent: {
                ...current.projectContent,
                components: (current.projectContent.components ?? []).map((component) => (
                  component.id === componentId
                    ? { ...component, node, updatedAt: new Date().toISOString() }
                    : component
                )),
              },
            }
          },
          false,
          'updateComponent',
        ),

      detachComponentInstance: (pageId, elementId) =>
        set(
          (current) => {
            const page = current.pages.find((item) => item.id === pageId)
            const instance = page ? findNodeById(page.elements, elementId) : undefined
            if (!instance?.componentId) return {}
            useHistoryStore.getState().pushSnapshot(current.pages)
            const loose = detachInstance(instance, current.projectContent)
            /* Ids nuevos para lo que entra: dos instancias desvinculadas del
               mismo original traerían los mismos, y a partir de ahí seleccionar
               una seleccionaría las dos. */
            const fresh = { ...loose, children: loose.children.map(regenIds) }
            return {
              pages: current.pages.map((item) => (
                item.id !== pageId
                  ? item
                  : { ...item, elements: updateNodeInTree(item.elements, elementId, fresh) }
              )),
            }
          },
          false,
          'detachComponentInstance',
        ),

      updateElement: (pageId, elementId, rawUpdates) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            const current = findNodeAnywhere(state, pageId, elementId)
            const withRadius = withMediaRadiusOverride(current, rawUpdates, pageId)
            /* Escribir el dato de una tarjeta de contacto «por completar» la da
               por completada: vuelve a salir publicada. */
            const completed = current?.contactPending && ('content' in rawUpdates || 'interaction' in rawUpdates)
              ? { ...withRadius, contactPending: undefined }
              : withRadius
            /* Y su enlace sale de lo escrito: el teléfono llama, el correo abre
               el correo, la dirección el mapa. Antes se quedaba en `#`. Sólo si
               el enlace era el automático —«por completar», o el que salía del
               dato anterior—: uno puesto a mano no se pisa. */
            const autoLink = current?.contactSlot && 'content' in rawUpdates && !('interaction' in rawUpdates)
              ? contactSlotHref(current.contactSlot, plainContactText(rawUpdates.content))
              : null
            const linkWasAuto = Boolean(current?.contactPending)
              || (current?.interaction?.href !== undefined && current.interaction.href === contactSlotHref(current.contactSlot, plainContactText(current.content)))
            const updates = autoLink && linkWasAuto
              ? { ...completed, interaction: { ...(current?.interaction ?? {}), action: 'link' as const, href: autoLink } }
              : completed
            // A form part has no node of its own: the edit is folded into the
            // host's form model, which is what the renderer reads.
            const partId = parseFormPartId(elementId)
            const reviewPartId2 = partId ? null : parseReviewPartId(elementId)
            const faqPart = partId || reviewPartId2 ? null : parseFaqPartId(elementId)
            const resolve = (elements: ElementNode[]): ElementNode[] => {
              if (partId) {
                const host = findNodeById(elements, partId.nodeId)
                const patch = host ? applyFormPartUpdate(host, partId.part, updates) : null
                return patch ? updateNodeInTree(elements, partId.nodeId, patch) : elements
              }
              if (reviewPartId2) {
                const host = findNodeById(elements, reviewPartId2.nodeId)
                const patch = host ? applyReviewPartUpdate(host, reviewPartId2.part, updates) : null
                return patch ? updateNodeInTree(elements, reviewPartId2.nodeId, patch) : elements
              }
              if (faqPart) {
                const host = findNodeById(elements, faqPart.nodeId)
                const patch = host ? applyFaqPartUpdate(host, faqPart.part, updates, state.activeFaqItem) : null
                return patch ? updateNodeInTree(elements, faqPart.nodeId, patch) : elements
              }
              return updateNodeInTree(elements, elementId, updates)
            }

            /* Editar dentro de una sección reutilizable edita el ORIGINAL, y
               con eso cambian todas sus instancias: no hay nada que propagar
               porque todas leen de ahí. Las partes de formulario y de reseña van
               antes y siempre contra la página, que es donde vive su anfitrión. */
            const routed = partId || reviewPartId2 || faqPart
              ? null
              : applyToOwningTree(state, pageId, elementId, (nodes) => updateNodeInTree(nodes, elementId, updates))
            if (routed) return routed

            return {
              pages: state.pages.map((page) =>
                page.id !== pageId ? page : { ...page, elements: resolve(page.elements) }
              ),
            }
          },
          false,
          'updateElement'
        ),

      addElement: (pageId, parentId, added) => {
        /* Con padre, siempre va dentro de algo: ver `withoutOwnSectionBackground`. */
        const element = parentId ? withoutOwnSectionBackground(added) : added
        /* Dentro de una galería (o un bloque que dibuja su interior) no: va
           justo después de él. Ver `holdsNoChildren`. */
        if (parentId && holdsNoChildren(findNodeById(owningTreeElements(get(), pageId, parentId), parentId))) {
          get().insertElementAt(pageId, element, parentId, 'after')
          return
        }
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            /* Dentro de una sección reutilizable se añade AL ORIGINAL, igual que
               ya hacían mover, reordenar, editar y borrar.

               Sin esto, soltar algo dentro de una instancia escribía en el árbol
               de la página buscando un padre que vive en el original: no lo
               encontraba y no pasaba nada. El elemento se «añadía» y no aparecía
               en ninguna parte. */
            /* Y tampoco se añaden ahí: un panel suelto sin su botón no se
               puede abrir. Se agregan desde los ajustes del bloque. */
            const host = state.pages.find((item) => item.id === pageId)
            if (host && parentId && isWidgetManagedParent(findNodeById(host.elements, parentId))) return {}
            if (parentId) {
              /* Primero lo de la instancia: su id SÍ está en la página, así que
                 `applyToOwningTree` escribiría ahí y el elemento no se vería. */
              const inside = addInsideInstance(state, pageId, parentId, (kids) => [...kids, element])
              if (inside) return inside
              const routed = applyToOwningTree(
                state,
                pageId,
                parentId,
                (nodes) => addNodeToTree(nodes, parentId, element),
              )
              if (routed) return routed
            }
            return {
              pages: state.pages.map((page) =>
                page.id !== pageId
                  ? page
                  /* Una sección nueva en la raíz alterna con la de arriba:
                     Principal · Secundaria · Principal… */
                  : { ...page, elements: alternateNewSection(addNodeToTree(page.elements, parentId, element), element.id) }
              ),
            }
          },
          false,
          'addElement'
        )
      },

      moveElement: (pageId, elementId, targetId, position) =>
        set(
          (state) => {
            /* Soltar DENTRO de una galería es soltar después de ella. */
            if (position === 'inside' && holdsNoChildren(findNodeById(owningTreeElements(state, pageId, targetId), targetId))) position = 'after'
            /* Un fijo no se mueve, y nada se suelta antes de él (ni entre dos). */
            const movePage = state.pages.find((p) => p.id === pageId)?.elements ?? []
            if (findNodeById(movePage, elementId)?.pinned) return {}
            if (position !== 'inside') {
              const place = siblingsOf(movePage, targetId)
              if (place && pinnedBlocksMove(movePage, '', { parentChildren: place.children, index: position === 'before' ? place.index : place.index + 1 })) return {}
            }
            /* La barra y el pie del sitio tienen su sitio fijo —arriba y abajo
               de todas las páginas— y no se mueven, ni se suelta nada «antes» o
               «después» de ellos: mezclaría la página con el sitio. Dentro sí. */
            if (isSiteSlotRoot(state.projectContent, elementId)) return {}
            if (position !== 'inside' && isSiteSlotRoot(state.projectContent, targetId)) {
              /* Junto a la barra es el principio de la página; junto al pie, el
                 final. Es lo que se ve en el lienzo al soltar ahí. */
              const pageNow = state.pages.find((p) => p.id === pageId)
              if (!pageNow || !findNodeById(pageNow.elements, elementId)) return {}
              useHistoryStore.getState().pushSnapshot(state.pages)
              const atEnd = siteSlotComponent(state.projectContent, 'footer')?.node.id === targetId
              return {
                pages: state.pages.map((p) => (p.id !== pageId ? p : {
                  ...p,
                  elements: moveNodeToParentIndex(p.elements, elementId, null, atEnd ? p.elements.length : 0),
                })),
              }
            }
            const elements = state.pages.find((p) => p.id === pageId)?.elements ?? []
            // Pinned behavior nodes (wb-* schemaId) can be reordered, but never leave their parent.
            const node = findNodeById(elements, elementId)
            if (node?.schemaId?.startsWith('wb-')) {
              const currentParent = findParentElement(elements, elementId)?.id ?? null
              const targetParent = position === 'inside' ? targetId : (findParentElement(elements, targetId)?.id ?? null)
              if (targetParent !== currentParent) return {}
            }
            useHistoryStore.getState().pushSnapshot(state.pages)
            /* Y mover, igual. Con una salvedad: mover algo de dentro de un
               componente a la página —o al revés— sacaría el nodo de un árbol y
               lo metería en otro, y `applyToOwningTree` sólo sabe de uno. Se
               resuelve contra el árbol de ORIGEN, así que un arrastre que
               cruzara la frontera no encuentra el destino y no hace nada, en
               vez de partir la sección por la mitad. */
            return applyToOwningTree(
              state,
              pageId,
              elementId,
              (nodes) => moveNodeInTree(nodes, elementId, targetId, position),
            ) ?? {}
          },
          false,
          'moveElement'
        ),

      moveElementToIndex: (pageId, elementId, parentId, index) =>
        set(
          (state) => {
            const elements = state.pages.find((p) => p.id === pageId)?.elements ?? []
            const destination = parentId ? findNodeById(elements, parentId)?.children ?? [] : elements
            if (pinnedBlocksMove(elements, elementId, { parentChildren: destination, index })) return {}
            const node = findNodeById(elements, elementId)
            if (node?.schemaId?.startsWith('wb-')) {
              const currentParent = findParentElement(elements, elementId)?.id ?? null
              if (parentId !== currentParent) return {} // pinned: can't leave its container
            }
            useHistoryStore.getState().pushSnapshot(state.pages)
            return {
              activeElement: elementId,
              activePageLayer: false,
              selectedElements: [],
              enteredContainer: parentId,
              pages: state.pages.map((page) =>
                page.id !== pageId
                  ? page
                  : { ...page, elements: moveNodeToParentIndex(page.elements, elementId, parentId, index) }
              ),
            }
          },
          false,
          'moveElementToIndex'
        ),

      insertElementAt: (pageId, inserted, targetId, position) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            const host = state.pages.find((item) => item.id === pageId)
            const nested = position === 'inside' || !host?.elements.some((node) => node.id === targetId)
            const element = nested ? withoutOwnSectionBackground(inserted) : inserted
            /* Dentro de una galería no: después de ella. */
            if (position === 'inside' && holdsNoChildren(findNodeById(owningTreeElements(state, pageId, targetId), targetId))) position = 'after'
            /* Ni junto a uno: insertar «después del panel 2» crea un panel 3
               sin botón. Se agregan desde los ajustes del bloque. */
            const target = state.pages.find((item) => item.id === pageId)
            if (target && isWidgetManagedChild(target.elements, targetId)) return {}
            /* Antes o después de la barra o el pie del sitio: al principio o al
               final de la PÁGINA. Sin esto se insertaba junto a la raíz dentro
               del árbol del componente, que sólo guarda uno, y se perdía. */
            if (position !== 'inside' && isSiteSlotRoot(state.projectContent, targetId)) {
              const atEnd = siteSlotComponent(state.projectContent, 'footer')?.node.id === targetId
              return {
                pages: state.pages.map((page) => (page.id !== pageId ? page : {
                  ...page,
                  elements: alternateNewSection(belowPinnedBlocks(atEnd ? [...page.elements, element] : [element, ...page.elements], element.id), element.id),
                })),
              }
            }
            /* «Dentro de» una instancia es dentro del ORIGINAL. Antes y después
               no: ahí el vecino es la instancia entera, y eso sí es de la
               página. */
            if (position === 'inside') {
              const inside = addInsideInstance(state, pageId, targetId, (kids) => [...kids, element])
              if (inside) return inside
            }
            /* Igual que `addElement`: si el destino vive dentro de una sección
               reutilizable, se inserta en el original. */
            const routed = applyToOwningTree(
              state,
              pageId,
              targetId,
              (nodes) => belowPinnedBlocks(insertNodeAtTarget(nodes, element, targetId, position), element.id),
            )
            if (routed) return routed
            return {
              pages: state.pages.map((page) =>
                page.id !== pageId
                  ? page
                  : { ...page, elements: alternateNewSection(belowPinnedBlocks(insertNodeAtTarget(page.elements, element, targetId, position), element.id), element.id) }
              ),
            }
          },
          false,
          'insertElementAt'
        ),

      setSelectionFrame: (frame) => set({ selectionFrame: frame }, false, 'setSelectionFrame'),
      setIsEditingText: (editing) => set({ isEditingText: editing }, false, 'setIsEditingText'),
      setExtDropTarget: (target) => set({ extDropTarget: target }, false, 'setExtDropTarget'),

      setLayerDragPreview: (elementId, target) =>
        set((state) => {
          if (!target) return { layerDragPreviewPages: null }
          const pageId = state.activePage
          const elements = state.pages.find((p) => p.id === pageId)?.elements ?? []
          const node = findNodeById(elements, elementId)
          // Pinned (wb-) elements can't leave their container — show them in place.
          if (node?.schemaId?.startsWith('wb-')) {
            const currentParent = findParentElement(elements, elementId)?.id ?? null
            if (target.parentId !== currentParent) return { layerDragPreviewPages: null }
          }
          return {
            layerDragPreviewPages: state.pages.map((page) =>
              page.id !== pageId
                ? page
                : { ...page, elements: moveNodeToParentIndex(page.elements, elementId, target.parentId, target.index) }
            ),
          }
        }, false, 'setLayerDragPreview'),
      clearLayerDragPreview: () => set({ layerDragPreviewPages: null }, false, 'clearLayerDragPreview'),
      setLayerDraggingId: (id, kind) => set((state) => ({ layerDraggingId: id, layerDragKind: kind ?? state.layerDragKind }), false, 'setLayerDraggingId'),

      setPreviewStyles: (patch) => set({ previewStyles: patch }, false, 'setPreviewStyles'),

      copyElement: (elementId) =>
        set(
          (state) => {
            /* La mitad de un widget sube a su bloque: copiar el botón de una
               ventana daba un botón que no abría nada. Ver `copyableRoot`. */
            const node = copyableRoot(selectActivePageElements(state), elementId)
            if (!node) return {}
            return { clipboard: JSON.parse(JSON.stringify(node)) as ElementNode }
          },
          false,
          'copyElement'
        ),

      setClipboardElement: (element) =>
        set(
          { clipboard: element ? JSON.parse(JSON.stringify(element)) as ElementNode : null },
          false,
          'setClipboardElement'
        ),

      copyElementStyle: (elementId) =>
        set(
          (state) => {
            const node = findNodeById(selectActivePageElements(state), elementId)
              ?? (state.activePage ? findNodeAnywhere(state, state.activePage, elementId) : undefined)
            if (!node) return {}
            const clip: StyleClipboard = {
              styles: pickAppearanceStyles(node.styles),
              ...(node.iconSize !== undefined ? { iconSize: node.iconSize } : {}),
              ...(node.styleRefs ? { styleRefs: JSON.parse(JSON.stringify(node.styleRefs)) as ElementStyleRefs } : {}),
              ...(node.themeRole ? { themeRole: node.themeRole } : {}),
              ...(node.backgroundRole ? { backgroundRole: node.backgroundRole } : {}),
            }
            return { styleClipboard: clip }
          },
          false,
          'copyElementStyle'
        ),

      pasteElementStyle: (pageId, elementIds) =>
        set(
          (state) => {
            const clip = state.styleClipboard
            if (!clip || elementIds.length === 0) return {}
            useHistoryStore.getState().pushSnapshot(state.pages)
            let result = { pages: state.pages, projectContent: state.projectContent }
            for (const id of elementIds) {
              const node = findNodeAnywhere(result, pageId, id)
              if (!node) continue
              // Replace role / refs with the source's (not merge): a leftover themeRole
              // would strip pasted typography, and stale color tokens would override
              // pasted literal colors. `undefined` clears the field.
              const updates: Partial<ElementNode> = {
                styles: mergeAppearanceStyles(node.styles, clip.styles),
                themeRole: clip.themeRole,
                backgroundRole: clip.backgroundRole,
                ...(node.type === 'icon' || clip.iconSize !== undefined ? { iconSize: clip.iconSize } : {}),
                styleRefs: {
                  ...(clip.styleRefs?.typography !== undefined ? { typography: clip.styleRefs.typography } : {}),
                  ...(clip.styleRefs?.button !== undefined ? { button: clip.styleRefs.button } : {}),
                  ...(clip.styleRefs?.card !== undefined ? { card: clip.styleRefs.card } : {}),
                  ...(clip.styleRefs?.section !== undefined ? { section: clip.styleRefs.section } : {}),
                  ...(clip.styleRefs?.icon !== undefined ? { icon: clip.styleRefs.icon } : {}),
                  ...(clip.styleRefs?.overrides ? { overrides: [...clip.styleRefs.overrides] } : {}),
                  colors: { ...(clip.styleRefs?.colors ?? {}) },
                },
              }
              const routed = applyToOwningTree(result, pageId, id, (nodes) => updateNodeInTree(nodes, id, updates))
              if (routed) result = { ...result, ...routed }
            }
            return result
          },
          false,
          'pasteElementStyle'
        ),

      pasteElement: (pageId) =>
        set(
          (state) => {
            if (!state.clipboard) return {}
            useHistoryStore.getState().pushSnapshot(state.pages)
            /* Saneado antes de entrar: un portapapeles de otra sesión —o de
               antes de que copiar subiera al bloque— puede traer una mitad
               suelta, y con su `schemaId` intacto rompería la ventana en la que
               se pegue. Ver `stripOrphanWidgetParts`. */
            const newNode = stripOrphanWidgetParts(regenIds(state.clipboard))
            const afterId = state.activeElement
            // If the active element is a container, paste inside it
            const CONTAINER_TYPES: ElementType[] = ['section', 'div']
            /* Buscado en los dos árboles. Con algo de dentro de una sección
               reutilizable seleccionado, buscar sólo en la página devolvía nada:
               se pegaba «después» de un id que la página no tiene, o sea en
               ninguna parte. */
            const activeNode = afterId ? findNodeAnywhere(state, pageId, afterId) : null
            const position: 'inside' | 'after' =
              activeNode && CONTAINER_TYPES.includes(activeNode.type) ? 'inside' : 'after'
            const selected = { activeElement: newNode.id, activePageLayer: false }
            if (afterId) {
              /* Con la instancia elegida, dentro del original. Va antes que lo
                 demás por lo mismo que en `addElement`. */
              const inside = position === 'inside'
                ? addInsideInstance(state, pageId, afterId, (kids) => [...kids, newNode])
                : null
              if (inside) return { ...inside, ...selected }
              /* Y con algo de dentro elegido, al lado suyo — que también es el
                 original. */
              const routed = applyToOwningTree(
                state,
                pageId,
                afterId,
                (nodes) => insertNodeAtTarget(nodes, newNode, afterId, position),
              )
              if (routed) return { ...routed, ...selected }
            }
            return {
              pages: state.pages.map((page) =>
                page.id !== pageId
                  ? page
                  : { ...page, elements: [...page.elements, newNode] }
              ),
              ...selected,
            }
          },
          false,
          'pasteElement'
        ),

      /**
       * Un panel y su botón se crean y se borran JUNTOS.
       *
       * Son una lista con dos mitades que tienen que cuadrar: un panel de más
       * sin su botón no se puede abrir, y un botón sin panel abre el vacío. En
       * el lienzo el borrado también pasa por esta misma operación en pareja.
       *
       * El nuevo es una COPIA del último, no una plantilla de fábrica: hereda el
       * diseño que se le haya dado a los demás. Uno recién nacido con los
       * estilos originales obligaría a rehacerlo cada vez.
       */
      addTabsPanel: (pageId, hostId) =>
        set(
          (state) => {
            const elements = owningTreeElements(state, pageId, hostId)
            const host = findNodeById(elements, hostId)
            if (!host) return {}
            const track = findBySchema(host, 'wb-tabs-track')
            const nav = findBySchema(host, 'wb-tabs-nav')
            if (!track || !nav) return {}
            const lastPanel = track.children.at(-1) ?? {
              id: '', type: 'div' as const, children: [],
              styles: { desktop: { display: 'flex', flexDirection: 'column', gap: '16px', minHeight: '160px' } },
            }
            const lastTab = nav.children.at(-1) ?? {
              id: '', type: 'button' as const, children: [], content: 'Panel 1',
              styleRefs: { button: 'button-link' }, styles: { desktop: {} },
            }
            useHistoryStore.getState().pushSnapshot(state.pages)
            const n = track.children.length + 1
            /* Sin el `schemaId` del original: se usa para encontrar piezas
               concretas, y dos con el mismo nombre dejan de identificar nada. */
            /* Con `exactOptionalPropertyTypes`, poner la clave a `undefined` no
               es lo mismo que no tenerla: hay que quitarla. */
            const { schemaId: _p, ...panelRest } = regenIds(lastPanel)
            const { schemaId: _t, ...tabRest } = regenIds(lastTab)
            const panel: ElementNode = { ...panelRest, label: `Panel ${n}` }
            const tab: ElementNode = { ...tabRest, label: `Tab ${n}` }
            return {
              ...applyToOwningTree(state, pageId, hostId, (nodes) => updateNodeInTree(
                updateNodeInTree(nodes, track.id, { children: [...track.children, panel] }),
                nav.id,
                { children: [...nav.children, tab] },
              )),
              activeElement: panel.id,
            }
          },
          false,
          'addTabsPanel'
        ),

      removeTabsPanel: (pageId, hostId, index) =>
        set(
          (state) => {
            const elements = owningTreeElements(state, pageId, hostId)
            const host = findNodeById(elements, hostId)
            const track = host ? findBySchema(host, 'wb-tabs-track') : null
            const nav = host ? findBySchema(host, 'wb-tabs-nav') : null
            if (!track || !nav) return {}
            if (index < 0 || index >= track.children.length) return {}
            useHistoryStore.getState().pushSnapshot(state.pages)
            return {
              ...applyToOwningTree(state, pageId, hostId, (nodes) => updateNodeInTree(
                updateNodeInTree(nodes, track.id, {
                  children: track.children.filter((_, at) => at !== index),
                }),
                nav.id,
                { children: nav.children.filter((_, at) => at !== index) },
              )),
              activeElement: hostId,
              selectedElements: [],
            }
          },
          false,
          'removeTabsPanel'
        ),

      duplicateElement: (pageId, elementId) =>
        set(
          (state) => {
            const elements = state.pages.find((p) => p.id === pageId)?.elements ?? []
            const node = findNodeById(elements, elementId)
            if (!node || node.pinned) return {}
            useHistoryStore.getState().pushSnapshot(state.pages)
            /* Sin el ancla: dos secciones con el mismo `#id` son una sola para
               el navegador —un enlace sólo llega a la primera— y en el selector
               de destinos salían repetidas, todas marcadas. La copia estrena
               una propia cuando alguien la enlace. */
            const withoutAnchors = (item: ElementNode): ElementNode => {
              const { anchorId: _anchor, ...rest } = item
              return { ...rest, children: item.children.map(withoutAnchors) }
            }
            const copy = withoutAnchors(regenIds(node))
            return {
              pages: state.pages.map((page) =>
                page.id !== pageId
                  ? page
                  : { ...page, elements: insertNodeAtTarget(page.elements, copy, elementId, 'after') }
              ),
              activeElement: copy.id,
              activePageLayer: false,
            }
          },
          false,
          'duplicateElement'
        ),

      reorderElement: (pageId, elementId, direction, steps = 1) =>
        set(
          (state) => {
            const pageElements = state.pages.find((p) => p.id === pageId)?.elements ?? []
            const place = siblingsOf(pageElements, elementId)
            if (place) {
              const to = direction === 'up' ? place.index - steps : place.index + steps
              /* Ni el fijo se mueve ni otro sube por encima de él. */
              if (pinnedBlocksMove(pageElements, elementId) || (direction === 'up' && pinnedBlocksMove(pageElements, '', { parentChildren: place.children, index: to }))) return {}
              if (direction === 'down' && place.children.slice(place.index + 1, to + 1).some((child) => child.pinned)) return {}
            }
            useHistoryStore.getState().pushSnapshot(state.pages)
            /* Reordenar dentro de una sección reutilizable reordena el original:
               el orden es parte de la sección, no de la página donde se ve. */
            return applyToOwningTree(
              state,
              pageId,
              elementId,
              (nodes) => reorderNodeInTree(nodes, elementId, direction, steps),
            ) ?? {}
          },
          false,
          'reorderElement'
        ),

      deleteElement: (pageId, elementId) => {
        const current = get()
        const elements = owningTreeElements(current, pageId, elementId)
        if (findNodeById(elements, elementId)?.pinned) return
        /* En un carrusel se borra la DIAPOSITIVA entera, no capa por capa: lo
           último que queda dentro de una diapositiva se la lleva con ella, y
           la última diapositiva se lleva el carrusel —un carrusel vacío sólo
           deja una caja sin nada que pasar—. */
        const wider = componentDeletionTarget(elements, elementId)
        if (wider !== elementId) {
          current.deleteElement(pageId, wider)
          /* Lo que se pidió borrar iba dentro: que no quede elegido. */
          set((state) => ({
            selectedElements: state.selectedElements.filter((id) => id !== elementId),
            activeElement: state.activeElement === elementId ? null : state.activeElement,
          }), false, 'deleteElement')
          return
        }
        const parent = findParentElement(elements, elementId)
        if (isWidgetManagedParent(parent)) {
          let host = parent
          while (host && host.behavior?.type !== 'tabs') host = findParentElement(elements, host.id)
          if (host && parent) {
            current.removeTabsPanel(pageId, host.id, parent.children.findIndex((child) => child.id === elementId))
            return
          }
        }
        set(
          (state) => {
            const page0 = state.pages.find((item) => item.id === pageId)
            const parent = findParentElement(owningTreeElements(state, pageId, elementId), elementId)
            const emptyMarquee = parent?.behavior?.type === 'marquee' && parent.children.length === 1 ? parent.id : null
            /* Un bloque fijo no se borra: ver `pinned`. */
            if (page0 && findNodeById(page0.elements, elementId)?.pinned) return {}
            useHistoryStore.getState().pushSnapshot(state.pages)
            /* Borrar la barra o el pie del sitio es quitarlos del sitio: no
               tienen padre donde borrarse, y dejar el componente vacío lo
               dejaría pintando una caja en todas las páginas. */
            if (isSiteSlotRoot(state.projectContent, elementId)) {
              return {
                projectContent: {
                  ...state.projectContent,
                  components: (state.projectContent.components ?? []).filter((component) => !(component.slot && component.node.id === elementId)),
                },
                activeElement: state.activeElement === elementId ? emptyMarquee : state.activeElement,
                selectedElements: state.selectedElements.filter((id) => id !== elementId),
              }
            }
            /* Borrar dentro de una sección reutilizable borra del ORIGINAL, y
               por tanto de todas las páginas: es lo que significa la palabra. */
            const routed = applyToOwningTree(state, pageId, elementId, (nodes) => deleteNodeFromTree(nodes, elementId))
            if (routed?.projectContent) {
              return {
                ...routed,
                activeElement: state.activeElement === elementId ? emptyMarquee : state.activeElement,
                selectedElements: state.selectedElements.filter((id) => id !== elementId),
              }
            }
            return {
              activeElement:
                state.activeElement === elementId ? emptyMarquee : state.activeElement,
              activePageLayer: false,
              selectedElements: state.selectedElements.filter((id) => id !== elementId),
              // Clear enteredContainer if the deleted element is or contains it
              enteredContainer:
                state.enteredContainer === elementId ? null : state.enteredContainer,
              pages: state.pages.map((page) =>
                page.id !== pageId
                  ? page
                  : { ...page, elements: deleteNodeFromTree(page.elements, elementId) }
              ),
            }
          },
          false,
          'deleteElement'
        )
      },

      groupElements: (pageId, ids) =>
        set(
          (state) => {
            if (ids.length < 2) return {}
            useHistoryStore.getState().pushSnapshot(state.pages)
            const wrapperId = crypto.randomUUID()
            return {
              pages: state.pages.map((page) =>
                page.id !== pageId
                  ? page
                  : { ...page, elements: wrapNodesInDiv(page.elements, ids, wrapperId) }
              ),
              activeElement: wrapperId,
              activePageLayer: false,
              selectedElements: [],
            }
          },
          false,
          'groupElements'
        ),

      wrapElement: (pageId, elementId) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            const wrapperId = crypto.randomUUID()
            return {
              pages: state.pages.map((page) =>
                page.id !== pageId
                  ? page
                  : { ...page, elements: wrapNodesInDiv(page.elements, [elementId], wrapperId) }
              ),
              activeElement: wrapperId,
              activePageLayer: false,
              selectedElements: [],
            }
          },
          false,
          'wrapElement'
        ),

      /**
       * Estilos a varios elementos a la vez.
       *
       * Va elemento a elemento y por `applyToOwningTree`, igual que
       * `updateElement`, porque lo seleccionado puede no estar en la página: un
       * hijo de una sección reutilizable vive en el ORIGINAL. Esto miraba sólo
       * el árbol de la página, no encontraba el nodo y seguía al siguiente —así
       * que editar dentro de un reutilizable con más de un elemento
       * seleccionado no hacía nada, ni avisaba: el cambio se descartaba y el
       * lienzo no se rehacía, que desde fuera se ve como si el editor se
       * hubiera quedado congelado.
       *
       * El estado se acumula entre vueltas: la selección puede repartirse entre
       * la página y dos componentes distintos, y cada escritura tiene que ver la
       * anterior o la última pisaría a las demás.
       */
      updateMultipleStyles: (pageId, ids, bp, patch) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            let pages = state.pages
            let projectContent = state.projectContent
            for (const id of ids) {
              const scope = { pages, projectContent }
              const node = findNodeAnywhere(scope, pageId, id)
              if (!node) continue
              const existing = bp === 'desktop' ? node.styles.desktop : (node.styles[bp] ?? {})
              const merged = { ...existing, ...patch }
              for (const key of Object.keys(merged) as (keyof StyleProperties)[]) {
                if (merged[key] === '') delete merged[key]
              }
              const update = withMediaRadiusOverride(node, { styles: { ...node.styles, [bp]: merged } }, pageId)
              const routed = applyToOwningTree(scope, pageId, id, (nodes) => (
                updateNodeInTree(nodes, id, update)
              ))
              if (!routed) continue
              if (routed.pages) pages = routed.pages
              if (routed.projectContent) projectContent = routed.projectContent
            }
            return { pages, projectContent }
          },
          false,
          'updateMultipleStyles'
        ),

      // A regular page — blank unless the creation wizard supplies a template.
      // No auto-generated CMS collection: CMS is created explicitly from its
      // workspace, so autosave persists just this page (+ order).
      openCollectionTemplatePage: (build, previewSlug) =>
        set(
          (state) => {
            /* La propia de esa colección, si la tiene; si no, la general con ésa de muestra. */
            const own = previewSlug ? state.pages.find((page) => isCollectionTemplatePage(page) && page.cmsDynamic?.forCollection === previewSlug) : undefined
            if (own) return { activePage: own.id, activeElement: null }
            const existing = state.pages.find((page) => isSharedCollectionTemplate(page))
            if (existing) {
              const withPreview = previewSlug && existing.cmsDynamic
                ? { ...existing, cmsDynamic: { ...existing.cmsDynamic, previewItemId: previewSlug } }
                : existing
              return {
                ...(withPreview !== existing ? { pages: state.pages.map((page) => (page.id === existing.id ? withPreview : page)) } : {}),
                activePage: existing.id,
                activeElement: null,
              }
            }
            useHistoryStore.getState().pushSnapshot(state.pages)
            const built = build()
            const page = previewSlug && built.cmsDynamic ? { ...built, cmsDynamic: { ...built.cmsDynamic, previewItemId: previewSlug } } : built
            return { pages: [...state.pages, page], activePage: page.id, activeElement: null }
          },
          false,
          'openCollectionTemplatePage'
        ),

      ownCollectionTemplatePage: (build, slug, title, elements) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            const current = state.pages.find((page) => isCollectionTemplatePage(page) && page.cmsDynamic?.forCollection === slug)
            if (current) {
              if (!elements) return { activePage: current.id, activeElement: null }
              return {
                pages: state.pages.map((page) => (page.id === current.id ? { ...page, elements, updatedAt: new Date().toISOString() } : page)),
                activePage: current.id,
                activeElement: null,
              }
            }
            const shared = state.pages.find((page) => isSharedCollectionTemplate(page)) ?? build()
            const now = new Date().toISOString()
            const own: Page = {
              ...shared,
              id: crypto.randomUUID(),
              name: title,
              /* Interna, como la general: el sitio la sirve en `/colecciones/<slug>`. */
              slug: ensureUniqueSlug(`/_collection/${slug}`, state.pages),
              cmsDynamic: { ...shared.cmsDynamic!, forCollection: slug, previewItemId: slug },
              elements: elements ?? withFreshNodeIds(shared.elements),
              createdAt: now,
              updatedAt: now,
            }
            const hasShared = state.pages.some((page) => isSharedCollectionTemplate(page))
            return {
              pages: [...state.pages, ...(hasShared ? [] : [shared]), own],
              activePage: own.id,
              activeElement: null,
            }
          },
          false,
          'ownCollectionTemplatePage'
        ),

      dropCollectionTemplatePage: (slug) =>
        set(
          (state) => {
            const own = state.pages.find((page) => isCollectionTemplatePage(page) && page.cmsDynamic?.forCollection === slug)
            if (!own) return {}
            useHistoryStore.getState().pushSnapshot(state.pages)
            const shared = state.pages.find((page) => isSharedCollectionTemplate(page))
            const pages = state.pages
              .filter((page) => page.id !== own.id)
              .map((page) => (shared && page.id === shared.id ? { ...page, cmsDynamic: { ...page.cmsDynamic!, previewItemId: slug } } : page))
            return { pages, activePage: shared?.id ?? state.activePage, activeElement: null }
          },
          false,
          'dropCollectionTemplatePage'
        ),

      addPage: (name, elements, options) =>
        set(
          (state) => {
            const pageName = name ?? 'Nueva página'
            const now = new Date().toISOString()
            const slug = options?.slug ?? ensureUniqueSlug(generateSlug(pageName), state.pages)
            const newPage: Page = {
              id: crypto.randomUUID(),
              name: pageName,
              slug,
              isHome: false,
              isSlugCustom: Boolean(options?.slug),
              elements: elements ?? [],
              // Brand tokens are project-wide. Keeping the same theme on the
              // page also makes previews/fallbacks consistent while loading.
              theme: state.projectTheme ?? DEFAULT_PAGE_THEME,
              pageStyles: DEFAULT_PAGE_STYLES,
              seoTitle: '',
              seoDescription: '',
              canonicalUrl: '',
              ogTitle: '',
              ogDescription: '',
              ogImage: null,
              accessMode: 'public',
              password: '',
              indexable: true,
              includeInSitemap: true,
              redirect301: null,
              customHeadCode: '',
              thumbnailUrl: null,
              createdAt: now,
              updatedAt: now,
            }
            return {
              pages: [...state.pages, newPage],
              activePage: newPage.id,
              activeElement: null,
            }
          },
          false,
          'addPage'
        ),

      replacePageElements: (pageId, elements) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            return {
              pages: state.pages.map((page) => (page.id === pageId ? { ...page, elements, updatedAt: new Date().toISOString() } : page)),
              activeElement: null,
            }
          },
          false,
          'replacePageElements'
        ),

      createCmsDynamicPage: (collectionId) =>
        set(
          (state) => {
            /* El blog puede no haberse cargado todavía (o no tener entradas):
               su plantilla se crea igual, con sus campos fijos. */
            const collection = state.projectContent.collections.find((item) => item.id === collectionId)
              ?? (collectionId === CMS_BLOG_SOURCE ? { id: CMS_BLOG_SOURCE, name: 'Blog', fields: CMS_BLOG_FIELDS, items: [] } : undefined)
            if (!collection) return {}
            const existing = state.pages.find((page) =>
              page.cmsDynamic?.collection === collectionId && page.cmsDynamic.kind !== 'collection'
            )
            if (existing) {
              return { activePage: existing.id, activeElement: null }
            }
            useHistoryStore.getState().pushSnapshot(state.pages)
            const now = new Date().toISOString()
            const baseSlug = `/${generateSlug(collection.name).replace(/^\//, '') || collection.id}/[slug]`
            const newPage: Page = {
              id: crypto.randomUUID(),
              name: collectionId === CMS_BLOG_SOURCE ? 'Blog post' : `${collection.name} Template`,
              slug: collectionId === CMS_BLOG_SOURCE ? '/blog/[slug]' : ensureUniqueSlug(baseSlug, state.pages),
              cmsDynamic: {
                kind: 'item',
                collection: collectionId,
                slugField: 'slug',
                ...(collection.items[0]?.id ? { previewItemId: collection.items[0].id } : {}),
              },
              isHome: false,
              isSlugCustom: true,
              elements: collectionId === CMS_BLOG_SOURCE
                ? makeBlogTemplateElements()
                : defaultCmsEntryElements(collection, state.pages.find((page) => page.cmsDynamic?.collection === collectionId && page.cmsDynamic.kind === 'collection')?.slug),
              theme: state.projectTheme ?? DEFAULT_PAGE_THEME,
              pageStyles: DEFAULT_PAGE_STYLES,
              seoTitle: '',
              seoDescription: '',
              canonicalUrl: '',
              ogTitle: '',
              ogDescription: '',
              ogImage: null,
              accessMode: 'public',
              password: '',
              indexable: true,
              includeInSitemap: true,
              redirect301: null,
              customHeadCode: '',
              thumbnailUrl: null,
              createdAt: now,
              updatedAt: now,
            }
            return {
              pages: [...state.pages, newPage],
              activePage: newPage.id,
              activeElement: null,
            }
          },
          false,
          'createCmsDynamicPage'
        ),

      createOfferTemplatePage: (kind) =>
        set(
          (state) => {
            const existing = state.pages.find((page) => isOfferTemplatePage(page, kind))
            if (existing) {
              const upgraded = withOfferDetails(existing)
              return {
                ...(upgraded !== existing ? { pages: state.pages.map((page) => (page.id === existing.id ? upgraded : page)) } : {}),
                activePage: existing.id,
                activeElement: null,
              }
            }
            useHistoryStore.getState().pushSnapshot(state.pages)
            const newPage = offerTemplatePageFor(kind, state.projectTheme ?? DEFAULT_PAGE_THEME)
            return { pages: [...state.pages, newPage], activePage: newPage.id, activeElement: null }
          },
          false,
          'createOfferTemplatePage'
        ),

      createCmsCollectionPage: (collectionId) =>
        set(
          (state) => {
            const collection = state.projectContent.collections.find((item) => item.id === collectionId)
              ?? (collectionId === CMS_BLOG_SOURCE ? { id: CMS_BLOG_SOURCE, name: 'Blog', fields: CMS_BLOG_FIELDS, items: [] } : undefined)
            if (!collection) return {}
            const existing = state.pages.find((page) =>
              page.cmsDynamic?.collection === collectionId && page.cmsDynamic.kind === 'collection'
            )
            if (existing) {
              return { activePage: existing.id, activeElement: null }
            }
            useHistoryStore.getState().pushSnapshot(state.pages)
            const now = new Date().toISOString()
            const baseSlug = `/${generateSlug(collection.name).replace(/^\//, '') || collection.id}`
            const dynamicPage = state.pages.find((page) =>
              page.cmsDynamic?.collection === collectionId && page.cmsDynamic.kind !== 'collection'
            )
            const newPage: Page = {
              id: crypto.randomUUID(),
              name: collection.name,
              slug: collectionId === CMS_BLOG_SOURCE ? '/blog' : ensureUniqueSlug(baseSlug, state.pages),
              cmsDynamic: {
                kind: 'collection',
                collection: collectionId,
                slugField: 'slug',
              },
              isHome: false,
              isSlugCustom: false,
              elements: collectionId === CMS_BLOG_SOURCE
                ? makeBlogFeaturedIndexElements()
                : defaultCmsListElements(collection, dynamicPage?.slug),
              theme: state.projectTheme ?? DEFAULT_PAGE_THEME,
              pageStyles: DEFAULT_PAGE_STYLES,
              seoTitle: '',
              seoDescription: '',
              canonicalUrl: '',
              ogTitle: '',
              ogDescription: '',
              ogImage: null,
              accessMode: 'public',
              password: '',
              indexable: true,
              includeInSitemap: true,
              redirect301: null,
              customHeadCode: '',
              thumbnailUrl: null,
              createdAt: now,
              updatedAt: now,
            }
            return {
              pages: [...state.pages, newPage],
              activePage: newPage.id,
              activeElement: null,
            }
          },
          false,
          'createCmsCollectionPage'
        ),

      deletePage: (pageId) =>
        set(
          (state) => {
            if (state.pages.length <= 1) return {}
            const target = state.pages.find((p) => p.id === pageId)
            if (!target || target.isHome) return {}
            const idx = state.pages.findIndex((p) => p.id === pageId)
            const next = state.pages.filter((p) => p.id !== pageId)
            const newActive =
              state.activePage === pageId
                ? (next[Math.max(0, idx - 1)]?.id ?? next[0]?.id ?? null)
                : state.activePage
            return {
              pages: next,
              activePage: newActive,
              activeElement: null,
              projectContent: withoutLinksToPages(state.projectContent, [target]),
            }
          },
          false,
          'deletePage'
        ),

      renamePage: (pageId, name) =>
        set(
          (state) => ({
            pages: state.pages.map((p) =>
              p.id === pageId ? { ...p, name } : p
            ),
          }),
          false,
          'renamePage'
        ),

      duplicatePage: (pageId) =>
        set(
          (state) => {
            const source = state.pages.find((p) => p.id === pageId)
            if (!source) return {}
            const now = new Date().toISOString()
            /* `translate` y no `t`: esto es el almacén, no un componente, y
               ahí no se pueden llamar hooks. El sufijo iba escrito en español
               fijo, así que en inglés la página duplicada nacía «Home (copia)». */
            const copyName = `${source.name} ${translate('common.copySuffix')}`
            const slug = ensureUniqueSlug(generateSlug(copyName), state.pages)
            const copy: Page = {
              ...source,
              id: crypto.randomUUID(),
              name: copyName,
              slug,
              isHome: false,
              isSlugCustom: false,
              elements: source.elements.map(regenIds),
              createdAt: now,
              updatedAt: now,
            }
            const idx = state.pages.findIndex((p) => p.id === pageId)
            const pages = [...state.pages]
            pages.splice(idx + 1, 0, copy)
            return {
              pages,
              activePage: copy.id,
              activeElement: null,
            }
          },
          false,
          'duplicatePage'
        ),

      reorderPages: (orderedIds) =>
        set(
          (state) => {
            const byId = new Map(state.pages.map((p) => [p.id, p]))
            const reordered = orderedIds
              .map((id) => byId.get(id))
              .filter((p): p is Page => !!p)
            if (reordered.length !== state.pages.length) return {}
            return { pages: reordered }
          },
          false,
          'reorderPages'
        ),

      updatePageSettings: (pageId, settings) =>
        set(
          (state) => ({
            pages: state.pages.map((p) =>
              p.id === pageId
                ? { ...p, ...settings, updatedAt: new Date().toISOString() }
                : p
            ),
          }),
          false,
          'updatePageSettings'
        ),

      updatePageTheme: (pageId, theme) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            return {
              pages: state.pages.map((p) =>
                p.id === pageId ? { ...p, theme, updatedAt: new Date().toISOString() } : p
              ),
            }
          },
          false,
          'updatePageTheme'
        ),

      updateProjectTheme: (theme) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            return { projectTheme: normalizeProjectTheme(theme, state.projectTheme) }
          },
          false,
          'updateProjectTheme'
        ),

      patchProjectTheme: (updater) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            return { projectTheme: normalizeProjectTheme(updater(state.projectTheme), state.projectTheme) }
          },
          false,
          'patchProjectTheme'
        ),

      updateProjectContent: (patch) =>
        set(
          (state) => {
            useHistoryStore.getState().pushSnapshot(state.pages)
            /* Quien reescribe las colecciones las ve sin las virtuales (el
               blog): se conservan, o una edición en el CMS se lo llevaría. */
            const virtual = patch.collections ? state.projectContent.collections.filter((item) => isVirtualCollection(item.id) && !patch.collections!.some((next) => next.id === item.id)) : []
            return {
              projectContent: {
                ...state.projectContent,
                ...patch,
                ...(patch.collections ? { collections: [...patch.collections, ...virtual] } : {}),
                business: { ...state.projectContent.business, ...(patch.business ?? {}) },
                navigation: { ...state.projectContent.navigation, ...(patch.navigation ?? {}) },
                contact: { ...state.projectContent.contact, ...(patch.contact ?? {}) },
              },
            }
          },
          false,
          'updateProjectContent'
        ),

      setBlogCollection: (collection) =>
        set(
          (state) => {
            const rest = state.projectContent.collections.filter((item) => item.id !== CMS_BLOG_SOURCE)
            return { projectContent: { ...state.projectContent, collections: collection ? [...rest, collection] : rest } }
          },
          false,
          'setBlogCollection'
        ),

      addCmsCollection: () =>
        set((state) => {
          useHistoryStore.getState().pushSnapshot(state.pages)
          const suffix = crypto.randomUUID().slice(0, 6)
          const id = `collection-${suffix}`
          const collection = makeDefaultCmsCollection(id, 'Nueva colección')
          const now = new Date().toISOString()
          const { collectionPage, dynamicPage } = makeCmsPageBundle(collection, state.pages, state.projectTheme ?? DEFAULT_PAGE_THEME, now)
          return {
            projectContent: { ...state.projectContent, collections: [...state.projectContent.collections, collection] },
            pages: [...state.pages, collectionPage, dynamicPage],
            activePage: collectionPage.id,
            activeElement: null,
          }
        }, false, 'addCmsCollection'),

      createCmsCollection: (config) => {
        const id = `collection-${crypto.randomUUID().slice(0, 6)}`
        set((state) => {
          useHistoryStore.getState().pushSnapshot(state.pages)
          const name = config.name.trim() || 'Nueva colección'
          // Title field + the user's custom fields. System fields (slug/status/created/content/…)
          // are added automatically by withCmsSystemFields. New collections start empty (no items).
          const fields: CmsField[] = [{ id: 'title', name: 'Nombre', type: 'title' }]
          for (const f of config.fields ?? []) {
            const fname = f.name.trim()
            if (fname) fields.push({
              id: `field-${crypto.randomUUID().slice(0, 8)}`,
              name: fname,
              type: f.type,
              ...(f.options && f.options.length ? { options: f.options } : {}),
            })
          }
          const collection: CmsCollection = {
            id,
            name,
            description: '',
            icon: config.icon ?? CMS_DEFAULT_ICON,
            color: config.color ?? CMS_DEFAULT_COLOR,
            fields,
            items: [],
          }
          const now = new Date().toISOString()
          const bundle = makeCmsPageBundle(collection, state.pages, state.projectTheme ?? DEFAULT_PAGE_THEME, now)
          let collectionPage = bundle.collectionPage
          let dynamicPage = bundle.dynamicPage
          if (config.url?.trim()) {
            const base = `/${cmsSlugPart(config.url, id)}`
            collectionPage = { ...collectionPage, slug: ensureUniqueSlug(base, state.pages), isSlugCustom: true }
            dynamicPage = { ...dynamicPage, slug: ensureUniqueSlug(`${collectionPage.slug}/[slug]`, [...state.pages, collectionPage]), isSlugCustom: true }
            collectionPage = { ...collectionPage, elements: patchCmsItemLinkPageSlug(collectionPage.elements, dynamicPage.slug) }
          }
          return {
            projectContent: { ...state.projectContent, collections: [...state.projectContent.collections, collection] },
            pages: [...state.pages, collectionPage, dynamicPage],
            activePage: collectionPage.id,
            activeElement: null,
          }
        }, false, 'createCmsCollection')
        return id
      },

      updateCmsCollection: (collectionId, config) => {
        set((state) => {
          useHistoryStore.getState().pushSnapshot(state.pages)
          const colIndex = state.projectContent.collections.findIndex((c) => c.id === collectionId)
          if (colIndex === -1) return state
          
          const collection = state.projectContent.collections[colIndex]
          if (!collection) return state
          const name = config.name.trim() || 'Colección'
          
          // Fields
          const updatedFields = collection.fields.filter((f) => CMS_LOCKED_FIELD_IDS.has(f.id) || f.id === 'title')
          /**
           * Un id que no está en la colección es un campo nuevo, no un campo a
           * ignorar.
           *
           * Antes, con `f.id` presente y sin coincidencia, la rama no hacía nada
           * y el campo se perdía sin aviso. Y el editor de colecciones etiqueta
           * con id TODAS las filas cuando está modificando una existente —
           * también las recién añadidas, que llevan uno inventado en el
           * formulario—, así que agregar campos y guardar parecía descartar los
           * cambios: se volvía atrás con la colección igual que estaba.
           *
           * Se decide por existencia, no por si viene id, para que la acción no
           * pueda tragarse parte de lo que se le pasa.
           */
          const newFieldIds: string[] = []
          for (const f of config.fields ?? []) {
             const fname = f.name.trim()
             if (!fname) continue
             const options = f.options && f.options.length ? { options: f.options } : {}
             const existing = f.id ? collection.fields.find((cf) => cf.id === f.id) : undefined
             if (existing) {
               updatedFields.push({ ...existing, name: fname, type: f.type, ...options })
               continue
             }
             // El id del formulario no se reutiliza: se acuña con el mismo
             // formato que `addCmsField`, para que todos los campos guardados se
             // nombren igual vengan de donde vengan.
             const id = `field-${crypto.randomUUID().slice(0, 8)}`
             newFieldIds.push(id)
             updatedFields.push({ id, name: fname, type: f.type, ...options })
          }
          
          // Los elementos que ya existían estrenan el campo vacío, igual que en
          // `addCmsField`: así una colección tiene la misma forma se haya
          // añadido el campo por un sitio o por el otro.
          const newCollection = {
            ...collection,
            name,
            fields: updatedFields,
            items: newFieldIds.length === 0
              ? collection.items
              : collection.items.map((item) => ({
                  ...item,
                  values: { ...Object.fromEntries(newFieldIds.map((id) => [id, ''])), ...item.values },
                })),
          }
          const newCollections = [...state.projectContent.collections]
          newCollections[colIndex] = newCollection
          
          // URL
          let newPages = state.pages
          if (config.url?.trim()) {
             const base = `/${cmsSlugPart(config.url, collectionId)}`
             const cPageIdx = state.pages.findIndex((p) => p.cmsDynamic?.collection === collectionId && p.cmsDynamic.kind === 'collection')
             const dPageIdx = state.pages.findIndex((p) => p.cmsDynamic?.collection === collectionId && p.cmsDynamic.kind !== 'collection')
             
             if (cPageIdx !== -1 && dPageIdx !== -1) {
                const cPage = state.pages[cPageIdx]
                const dPage = state.pages[dPageIdx]
                if (!cPage || !dPage) {
                   return { projectContent: { ...state.projectContent, collections: newCollections }, pages: newPages }
                }
                
                if (cPage.slug !== base) {
                  newPages = [...state.pages]
                  const newCPage = { ...cPage, slug: ensureUniqueSlug(base, state.pages.filter(p => p.id !== cPage.id)), isSlugCustom: true } as Page
                  newPages[cPageIdx] = newCPage
                  const newDPage = { ...dPage, slug: ensureUniqueSlug(`${newCPage.slug}/[slug]`, newPages.filter(p => p.id !== dPage.id)), isSlugCustom: true } as Page
                  newPages[dPageIdx] = newDPage
                  newPages[cPageIdx] = { ...newCPage, elements: patchCmsItemLinkPageSlug(newCPage.elements, newDPage.slug) }
                }
             }
          }
          
          return {
             projectContent: { ...state.projectContent, collections: newCollections },
             pages: newPages
          }
        }, false, 'updateCmsCollection')
      },

      renameCmsCollection: (collectionId, name) =>
        set((state) => mapCollection(state, collectionId, (c) => ({ ...c, name })), false, 'renameCmsCollection'),

      setCmsCollectionDescription: (collectionId, description) =>
        set((state) => mapCollection(state, collectionId, (c) => ({ ...c, description })), false, 'setCmsCollectionDescription'),

      setCmsCollectionImage: (collectionId, image) =>
        set((state) => mapCollection(state, collectionId, (c) => ({ ...c, image })), false, 'setCmsCollectionImage'),

      setCmsCollectionIcon: (collectionId, icon) =>
        set((state) => mapCollection(state, collectionId, (c) => ({ ...c, icon })), false, 'setCmsCollectionIcon'),

      setCmsCollectionColor: (collectionId, color) =>
        set((state) => mapCollection(state, collectionId, (c) => ({ ...c, color })), false, 'setCmsCollectionColor'),

      setCmsCollectionSchedule: (collectionId, schedule) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          /* Quitarla borra la clave en vez de dejarla en `undefined`: con
             `exactOptionalPropertyTypes` no es lo mismo, y una colección que
             lleva la clave vacía sigue pareciendo que declara vigencia. */
          if (!schedule?.startField) {
            const { schedule: _drop, ...rest } = c
            return rest
          }
          return { ...c, schedule }
        }), false, 'setCmsCollectionSchedule'),

      toggleCmsFieldHidden: (collectionId, fieldId) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          const hidden = new Set(c.hiddenFields ?? [])
          if (hidden.has(fieldId)) hidden.delete(fieldId)
          else hidden.add(fieldId)
          return { ...c, hiddenFields: Array.from(hidden) }
        }), false, 'toggleCmsFieldHidden'),

      deleteCmsCollection: (collectionId) =>
        set((state) => {
          useHistoryStore.getState().pushSnapshot(state.pages)
          const cleanedPages = clearProjectBindings(state.pages, (binding) => binding.collection === collectionId)
            .filter((page) => page.cmsDynamic?.collection !== collectionId)
          const activePageStillExists = cleanedPages.some((page) => page.id === state.activePage)
          const fallbackPage = cleanedPages.find((page) => page.isHome) ?? cleanedPages[0] ?? state.pages.find((page) => page.isHome) ?? state.pages[0]
          /* Y los enlaces de la barra y el pie hechos a mano que iban a sus
             páginas —la lista y cada ficha—: ya no llevan a ninguna parte. */
          const removedPages = state.pages.filter((page) => page.cmsDynamic?.collection === collectionId)
          const withoutCollection = { ...state.projectContent, collections: state.projectContent.collections.filter((c) => c.id !== collectionId) }
          return {
            pages: cleanedPages,
            activePage: activePageStillExists ? state.activePage : fallbackPage?.id ?? null,
            activeElement: null,
            projectContent: withoutLinksToPages(withoutCollection, removedPages),
          }
        }, false, 'deleteCmsCollection'),

      addCmsField: (collectionId, type, near) => {
        // El id se genera fuera del `set`: hay que devolverlo, y quien llama lo
        // necesita para colocar la columna nueva donde toca.
        const fid = `field-${crypto.randomUUID().slice(0, 6)}`
        set((state) => mapCollection(state, collectionId, (c) => {
          const label = type === 'image' ? 'Imagen' : type === 'richText' ? 'Texto' : type === 'code' ? 'Código' : type === 'title' ? 'Título' : 'Campo'
          const field: CmsField = { id: fid, name: label, type }
          const at = near ? c.fields.findIndex((f) => f.id === near.fieldId) : -1
          // Sin vecino conocido, al final: pasa al insertar junto a una columna
          // que no es un campo —«Estado», «Creado»—, y ahí el sitio lo decide
          // el orden de columnas, no la lista de campos.
          const cut = at < 0 ? c.fields.length : (near!.side === 'before' ? at : at + 1)
          return {
            ...c,
            fields: [...c.fields.slice(0, cut), field, ...c.fields.slice(cut)],
            items: c.items.map((it) => ({ ...it, values: { ...it.values, [fid]: '' } })),
          }
        }), false, 'addCmsField')
        return fid
      },

      duplicateCmsField: (collectionId, fieldId) => {
        const fid = `field-${crypto.randomUUID().slice(0, 6)}`
        set((state) => mapCollection(state, collectionId, (c) => {
          const at = c.fields.findIndex((f) => f.id === fieldId)
          const source = c.fields[at]
          if (!source) return c
          // La copia se lleva todo lo del original —tipo, opciones y sus
          // colores— y también los datos: duplicar una columna vacía no ahorra
          // nada, lo que se quiere copiar es la columna entera.
          const copy: CmsField = { ...source, id: fid, name: `${source.name} ${translate('common.copySuffix')}` }
          return {
            ...c,
            fields: [...c.fields.slice(0, at + 1), copy, ...c.fields.slice(at + 1)],
            items: c.items.map((it) => ({ ...it, values: { ...it.values, [fid]: it.values[fieldId] ?? '' } })),
          }
        }), false, 'duplicateCmsField')
        return fid
      },

      renameCmsField: (collectionId, fieldId, name) =>
        set((state) => mapCollection(state, collectionId, (c) => ({
          ...c, fields: c.fields.map((f) => (f.id === fieldId ? { ...f, name } : f)),
        })), false, 'renameCmsField'),

      setCmsFieldType: (collectionId, fieldId, type) =>
        set((state) => mapCollection(state, collectionId, (c) => ({
          ...c, fields: c.fields.map((f) => (f.id === fieldId ? { ...f, type } : f)),
        })), false, 'setCmsFieldType'),

      setCmsFieldOptions: (collectionId, fieldId, options) =>
        set((state) => mapCollection(state, collectionId, (c) => ({
          ...c, fields: c.fields.map((f) => (f.id === fieldId ? { ...f, options } : f)),
        })), false, 'setCmsFieldOptions'),

      /**
       * Guarda el orden de las columnas y lo traslada a los campos.
       *
       * Lo segundo es lo que hace que la ficha del post enseñe sus datos en el
       * mismo orden que la tabla. Y se hace por huecos, no ordenando `fields`
       * entero: ahí viven también el nombre, el slug y los de SEO, que no son
       * columnas y por tanto no figuran en el orden; una ordenación general los
       * habría echado a todos al final de la ficha.
       *
       * Sólo los campos que SÍ son columnas se barajan entre las posiciones que
       * ya ocupaban, así que el resto se queda exactamente donde estaba.
       */
      setCmsColumnOrder: (collectionId, order) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          const rank = new Map(order.map((id, index) => [id, index]))
          const slots: number[] = []
          c.fields.forEach((field, index) => { if (rank.has(field.id)) slots.push(index) })
          const sorted = slots
            .map((index) => c.fields[index]!)
            .sort((a, b) => rank.get(a.id)! - rank.get(b.id)!)
          const fields = [...c.fields]
          slots.forEach((slot, index) => { fields[slot] = sorted[index]! })
          return { ...c, columnOrder: order, fields }
        }), false, 'setCmsColumnOrder'),

      setCmsFieldOptionColor: (collectionId, fieldId, option, color) =>
        set((state) => mapCollection(state, collectionId, (c) => ({
          ...c,
          fields: c.fields.map((f) => {
            if (f.id !== fieldId) return f
            const optionColors = { ...(f.optionColors ?? {}) }
            // Sin color se borra la entrada en vez de guardar cadena vacía: así
            // vuelve al automático de verdad, y no queda un hueco que habría
            // que interpretar en todos los sitios que lo lean.
            if (color) optionColors[option] = color
            else delete optionColors[option]
            return { ...f, optionColors }
          }),
        })), false, 'setCmsFieldOptionColor'),

      removeCmsFieldOption: (collectionId, fieldId, option) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          const target = option.trim()
          return {
            ...c,
            fields: c.fields.map((f) => {
              if (f.id !== fieldId) return f
              const optionColors = { ...(f.optionColors ?? {}) }
              delete optionColors[target]
              return { ...f, options: (f.options ?? []).filter((o) => o.trim() !== target), optionColors }
            }),
            // También de las fichas: la lista de categorías se arma con lo que
            // está en uso, así que quitarla sólo del campo la haría reaparecer
            // en cuanto se volviera a abrir el desplegable.
            items: c.items.map((item) => {
              const raw = item.values[fieldId]
              if (!raw) return item
              const kept = raw.split(',').map((part) => part.trim()).filter((part) => part && part !== target)
              const next = kept.join(', ')
              if (next === raw) return item
              return { ...item, values: { ...item.values, [fieldId]: next, updatedAt: new Date().toISOString() } }
            }),
          }
        }), false, 'removeCmsFieldOption'),

      deleteCmsField: (collectionId, fieldId) =>
        set((state) => ({
          ...mapCollection(state, collectionId, (c) => ({
            ...c,
            fields: c.fields.filter((f) => f.id !== fieldId),
            items: c.items.map((it) => {
              const rest = { ...it.values }
              delete rest[fieldId]
              return { ...it, values: rest }
            }),
          })),
          pages: clearProjectBindings(state.pages, (binding) => binding.collection === collectionId && binding.field === fieldId),
          activeElement: null,
        }), false, 'deleteCmsField'),

      addCmsItem: (collectionId) => {
        const itemId = `item-${crypto.randomUUID().slice(0, 8)}`
        set((state) => {
          const next = mapCollection(state, collectionId, (c) => {
            const values: Record<string, string> = {}
            for (const f of c.fields) values[f.id] = ''
            if (c.fields.some((f) => f.id === 'status')) values.status = 'Borrador'
            if (c.fields.some((f) => f.id === 'slug')) {
              const used = new Set<string>(c.items.map((item) => item.values.slug).filter((slug): slug is string => Boolean(slug)))
              values.slug = uniqueCmsSlug('', used, `item-${c.items.length + 1}`)
            }
            const todayLA = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
            if (c.fields.some((f) => f.id === 'created')) values.created = todayLA
            if (c.fields.some((f) => f.id === 'date')) values.date = todayLA
            const nowIso = new Date().toISOString()
            values.createdAt = nowIso
            values.updatedAt = nowIso
            return { ...c, items: [...c.items, { id: itemId, values }] }
          })
          return {
            ...next,
            pages: state.pages.map((page) => (
              page.cmsDynamic?.collection === collectionId && page.cmsDynamic.kind !== 'collection'
                ? { ...page, cmsDynamic: { ...page.cmsDynamic, previewItemId: itemId }, updatedAt: new Date().toISOString() }
                : page
            )),
          }
        }, false, 'addCmsItem')
        return itemId
      },

      duplicateCmsItem: (collectionId, itemId) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          const index = c.items.findIndex((it) => it.id === itemId)
          if (index === -1) return c
          const source = c.items[index]
          if (!source) return c
          const copy = { id: `item-${crypto.randomUUID().slice(0, 8)}`, values: { ...source.values }, publishedValues: null }
          const nowIso = new Date().toISOString()
          copy.values.createdAt = nowIso
          copy.values.updatedAt = nowIso
          if (c.fields.some((f) => f.id === 'created')) copy.values.created = new Date().toISOString().slice(0, 10)
          if (c.fields.some((f) => f.id === 'slug')) {
            const used = new Set<string>(c.items.map((item) => item.values.slug).filter((slug): slug is string => Boolean(slug)))
            copy.values.slug = uniqueCmsSlug(source.values.title ?? source.values.slug ?? '', used, source.values.slug ?? 'item')
          }
          const items = [...c.items.slice(0, index + 1), copy, ...c.items.slice(index + 1)]
          return { ...c, items }
        }), false, 'duplicateCmsItem'),

      updateCmsItemValue: (collectionId, itemId, fieldId, value, options) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          const titleField = c.fields.find((field) => field.type === 'title') ?? c.fields.find((field) => field.id === 'title')
          /* Sólo durante el alta: ver el porqué en la firma, arriba. */
          const shouldSyncSlugField = Boolean(
            options?.syncSlug
            && titleField && fieldId === titleField.id
            && c.fields.some((field) => field.id === 'slug'),
          )
          return {
            ...c,
            items: c.items.map((it) => {
              if (it.id !== itemId) return it
              const values = { ...it.values, [fieldId]: value }
              if (fieldId !== 'updatedAt' && fieldId !== 'createdAt') values.updatedAt = new Date().toISOString()
              if (shouldSyncSlugField) {
                const previousAutoSlug = generateSlug(it.values[fieldId] ?? '').replace(/^\//, '')
                const currentSlug = it.values.slug?.trim() ?? ''
                /* `item-N` es la que pone `addCmsItem` a una entrada sin título:
                   también es automática, y la primera vez que se escribe el
                   título la sustituye. */
                const slugIsAuto = !currentSlug || currentSlug === previousAutoSlug || /^item-\d+(?:-\d+)?$/.test(currentSlug)
                if (slugIsAuto) {
                  const used = new Set<string>(
                    c.items
                      .filter((item) => item.id !== itemId)
                      .map((item) => item.values.slug)
                      .filter((slug): slug is string => Boolean(slug)),
                  )
                  values.slug = uniqueCmsSlug(value, used, value || `item-${c.items.findIndex((item) => item.id === itemId) + 1}`)
                }
              }
              // Sync the status field based on dirtiness, but only if they have a status field
              if (c.fields.some((f) => f.id === 'status') && fieldId !== 'status') {
                if (it.publishedValues) {
                  values.status = 'Publicado'
                }
              }
              return { ...it, values }
            }),
          }
        }), false, 'updateCmsItemValue'),

      replaceCmsItemValues: (collectionId, itemId, values) =>
        set((state) => mapCollection(state, collectionId, (collection) => ({
          ...collection,
          items: collection.items.map((item) => item.id === itemId ? { ...item, values: { ...values } } : item),
        })), false, 'replaceCmsItemValues'),

      publishCmsItem: (collectionId, itemId) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          return {
            ...c,
            items: c.items.map((it) => {
              if (it.id !== itemId) return it
              return { ...it, values: { ...it.values, status: 'Publicado' }, publishedValues: { ...it.values, status: 'Publicado' } }
            })
          }
        }), false, 'publishCmsItem'),

      unpublishCmsItem: (collectionId, itemId) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          return {
            ...c,
            items: c.items.map((it) => {
              if (it.id !== itemId) return it
              return { ...it, values: { ...it.values, status: 'Borrador' }, publishedValues: null }
            })
          }
        }), false, 'unpublishCmsItem'),

      discardCmsItemChanges: (collectionId, itemId) =>
        set((state) => mapCollection(state, collectionId, (c) => {
          return {
            ...c,
            items: c.items.map((it) => {
              if (it.id !== itemId) return it
              if (it.publishedValues) {
                return { ...it, values: { ...it.publishedValues } }
              }
              return it
            })
          }
        }), false, 'discardCmsItemChanges'),

      deleteCmsItem: (collectionId, itemId) =>
        set((state) => ({
          ...mapCollection(state, collectionId, (c) => ({
            ...c, items: c.items.filter((it) => it.id !== itemId),
          })),
          pages: clearProjectBindings(state.pages, (binding) => binding.collection === collectionId && binding.itemId === itemId),
          activeElement: null,
        }), false, 'deleteCmsItem'),

      setHomePage: (pageId) =>
        set(
          (state) => {
            const target = state.pages.find((p) => p.id === pageId)
            if (!target || target.isHome) return {}
            return {
              pages: state.pages.map((p) => {
                if (p.id === pageId) {
                  return { ...p, isHome: true, slug: '/', isSlugCustom: false, redirect301: null, unpublished: undefined }
                }
                if (p.isHome) {
                  // Demote old home — generate a slug from its name
                  const slug = generateSlug(p.name)
                  return { ...p, isHome: false, slug: slug || '/pagina', isSlugCustom: false }
                }
                return p
              }),
            }
          },
          false,
          'setHomePage'
        ),
    }),
    /* Sólo en desarrollo. En producción, con la extensión de Redux DevTools
       instalada, cada acción serializaba el estado entero del editor —páginas,
       tema y CMS— y el historial sus 50 copias: un lastre invisible. */
    { name: 'EditorStore', enabled: import.meta.env.DEV }
  )
)

// ─── Selectors ────────────────────────────────────────────────────────────────

/** Returns the elements of the currently active page */
/** La barra o el pie del sitio, si existe. */
export function siteSlotComponent(content: ProjectContent | undefined, slot: SiteSlot): ProjectComponent | undefined {
  return content?.components?.find((component) => component.slot === slot)
}

/** Lo que queda de la barra en una página «minimalista»: la marca, sin menú. */
const MINIMAL_NAV_DROPS = new Set(['nav-links', 'nav-toggle', 'nav-panel'])

function minimalNav(node: ElementNode): ElementNode {
  const children = node.children
    .filter((child) => !MINIMAL_NAV_DROPS.has(child.schemaId ?? ''))
    .map(minimalNav)
  return { ...node, children }
}

/* Mismos datos → mismo array. Sin esto `useEditorStore(selectActivePageElements)`
   devolvería una lista nueva en cada lectura y el editor se redibujaría sin fin. */
let layoutCache: {
  elements: ElementNode[]
  banner: ElementNode | undefined
  nav: ElementNode | undefined
  footer: ElementNode | undefined
  layout: PageLayout | undefined
  result: ElementNode[]
} | null = null

/**
 * Los elementos de una página con la barra y el pie del sitio alrededor (y el
 * banner, si hay, encima de la barra).
 *
 * La barra y el pie no se guardan en cada página: son uno por sitio y se
 * dibujan arriba y abajo de todas. Así un cambio en uno se ve en todas, sin
 * copias que se desincronicen. Llevan los ids del original, de modo que
 * seleccionar y editar cualquier cosa suya escribe en él.
 *
 * Lo usan el lienzo, el inspector y la publicación: todo lo que tenga que ver
 * la página como la verá quien la visite. Lo que ESCRIBE en la página sigue
 * trabajando con `page.elements`.
 */
type SectionTone = 'default' | 'secondary'

const TONE_SECTION_ID: Record<SectionTone, string> = { default: 'section-default', secondary: 'section-secondary' }
const TONE_COLOR: Record<SectionTone, string> = { default: 'background', secondary: 'backgroundSecondary' }
const TONE_CSS: Record<SectionTone, string> = { default: 'var(--wb-background)', secondary: 'var(--wb-backgroundSecondary)' }

/**
 * Con qué mecanismo lleva una sección su fondo normal o secundario, y cuál es.
 *
 * Una sección puede decirlo de cuatro maneras, según de dónde venga: el estilo
 * de sección (lo que se elige en el lienzo), una referencia de color (lo que
 * pone la IA al crear el sitio), el color escrito en sus estilos (plantillas
 * viejas) o su `backgroundRole`. Un color enlazado manda sobre el estilo,
 * como al dibujar. Null si su fondo es otra cosa —una imagen, el color de marca, un
 * estilo propio, un color a mano—: esa no se toca y corta la alternancia.
 */
function sectionToneOf(node: ElementNode): { tone: SectionTone; via: 'section' | 'color' | 'local' | 'role' } | null {
  if (node.type !== 'section' || node.behavior?.type === 'nav') return null
  const local = (node.styles?.desktop ?? {}) as Record<string, unknown>
  if (local.backgroundImage || (typeof local.background === 'string' && /url\(|gradient\(/.test(local.background))) return null
  if (node.styleRefs?.overrides?.some((key) => key === 'background' || key === 'backgroundColor')) return null
  const sectionRef = node.styleRefs?.section
  if (sectionRef && sectionRef !== TONE_SECTION_ID.default && sectionRef !== TONE_SECTION_ID.secondary) return null
  const colorRef = node.styleRefs?.colors?.background ?? node.styleRefs?.colors?.backgroundColor
  if (colorRef) {
    if (colorRef === TONE_COLOR.default) return { tone: 'default', via: 'color' }
    if (colorRef === TONE_COLOR.secondary) return { tone: 'secondary', via: 'color' }
    return null
  }
  if (sectionRef === TONE_SECTION_ID.default) return { tone: 'default', via: 'section' }
  if (sectionRef === TONE_SECTION_ID.secondary) return { tone: 'secondary', via: 'section' }
  const localColor = local.background ?? local.backgroundColor
  if (typeof localColor === 'string' && localColor) {
    if (localColor === TONE_CSS.default) return { tone: 'default', via: 'local' }
    if (localColor === TONE_CSS.secondary) return { tone: 'secondary', via: 'local' }
    return null
  }
  const role = node.backgroundRole ?? 'auto'
  if (role === 'auto' || role === 'background') return { tone: 'default', via: 'role' }
  if (role === 'surface') return { tone: 'secondary', via: 'role' }
  return null
}

/** La sección con el otro fondo, cambiado por el mismo mecanismo con que lo lleva. */
function withSectionTone(node: ElementNode, via: 'section' | 'color' | 'local' | 'role', tone: SectionTone): ElementNode {
  const role: SectionBackgroundRole = tone === 'default' ? 'background' : 'surface'
  if (via === 'section') return { ...node, backgroundRole: role, styleRefs: { ...node.styleRefs, section: TONE_SECTION_ID[tone] } }
  if (via === 'role') return { ...node, backgroundRole: role }
  if (via === 'color') {
    const colors = { ...node.styleRefs?.colors }
    if (colors.background) colors.background = TONE_COLOR[tone]
    if (colors.backgroundColor) colors.backgroundColor = TONE_COLOR[tone]
    const section = node.styleRefs?.section
    return {
      ...node,
      backgroundRole: role,
      styleRefs: {
        ...node.styleRefs,
        colors,
        // Las plantillas pueden traer ambas referencias. Las dos deben decir
        // el mismo tono cuando la alternancia cambia su posición.
        ...(section === TONE_SECTION_ID.default || section === TONE_SECTION_ID.secondary
          ? { section: TONE_SECTION_ID[tone] } : {}),
      },
    }
  }
  const swap = <T extends object>(bag: T): T => {
    const next = { ...bag } as Record<string, unknown>
    for (const key of ['background', 'backgroundColor'] as const) {
      if (next[key] === TONE_CSS.default || next[key] === TONE_CSS.secondary) next[key] = TONE_CSS[tone]
    }
    return next as T
  }
  const styles = { ...node.styles, desktop: swap(node.styles.desktop) }
  if (node.styles.tablet) styles.tablet = swap(node.styles.tablet)
  if (node.styles.mobile) styles.mobile = swap(node.styles.mobile)
  return { ...node, styles }
}

/** Si una sección entra en «Alternar fondos»: su fondo es el normal o el secundario. */
export function sectionJoinsAutoTone(node: ElementNode): boolean {
  return sectionToneOf(node) !== null
}

const autoToneCache = new WeakMap<ElementNode[], ElementNode[]>()
/* La misma sección con el mismo fondo es el mismo objeto: el lienzo compara por
   identidad, y una copia nueva en cada edición la redibujaría sin motivo. */
const tonedNodeCache = new WeakMap<ElementNode, Partial<Record<SectionTone, ElementNode>>>()

function toned(node: ElementNode, via: 'section' | 'color' | 'local' | 'role', tone: SectionTone): ElementNode {
  const known = tonedNodeCache.get(node) ?? {}
  const hit = known[tone]
  if (hit) return hit
  const next = withSectionTone(node, via, tone)
  tonedNodeCache.set(node, { ...known, [tone]: next })
  return next
}

/**
 * «Alternar fondos»: las secciones de fondo normal y secundario, intercaladas
 * según su sitio en la página.
 *
 * Se calcula al dibujar y no se guarda, como el tono de las tarjetas: mover,
 * agregar o quitar una sección recoloca las de alrededor sin tocar el
 * documento, y apagarlo devuelve a cada una lo que tenía.
 *
 * Las reglas:
 *  - Entran sólo las secciones con fondo normal o secundario (`sectionToneOf`).
 *    Las demás —con imagen, de marca, de un color propio— se quedan como están
 *    y cortan la cuenta: después de una, se vuelve a empezar por el normal.
 *  - Dentro de cada tramo, dos seguidas nunca comparten fondo.
 *  - Una fijada a mano (`sectionToneManual`) conserva el suyo, y las de su
 *    tramo se ordenan a partir de ella.
 */
export function withAutoSectionTones(elements: ElementNode[], on: boolean): ElementNode[] {
  if (!on || elements.length === 0) return elements
  const cached = autoToneCache.get(elements)
  if (cached) return cached
  const result = [...elements]
  let changed = false
  const flip = (tone: SectionTone): SectionTone => (tone === 'default' ? 'secondary' : 'default')
  const assign = (run: { index: number; tone: SectionTone; via: 'section' | 'color' | 'local' | 'role'; manual: boolean }[]) => {
    if (!run.length) return
    const anchor = run.findIndex((entry) => entry.manual)
    const wanted: SectionTone[] = Array.from({ length: run.length }, (): SectionTone => 'default')
    if (anchor < 0) {
      run.forEach((_, at) => { wanted[at] = at % 2 === 0 ? 'default' : 'secondary' })
    } else {
      wanted[anchor] = run[anchor]!.tone
      for (let at = anchor - 1; at >= 0; at -= 1) wanted[at] = run[at]!.manual ? run[at]!.tone : flip(wanted[at + 1]!)
      for (let at = anchor + 1; at < run.length; at += 1) wanted[at] = run[at]!.manual ? run[at]!.tone : flip(wanted[at - 1]!)
    }
    run.forEach((entry, at) => {
      if (entry.manual || entry.tone === wanted[at]) return
      result[entry.index] = toned(result[entry.index]!, entry.via, wanted[at]!)
      changed = true
    })
  }
  let run: Parameters<typeof assign>[0] = []
  elements.forEach((node, index) => {
    const tone = sectionToneOf(node)
    if (!tone) { assign(run); run = []; return }
    run.push({ index, ...tone, manual: node.sectionToneManual === true })
  })
  assign(run)
  const out = changed ? result : elements
  autoToneCache.set(elements, out)
  return out
}

export function withSiteLayout(
  page: Page | undefined,
  content: ProjectContent | undefined,
  theme?: { sectionToneAuto?: true } | PageTheme,
): ElementNode[] {
  const elements = withAutoSectionTones(page?.elements ?? [], (theme as { sectionToneAuto?: true } | undefined)?.sectionToneAuto === true)
  const navMode = page?.layout?.nav ?? 'show'
  const nav = navMode === 'hide' ? undefined : siteSlotComponent(content, 'nav')?.node
  const footer = page?.layout?.footer === 'hide' ? undefined : siteSlotComponent(content, 'footer')?.node
  const siteBanner = siteSlotComponent(content, 'banner')?.node
  /* Oculto en esta página, o puesto sólo en otras («Sólo en esta página»). */
  const onlyPages = siteBanner?.siteBanner?.onlyPages
  const banner = page?.layout?.banner === 'hide' || (onlyPages?.length && !onlyPages.includes(page?.id ?? ''))
    ? undefined
    : siteBanner
  if (!banner && !nav && !footer) return elements
  const cached = layoutCache
  if (cached && cached.elements === elements && cached.banner === banner && cached.nav === nav && cached.footer === footer && cached.layout === page?.layout) {
    return cached.result
  }
  const result = [
    ...(banner ? [banner] : []),
    ...(nav ? [navMode === 'minimal' ? minimalNav(nav) : nav] : []),
    ...elements,
    ...(footer ? [footer] : []),
  ]
  layoutCache = { elements, banner, nav, footer, layout: page?.layout, result }
  return result
}

/** Si un id es la raíz de la barra o del pie del sitio (no algo de dentro). */
export function isSiteSlotRoot(content: ProjectContent | undefined, elementId: string): boolean {
  return Boolean(content?.components?.some((component) => component.slot && component.node.id === elementId))
}

export function selectActivePageElements(state: EditorStore): ElementNode[] {
  if (!state.activePage) return []
  return withSiteLayout(state.pages.find((p) => p.id === state.activePage), state.projectContent, state.projectTheme)
}

/** Returns the currently active page */
export function selectActivePage(state: EditorStore): Page | undefined {
  if (!state.activePage) return undefined
  return state.pages.find((p) => p.id === state.activePage)
}

/** Returns the active ElementNode (undefined if nothing selected) */
export function selectActiveElement(state: EditorStore): ElementNode | undefined {
  if (!state.activeElement) return undefined
  const elements = selectActivePageElements(state)
  // A form part is not in the tree — it is synthesised from the host's model so
  // the inspector, the style path and the canvas all treat it as an element.
  const partId = parseFormPartId(state.activeElement)
  if (partId) {
    const host = findNodeById(elements, partId.nodeId)
    return host ? buildFormPartNode(host, partId.part) : undefined
  }
  const reviewPart = parseReviewPartId(state.activeElement)
  if (reviewPart) {
    const host = findNodeById(elements, reviewPart.nodeId)
    return host ? buildReviewPartNode(host, reviewPart.part) : undefined
  }
  const faqPart = parseFaqPartId(state.activeElement)
  if (faqPart) {
    const host = findNodeById(elements, faqPart.nodeId)
    return host ? buildFaqPartNode(host, faqPart.part, state.activeFaqItem) : undefined
  }
  const inPage = findNodeById(elements, state.activeElement)
  if (inPage) return inPage
  /* Lo de dentro de una sección reutilizable no está en la página: sin esto, el
     inspector se quedaba en blanco al seleccionarlo y parecía que el clic no
     había hecho nada. */
  const owner = componentOwning(state.projectContent, state.activeElement)
  return owner ? findNodeById([owner.node], state.activeElement) : undefined
}
